import React, { useState, useEffect, useMemo } from 'react';
// Recharts imports removed - using ImprovedCharts instead
import { Home, Users, DollarSign, Calendar, TrendingUp, Plus, Copy, Trash2, Edit3, Sparkles, Send, X, Settings, ChevronDown, Maximize2, Scale, AlertTriangle, Menu } from 'lucide-react';
import { getAIConfig, setUserAPIKey, clearUserAPIKey, makeAIRequest } from './config/aiConfig';
import { calculateNetIncome } from './utils/taxCalculations';
import { getDebtStatusAtAge } from './utils/financialEvents';
import { DebtPanel, EventsPanel, CareerPanel, CollegeSavingsPanel, LifestyleExpensesPanel } from './components/FinancialEventsPanel';
import AdvancedSettingsModal from './components/AdvancedSettings';
import { calculateSmartGrowthRate, getFinancialSettings } from './utils/careerGrowth';
import { IncomeExpensesChart, PortfolioChart, StressChart, SavingsRateChart } from './components/ImprovedCharts';
import MonteCarloChart from './components/MonteCarloChart';
import { ChartIcon, SliderIcon, brandColors } from './components/BrandIcons';
import { createSampleVisions, sampleVisions } from './data/sampleVisions';
import PresetExplorer from './components/PresetExplorer';
import { Analytics } from "@vercel/analytics/react";
// Supabase/Auth removed - using localStorage only

// FILE LOGGING for debugging
let logBuffer = [];
const fileLog = (message) => {
  const timestamp = new Date().toISOString();
  const logEntry = `[${timestamp}] ${message}`;
  console.log(logEntry);
  logBuffer.push(logEntry);

  // Keep last 500 lines
  if (logBuffer.length > 500) {
    logBuffer = logBuffer.slice(-500);
  }

  // Write to localStorage so we can download it
  try {
    localStorage.setItem('lifemath-debug-log', logBuffer.join('\n'));
  } catch (e) {
    // Ignore localStorage errors
  }
};

// Download logs function - can be called from console: window.downloadLogs()
window.downloadLogs = () => {
  const logs = localStorage.getItem('lifemath-debug-log') || 'No logs available';
  const blob = new Blob([logs], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `lifemath-debug-${new Date().toISOString()}.log`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Settings Modal Component
const SettingsModal = ({ onClose, visions, setVisions }) => {
  const [apiKey, setApiKey] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const aiConfig = getAIConfig();

  // Export visions to JSON file
  const handleExport = () => {
    const dataToExport = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      visions: visions
    };
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lifemath-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import visions from JSON file
  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.visions && Array.isArray(data.visions)) {
          // Merge imported visions with existing (avoid duplicates by id)
          const existingIds = new Set(visions.map(v => v.id));
          const newVisions = data.visions.filter(v => !existingIds.has(v.id));
          const mergedVisions = [...visions, ...newVisions];
          setVisions(mergedVisions);
          setImportStatus({ success: true, message: `Imported ${newVisions.length} new vision(s)!` });
        } else {
          setImportStatus({ success: false, message: 'Invalid file format' });
        }
      } catch (err) {
        setImportStatus({ success: false, message: 'Failed to parse file' });
      }
    };
    reader.readAsText(file);
    // Reset file input
    event.target.value = '';
  };

  useEffect(() => {
    // Load existing user key if available
    const userKey = localStorage.getItem('lifemath-api-key');
    if (userKey) {
      setApiKey(userKey);
    }
  }, []);

  const handleSave = () => {
    setUserAPIKey(apiKey);
    setIsSaved(true);
    setTimeout(() => {
      setIsSaved(false);
      onClose();
    }, 1500);
  };

  const handleUseDefault = () => {
    clearUserAPIKey();
    setApiKey('');
    setIsSaved(true);
    setTimeout(() => {
      setIsSaved(false);
      onClose();
    }, 1500);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Settings</h2>
            <p className="text-sm text-gray-500 mt-1">Configure AI provider</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              AI Provider: Groq (Free & Fast)
            </label>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 mb-3">
              {aiConfig.isUsingDefaultKey ? (
                <>
                  <strong>Using shared API key</strong>
                  <p className="mt-1">You're using the app's shared Groq API key. This is free and works great for most users!</p>
                </>
              ) : (
                <>
                  <strong>Using your own API key</strong>
                  <p className="mt-1">You're using your personal API key.</p>
                </>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Groq API Key (Optional)
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="gsk_..."
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white border-gray-300 text-gray-900"
            />
            <p className="text-xs text-gray-500 mt-1">
              Get your free API key at <a href="https://console.groq.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">console.groq.com</a>
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            {aiConfig.isUsingDefaultKey ? (
              <button
                onClick={handleSave}
                disabled={!apiKey.trim() || isSaved}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
              >
                {isSaved ? 'Saved!' : 'Use My API Key'}
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={!apiKey.trim() || isSaved}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                >
                  {isSaved ? 'Saved!' : 'Update Key'}
                </button>
                <button
                  onClick={handleUseDefault}
                  disabled={isSaved}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
                >
                  Use Shared Key
                </button>
              </>
            )}
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
            <strong className="text-gray-900">About Groq</strong>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>Free tier: 14,400 requests/day</li>
              <li>Super fast responses (500+ tokens/sec)</li>
              <li>No credit card required</li>
            </ul>
          </div>

          {/* Data Backup Section */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Data Backup</h3>
            <p className="text-xs text-gray-500 mb-3">
              Your visions are stored locally in your browser. Export to create a backup or transfer to another device.
            </p>

            <div className="flex gap-2">
              <button
                onClick={handleExport}
                disabled={!visions || visions.length === 0}
                className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Export Visions ({visions?.length || 0})
              </button>
              <label className="flex-1">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                />
                <span className="block px-3 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm text-center cursor-pointer">
                  Import Visions
                </span>
              </label>
            </div>

            {importStatus && (
              <div className={`mt-2 p-2 rounded text-xs ${importStatus.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {importStatus.message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// About Modal Component
const AboutModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold text-slate-800">About LifeMath</h2>
            <p className="text-sm text-slate-500 mt-1">Educational financial planning tool</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Disclaimer */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-slate-600" /> Disclaimer
            </h3>
            <p className="text-sm text-slate-600">
              This tool is for <strong>educational purposes only</strong> and is not financial advice.
              All projections are estimates. Consult qualified professionals before making financial decisions.
            </p>
          </div>

          {/* How It Works */}
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
            <h3 className="font-semibold text-slate-800 mb-2">How It Works</h3>
            <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
              <li>Describe your life vision to our AI assistant</li>
              <li>Fine-tune details using the panels</li>
              <li>View projections and run Monte Carlo simulations</li>
              <li>Compare multiple scenarios side by side</li>
            </ol>
          </div>

          {/* Privacy */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <h3 className="font-semibold text-slate-800 mb-2">Your Privacy</h3>
            <p className="text-sm text-slate-600">
              Your data stays in your browser (local storage). No accounts required.
              AI features use Groq API for processing prompts.
            </p>
          </div>

          {/* Footer */}
          <p className="text-xs text-slate-400 text-center pt-2 border-t border-slate-100">
            By using this tool, you acknowledge that you are responsible for your own financial decisions.
          </p>
        </div>
      </div>
    </div>
  );
};

// Default lifestyle choices for new visions
const getDefaultLifestyleChoices = (age) => [
  {
    id: 1,
    name: "Airbnb a spare room",
    icon: "🏠",
    upfrontCost: 2000,
    monthlyCost: 200,
    monthlySavings: 0,
    monthlyIncome: 1200,
    duration: 10,
    description: "Rent spare bedroom on Airbnb, +$1000/mo net",
    age: age
  },
  {
    id: 2,
    name: "Drive for Uber/Lyft",
    icon: "🚗",
    upfrontCost: 0,
    monthlyCost: 300,
    monthlySavings: 0,
    monthlyIncome: 1500,
    duration: 5,
    description: "Part-time rideshare 15-20 hrs/week, +$1200/mo net",
    age: age
  },
  {
    id: 3,
    name: "Start a side business",
    icon: "💻",
    upfrontCost: 5000,
    monthlyCost: 100,
    monthlySavings: 0,
    monthlyIncome: 800,
    duration: 15,
    description: "Online business or freelancing, +$700/mo net",
    age: age
  },
  {
    id: 4,
    name: "Buy a boat",
    icon: "⛵",
    upfrontCost: 50000,
    monthlyCost: 800,
    monthlySavings: 0,
    monthlyIncome: 0,
    duration: 10,
    sellValue: 25000,
    description: "30ft sailboat with slip fees & maintenance",
    age: age
  },
  {
    id: 5,
    name: "Private school for kids",
    icon: "🎓",
    upfrontCost: 0,
    monthlyCost: 2000,
    monthlySavings: 0,
    monthlyIncome: 0,
    duration: 13,
    description: "Per child, grades K-12",
    age: age
  }
];

// Lifestyle Trade-offs Panel Component
const DecisionScenariosPanel = ({ vision, onUpdate, calculateRetirementAge, darkMode }) => {
  const [lifestyleInput, setLifestyleInput] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Use vision's lifestyleChoices, or initialize with defaults if empty
  const lifestyleChoices = vision.lifestyleChoices?.length > 0
    ? vision.lifestyleChoices
    : getDefaultLifestyleChoices(vision.age);

  // Update lifestyle choices in the vision
  const setLifestyleChoices = (newChoices) => {
    const choices = typeof newChoices === 'function'
      ? newChoices(lifestyleChoices)
      : newChoices;
    onUpdate({ lifestyleChoices: choices });
  };

  const calculateImpact = (choice) => {
    const currentRetireAge = vision.retireAge || 65;
    const currentAge = vision.age;
    const purchaseAge = choice.age || currentAge;
    const duration = choice.duration || 10; // Default 10 years if not specified
    const endAge = purchaseAge + duration;
    const sellValue = choice.sellValue || 0;

    // Check if this is an income-boosting or cost-cutting choice
    const monthlyIncome = choice.monthlyIncome || 0;
    const monthlySavings = choice.monthlySavings || 0;
    const monthlyCost = choice.monthlyCost || 0;
    // Positive = costs money, Negative = saves/earns money
    const netMonthlyImpact = monthlyCost - monthlyIncome - monthlySavings;

    // Calculate years of active impact (respecting duration)
    const activeYears = Math.min(duration, Math.max(0, currentRetireAge - purchaseAge));

    // IMPORTANT: Model compound growth for monthly cash flows
    // Assume investment return of 7% real (after inflation)
    const assumedRealReturn = 0.07;
    const monthlyReturn = assumedRealReturn / 12;
    const monthsActive = activeYears * 12;

    // Calculate future value of monthly net impact with compound growth
    // If netMonthlyImpact is negative (saving/earning), this becomes a growing asset
    // If positive (costing), this is opportunity cost of not investing that money
    let compoundedOngoingImpact = 0;

    if (monthsActive > 0 && netMonthlyImpact !== 0) {
      // Future Value of Annuity formula: FV = PMT × [((1 + r)^n - 1) / r]
      compoundedOngoingImpact = netMonthlyImpact *
        ((Math.pow(1 + monthlyReturn, monthsActive) - 1) / monthlyReturn);
    }

    // Total impact includes upfront cost, compounded monthly impacts, and sellValue
    const totalImpact = choice.upfrontCost + compoundedOngoingImpact - sellValue;

    // Total cost/benefit over full duration (for display) - also with compounding
    const monthsDuration = duration * 12;
    let compoundedDurationImpact = 0;
    if (monthsDuration > 0 && netMonthlyImpact !== 0) {
      compoundedDurationImpact = netMonthlyImpact *
        ((Math.pow(1 + monthlyReturn, monthsDuration) - 1) / monthlyReturn);
    }
    const totalCostOverDuration = choice.upfrontCost + compoundedDurationImpact - sellValue;

    // Calculate years change using actual annual savings from the vision
    // Use a more realistic savings calculation based on income and expenses
    const annualIncome = vision.currentIncome || 85000;

    // Estimate current savings rate from precision expenses if available
    let estimatedAnnualExpenses = 0;
    if (vision.precisionExpenses) {
      const pe = vision.precisionExpenses;
      estimatedAnnualExpenses = (
        (pe.rent || 0) + (pe.utilities || 0) + (pe.groceries || 0) + (pe.diningOut || 0) +
        (pe.carPayment || 0) + (pe.carInsurance || 0) + (pe.gas || 0) + (pe.carMaintenance || 0) +
        (pe.healthInsurance || 0) + (pe.medicalExpenses || 0) + (pe.entertainment || 0) +
        (pe.shopping || 0) + (pe.personalCare || 0) + (pe.travel || 0) + (pe.hobbies || 0) +
        (pe.subscriptions || 0) + (pe.gym || 0) + (pe.childcare || 0) + (pe.childEducation || 0) +
        (pe.childActivities || 0) + (pe.childOther || 0) + (pe.debtPayments || 0) +
        (pe.gifts || 0) + (pe.charity || 0) + (pe.petExpenses || 0) + (pe.other || 0)
      ) * 12;
    }

    // Calculate actual annual savings (or use 20% of income as fallback)
    const annualSavings = estimatedAnnualExpenses > 0
      ? Math.max(annualIncome * 0.05, annualIncome - estimatedAnnualExpenses) // At least 5% savings
      : annualIncome * 0.20;

    // Years change = total impact / annual savings
    // Positive totalImpact = delays retirement (expense)
    // Negative totalImpact = accelerates retirement (income boost)
    const rawYearsChange = annualSavings > 0 ? totalImpact / annualSavings : 0;

    // Round to 1 decimal place for display, but keep fractional for accuracy
    const yearsChange = Math.round(rawYearsChange * 10) / 10;

    // Calculate new retirement age
    const newRetirementAge = Math.max(purchaseAge + 1, Math.round(currentRetireAge + yearsChange));

    return {
      retirementAgeChange: yearsChange,
      newRetirementAge: newRetirementAge,
      totalCost: totalCostOverDuration,
      totalImpact: totalImpact,
      monthlyImpact: netMonthlyImpact,
      monthlyIncome: monthlyIncome,
      monthlySavings: monthlySavings,
      monthlyCost: monthlyCost,
      purchaseAge: purchaseAge,
      duration: duration,
      endAge: endAge,
      sellValue: sellValue,
      activeYears: activeYears,
      isIncomeBoost: monthlyIncome > monthlyCost, // True if net positive income
      isCostCutting: monthlySavings > 0, // True if cutting expenses
      isBeneficial: (monthlyIncome + monthlySavings) > monthlyCost, // True if helps retirement
      annualSavingsUsed: annualSavings // For debugging/transparency
    };
  };

  const analyzeLifestyleCost = async (lifestyleDescription) => {
    try {
      const prompt = `Analyze the following lifestyle choice and estimate realistic costs, savings, OR income: "${lifestyleDescription}"

Please respond with ONLY a valid JSON object in this exact format (no markdown, no code blocks):
{
  "upfrontCost": <number>,
  "monthlyCost": <number>,
  "monthlySavings": <number>,
  "monthlyIncome": <number>,
  "duration": <number>,
  "sellValue": <number>,
  "description": "<brief description>",
  "age": <number or null>
}

Important:
- upfrontCost: One-time purchase, investment, or training cost (0 if none, can be negative for things sold)
- monthlyCost: Ongoing monthly costs (insurance, maintenance, fees, etc.) - always positive or 0
- monthlySavings: Monthly money SAVED by cutting expenses (0 if this adds costs)
- monthlyIncome: Additional monthly income generated (0 if purely an expense)
- duration: How many years this lasts (e.g., 10 for a boat you'll own 10 years, 5 for a side hustle, 13 for K-12 schooling)
- sellValue: Resale value at end of duration (0 for services/subscriptions, estimate for assets like boats/cars)
- age: Starting age if mentioned (null if not)
- Be realistic with current market prices

For EXPENSE choices (buying things):
- monthlyCost = cost, duration = years owned, sellValue = resale value

For COST-CUTTING choices (stop eating out, cancel subscriptions):
- monthlySavings = amount saved, duration = how long you'll maintain this change

For INCOME-BOOSTING choices (side hustles, businesses):
- monthlyIncome = earnings, monthlyCost = any costs, duration = years you'll do this

For HOME UPGRADE scenarios (sell current home and buy new one):
- Calculate: Sale proceeds = current home value × 0.94 (6% selling costs)
- Calculate: Purchase costs = new home price × 1.03 (3% closing costs)
- Calculate: Net upfront = (Purchase costs - Sale proceeds)
- Calculate: New mortgage = (new home price × 0.80) assuming 20% down
- Calculate: Monthly mortgage change = (New mortgage payment at 7% for 30yr) - (current mortgage payment)
- upfrontCost = Net upfront (can be negative if downsizing)
- monthlyCost = Increase in mortgage + property tax + maintenance difference (or 0 if negative)
- monthlySavings = Decrease in total housing costs (or 0 if costs increase)
- Important: Account for BOTH upfront costs AND monthly changes

Examples:
"Buy a corvette" -> {"upfrontCost": 70000, "monthlyCost": 400, "monthlySavings": 0, "monthlyIncome": 0, "duration": 8, "sellValue": 35000, "description": "2024 Corvette for 8 years", "age": null}
"Stop eating out" -> {"upfrontCost": 0, "monthlyCost": 0, "monthlySavings": 400, "monthlyIncome": 0, "duration": 20, "sellValue": 0, "description": "Cook at home long-term", "age": null}
"Airbnb spare room" -> {"upfrontCost": 2000, "monthlyCost": 200, "monthlySavings": 0, "monthlyIncome": 1200, "duration": 10, "sellValue": 0, "description": "Rent spare room for 10 years", "age": null}
"Drive for Uber for 5 years" -> {"upfrontCost": 0, "monthlyCost": 300, "monthlySavings": 0, "monthlyIncome": 1500, "duration": 5, "sellValue": 0, "description": "Part-time rideshare", "age": null}
"Buy a boat for 10 years" -> {"upfrontCost": 50000, "monthlyCost": 800, "monthlySavings": 0, "monthlyIncome": 0, "duration": 10, "sellValue": 25000, "description": "Sailboat, sell at end", "age": null}
"Private school K-12" -> {"upfrontCost": 0, "monthlyCost": 2000, "monthlySavings": 0, "monthlyIncome": 0, "duration": 13, "sellValue": 0, "description": "Private school for 13 years", "age": null}
"Buy 1.2M home, sell current 700k home" -> {"upfrontCost": 178000, "monthlyCost": 2800, "monthlySavings": 0, "monthlyIncome": 0, "duration": 30, "sellValue": 0, "description": "Upgrade to 1.2M home (net cost after selling 700k home minus 6% fees + 3% closing)", "age": null}
"Downsize from 800k to 500k home" -> {"upfrontCost": -237000, "monthlyCost": 0, "monthlySavings": 1500, "monthlyIncome": 0, "duration": 30, "sellValue": 0, "description": "Downsize to 500k home (pocket cash from sale + lower mortgage)", "age": null}`;

      const response = await makeAIRequest([
        {
          role: 'system',
          content: 'You are a financial analysis assistant. Always respond with valid JSON only, no markdown formatting.'
        },
        {
          role: 'user',
          content: prompt
        }
      ], { temperature: 0.3, max_tokens: 500 });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      let responseText = data.choices[0].message.content.trim();

      // Clean up the response - remove markdown code blocks if present
      responseText = responseText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

      const costData = JSON.parse(responseText);

      return {
        upfrontCost: costData.upfrontCost || 0,
        monthlyCost: costData.monthlyCost || 0,
        monthlySavings: costData.monthlySavings || 0,
        monthlyIncome: costData.monthlyIncome || 0,
        description: costData.description || "AI-estimated costs",
        extractedAge: costData.age || null
      };
    } catch (error) {
      console.error('Error analyzing lifestyle cost:', error);
      // Smart fallback based on keywords in the description
      const lowerDesc = lifestyleDescription.toLowerCase();

      // Check for home upgrade/move scenarios
      const isHomeUpgrade = (lowerDesc.includes('sell') && lowerDesc.includes('home') && lowerDesc.includes('buy')) ||
                           (lowerDesc.includes('sell') && lowerDesc.includes('house') && lowerDesc.includes('buy')) ||
                           (lowerDesc.includes('upgrade') && lowerDesc.includes('home')) ||
                           (lowerDesc.includes('downsize') && lowerDesc.includes('home'));

      if (isHomeUpgrade) {
        // Try to extract home values from the description
        // Match patterns like "1.2M", "700k", "500000" anywhere in the string
        const priceMatches = lifestyleDescription.match(/\$?(\d+(?:\.\d+)?)\s*([mMkK])?/g);
        let newHomePrice = 800000; // Default
        let currentHomeValue = 500000; // Default

        if (priceMatches && priceMatches.length >= 2) {
          // Parse the prices - look for patterns like "1.2M", "700k", "800000"
          const prices = priceMatches
            .map(match => {
              const num = parseFloat(match.replace(/\$/g, ''));
              if (match.toLowerCase().includes('m')) return num * 1000000;
              if (match.toLowerCase().includes('k')) return num * 1000;
              // Only consider numbers > 100 as home prices (filter out ages, years, etc.)
              return num > 100 ? num : null;
            })
            .filter(price => price !== null && price >= 100000); // Only home-like values (100k+)

          if (prices.length >= 2) {
            newHomePrice = Math.max(...prices);
            currentHomeValue = Math.min(...prices);
          }
        }

        // Calculate home upgrade economics
        // Selling current home
        const saleProceeds = currentHomeValue * 0.94; // Net proceeds after 6% selling costs
        const currentMortgageBalance = currentHomeValue * 0.70; // Assume 70% LTV remaining
        const cashFromSale = saleProceeds - currentMortgageBalance; // Equity you pocket

        // Buying new home
        const newDownPayment = newHomePrice * 0.20; // 20% down payment
        const closingCosts = newHomePrice * 0.03; // 3% closing costs
        const totalPurchaseCash = newDownPayment + closingCosts;

        // Net upfront cash needed
        const netUpfront = totalPurchaseCash - cashFromSale;

        // Monthly payment calculations
        const newMortgage = newHomePrice * 0.80; // 80% LTV
        const currentMortgage = currentHomeValue * 0.70; // 70% LTV (remaining)

        // Monthly payment formula: P * [r(1+r)^n]/[(1+r)^n-1] where r = monthly rate, n = months
        const monthlyRate = 0.07 / 12;
        const months = 360;
        const factor = Math.pow(1 + monthlyRate, months);
        const newPayment = (newMortgage * monthlyRate * factor) / (factor - 1);
        const currentPayment = (currentMortgage * monthlyRate * factor) / (factor - 1);
        const mortgagePaymentDiff = newPayment - currentPayment;

        // Property tax and maintenance scale with home value
        const propertyTaxDiff = (newHomePrice - currentHomeValue) * 0.012 / 12; // 1.2% annual / 12 months
        const maintenanceDiff = (newHomePrice - currentHomeValue) * 0.01 / 12; // 1% annual / 12 months
        const totalMonthlyDiff = mortgagePaymentDiff + propertyTaxDiff + maintenanceDiff;

        return {
          upfrontCost: Math.round(netUpfront),
          monthlyCost: totalMonthlyDiff > 0 ? Math.round(totalMonthlyDiff) : 0,
          monthlySavings: totalMonthlyDiff < 0 ? Math.round(Math.abs(totalMonthlyDiff)) : 0,
          monthlyIncome: 0,
          description: `${newHomePrice > currentHomeValue ? 'Upgrade' : 'Downsize'} from $${(currentHomeValue/1000).toFixed(0)}k to $${(newHomePrice/1000).toFixed(0)}k home (20% down + 3% closing - equity from sale)`,
          extractedAge: null
        };
      }

      // Check for debt payoff vs investment scenarios (CRITICAL financial decision)
      const debtPayoffKeywords = ['pay off', 'payoff', 'pay down', 'debt', 'student loan', 'mortgage', 'loan'];
      const investmentKeywords = ['invest', 'retirement', '401k', 'ira', 'save', 'portfolio'];
      const isDebtPayoffScenario = debtPayoffKeywords.some(kw => lowerDesc.includes(kw)) &&
                                    (lowerDesc.includes('vs') || lowerDesc.includes('or') || investmentKeywords.some(kw => lowerDesc.includes(kw)));

      // Check for cost-cutting keywords
      const savingsKeywords = ['cancel', 'stop', 'quit', 'reduce', 'cut', 'less', 'no more', 'cheaper', 'save', 'eliminate'];
      const isSavingsLikely = savingsKeywords.some(kw => lowerDesc.includes(kw));

      // Check for income keywords (side hustles, gig economy, passive income)
      const incomeKeywords = ['work', 'earn', 'side', 'hustle', 'freelance', 'overtime', 'promotion', 'raise', 'airbnb', 'uber', 'lyft', 'doordash', 'turo', 'rent out', 'consulting', 'tutoring', 'blog', 'youtube', 'etsy', 'ebay', 'business', 'passive income', 'invest', 'rental'];
      const isIncomeLikely = incomeKeywords.some(kw => lowerDesc.includes(kw));

      // Check for big purchase keywords
      const bigPurchaseKeywords = ['buy', 'boat', 'car', 'vacation', 'travel', 'club', 'membership'];
      const isBigPurchase = bigPurchaseKeywords.some(kw => lowerDesc.includes(kw));

      if (isDebtPayoffScenario) {
        // Special handling for debt payoff vs investment trade-offs
        const isStudentLoan = lowerDesc.includes('student') || lowerDesc.includes('student loan');
        const isMortgage = lowerDesc.includes('mortgage') || lowerDesc.includes('house');

        // Default assumptions
        let debtAmount = 50000;
        let interestRate = 0.065; // 6.5% default
        let monthlyPayment = 500;

        if (isStudentLoan) {
          debtAmount = 40000;
          interestRate = 0.055; // Student loans ~5.5%
          monthlyPayment = 450;
        } else if (isMortgage) {
          debtAmount = 200000;
          interestRate = 0.065; // Mortgage ~6.5%
          monthlyPayment = 1500;
        }

        // Calculate extra payment to pay off faster
        const extraPayment = 500; // Extra $500/month toward debt

        // This is a COMPARISON scenario - we show TWO options:
        // Option A: Pay minimum, invest the extra
        // Option B: Pay extra toward debt

        const marketReturn = 0.08; // 8% expected market return
        const guaranteedReturn = interestRate; // Guaranteed by paying down debt
        const breakEvenRate = guaranteedReturn;

        // Risk-adjusted recommendation
        const isHighInterest = interestRate > 0.06;
        const recommendation = isHighInterest
          ? "Pay down debt (guaranteed " + (interestRate * 100).toFixed(1) + "% return beats risky 8% market)"
          : "Invest instead (8% market return likely beats " + (interestRate * 100).toFixed(1) + "% debt interest)";

        // Calculate the ACTUAL compound growth difference between paying off debt vs investing
        // We need to estimate years until retirement to model this properly
        const assumedYearsUntilRetirement = 20; // Conservative assumption

        // Option A: Invest extra payment at market rate for N years
        // Future value of monthly investments: FV = PMT × [((1 + r)^n - 1) / r]
        const monthlyMarketRate = marketReturn / 12;
        const monthsToRetirement = assumedYearsUntilRetirement * 12;
        const futureValueIfInvested = extraPayment *
          ((Math.pow(1 + monthlyMarketRate, monthsToRetirement) - 1) / monthlyMarketRate);

        // Option B: Pay off debt early
        // This is more complex - it depends on remaining loan balance and term
        // Simplified: Calculate interest saved by paying extra
        // Assumption: Extra payments reduce principal, saving future interest
        const monthlyDebtRate = interestRate / 12;

        // Estimate remaining loan term (assume 20 years left)
        const remainingMonths = 240; // 20 years

        // Calculate total interest with minimum payments
        const totalInterestWithMinPayments = (monthlyPayment * remainingMonths) - debtAmount;

        // Calculate how much faster you pay off with extra payments
        // Using simplified payoff calculator: higher payment = fewer months
        const newMonthlyPayment = monthlyPayment + extraPayment;
        let balance = debtAmount;
        let monthsToPayoff = 0;
        let totalPaid = 0;

        for (let m = 0; m < remainingMonths && balance > 0; m++) {
          const interestCharge = balance * monthlyDebtRate;
          const principalPayment = newMonthlyPayment - interestCharge;
          balance -= principalPayment;
          totalPaid += newMonthlyPayment;
          monthsToPayoff++;
          if (balance <= 0) break;
        }

        const totalInterestWithExtraPayments = totalPaid - debtAmount;
        const interestSaved = totalInterestWithMinPayments - totalInterestWithExtraPayments;

        // Add value of freed-up mortgage payments after payoff (if before retirement)
        const monthsFreed = Math.max(0, monthsToRetirement - monthsToPayoff);
        const freedCashflowValue = monthlyPayment * monthsFreed; // Simplified - could compound this too

        const totalValueOfPayoffStrategy = interestSaved + freedCashflowValue;

        // Net difference: Investing vs Paying off debt
        const netDifference = futureValueIfInvested - totalValueOfPayoffStrategy;

        // Convert to equivalent monthly "savings" by amortizing over the period
        // This is a simplified way to make it work with the existing system
        const equivalentMonthlySavings = Math.round(netDifference / monthsToRetirement);

        return {
          upfrontCost: 0,
          monthlyCost: 0,
          monthlySavings: equivalentMonthlySavings, // Positive if investing is better, negative if paying off is better
          monthlyIncome: 0,
          description: `${isStudentLoan ? 'Student loan' : isMortgage ? 'Mortgage' : 'Debt'} at ${(interestRate * 100).toFixed(1)}% vs investing. ${recommendation}`,
          extractedAge: null,
          isDebtComparison: true,
          debtDetails: {
            debtAmount,
            interestRate,
            monthlyPayment,
            extraPayment,
            marketReturn,
            breakEvenRate,
            isHighInterest
          }
        };
      } else if (isSavingsLikely) {
        // Estimate savings - subscriptions ~$100, dining ~$300, etc
        const hasSubscription = lowerDesc.includes('subscription') || lowerDesc.includes('netflix') || lowerDesc.includes('streaming');
        const hasDining = lowerDesc.includes('dining') || lowerDesc.includes('eating out') || lowerDesc.includes('restaurant');
        const estimatedSavings = hasSubscription ? 100 : hasDining ? 400 : 200;
        return {
          upfrontCost: 0,
          monthlyCost: 0,
          monthlySavings: estimatedSavings,
          monthlyIncome: 0,
          description: `Estimated ~$${estimatedSavings}/mo savings`,
          extractedAge: null
        };
      } else if (isIncomeLikely) {
        return {
          upfrontCost: 0,
          monthlyCost: 100,
          monthlySavings: 0,
          monthlyIncome: 1000,
          description: "Estimated additional income (minus time cost)",
          extractedAge: null
        };
      } else if (isBigPurchase) {
        return {
          upfrontCost: 30000,
          monthlyCost: 400,
          monthlySavings: 0,
          monthlyIncome: 0,
          description: "Estimated purchase cost + maintenance",
          extractedAge: null
        };
      } else {
        // Generic moderate expense fallback
        return {
          upfrontCost: 5000,
          monthlyCost: 200,
          monthlySavings: 0,
          monthlyIncome: 0,
          description: "Estimated costs (AI unavailable)",
          extractedAge: null
        };
      }
    }
  };

  return (
    <div className={`backdrop-blur-sm rounded-xl border-2 shadow-lg p-6 ${
      darkMode ? 'bg-slate-800/90 border-blue-600' : 'bg-white/80 border-blue-300'
    }`}>
      <div className="flex items-center gap-2 mb-4">
        <Scale className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
        <h3 className={`text-xl font-bold ${darkMode ? 'text-blue-400' : 'text-gray-900'}`}>Lifestyle Trade-offs</h3>
      </div>

      <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        Explore how lifestyle choices impact your retirement timeline. Each choice shows the trade-off in years until retirement.
      </p>

      {/* AI Input for Custom Lifestyle Choices */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={lifestyleInput}
            onChange={(e) => setLifestyleInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isAnalyzing && lifestyleInput.trim()) {
                document.querySelector('.lifestyle-analyze-btn')?.click();
              }
            }}
            placeholder="e.g., 'Buy a Tesla Model S' or 'Join a country club'"
            className={`flex-1 min-w-0 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              darkMode ? 'bg-slate-700 border-slate-500 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
          <button
            onClick={async () => {
              if (!lifestyleInput.trim()) return;
              setIsAnalyzing(true);

              try {
                const costEstimate = await analyzeLifestyleCost(lifestyleInput);
                // Determine icon based on whether this is beneficial (savings/income) or a cost
                const isBeneficial = (costEstimate.monthlySavings > 0) || (costEstimate.monthlyIncome > costEstimate.monthlyCost);
                const newChoice = {
                  id: Date.now(),
                  name: lifestyleInput,
                  icon: isBeneficial ? "💰" : "✨",
                  upfrontCost: costEstimate.upfrontCost,
                  monthlyCost: costEstimate.monthlyCost,
                  monthlySavings: costEstimate.monthlySavings || 0,
                  monthlyIncome: costEstimate.monthlyIncome || 0,
                  description: costEstimate.description,
                  age: costEstimate.extractedAge || vision.age // Use extracted age if available, otherwise current age
                };
                // Add new choices at the TOP of the list
                setLifestyleChoices([newChoice, ...lifestyleChoices]);
                setLifestyleInput('');
              } catch (error) {
                console.error('Error analyzing lifestyle:', error);
              } finally {
                setIsAnalyzing(false);
              }
            }}
            disabled={isAnalyzing || !lifestyleInput.trim()}
            className="lifestyle-analyze-btn px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium flex items-center justify-center gap-2 shadow-md whitespace-nowrap"
          >
            {isAnalyzing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Analyze with AI
              </>
            )}
          </button>
        </div>
        <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          AI will estimate upfront and ongoing costs, then show impact on your retirement age
        </p>
      </div>

      {/* Lifestyle Choice Cards */}
      <div className="space-y-3">
        {lifestyleChoices.map(choice => {
          const impact = calculateImpact(choice);

          return (
            <div key={choice.id} className={`border-2 rounded-lg p-4 hover:shadow-lg transition-all ${
              darkMode
                ? 'bg-slate-700 border-slate-600 hover:border-blue-400'
                : 'bg-white border-blue-200 hover:border-blue-400'
            }`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{choice.icon}</span>
                  <div>
                    <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{choice.name}</div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{choice.description}</div>
                  </div>
                </div>
                <button
                  className="text-red-500 hover:text-red-700 text-xs"
                  onClick={() => setLifestyleChoices(lifestyleChoices.filter(c => c.id !== choice.id))}
                >
                  Remove
                </button>
              </div>

              {/* Compact inline fields */}
              <div className="flex flex-wrap gap-3 mb-3 text-sm">
                <div className="flex items-center gap-1">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Age:</span>
                  <input
                    type="number"
                    value={choice.age}
                    onChange={(e) => {
                      const updatedChoices = lifestyleChoices.map(c =>
                        c.id === choice.id ? { ...c, age: parseInt(e.target.value) || vision.age } : c
                      );
                      setLifestyleChoices(updatedChoices);
                    }}
                    className={`w-12 font-bold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      darkMode ? 'text-white bg-slate-600 border border-slate-500' : 'text-gray-900 bg-white border border-gray-300'
                    }`}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>For:</span>
                  <input
                    type="number"
                    value={choice.duration || 10}
                    onChange={(e) => {
                      const updatedChoices = lifestyleChoices.map(c =>
                        c.id === choice.id ? { ...c, duration: parseInt(e.target.value) || 10 } : c
                      );
                      setLifestyleChoices(updatedChoices);
                    }}
                    className={`w-12 font-bold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      darkMode ? 'text-white bg-slate-600 border border-slate-500' : 'text-gray-900 bg-white border border-gray-300'
                    }`}
                  />
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>yrs</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Upfront:</span>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>$</span>
                  <input
                    type="number"
                    value={choice.upfrontCost}
                    onChange={(e) => {
                      const updatedChoices = lifestyleChoices.map(c =>
                        c.id === choice.id ? { ...c, upfrontCost: e.target.value === '' ? 0 : parseInt(e.target.value) } : c
                      );
                      setLifestyleChoices(updatedChoices);
                    }}
                    className={`w-20 font-bold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      darkMode ? 'text-white bg-slate-600 border border-slate-500' : 'text-gray-900 bg-white border border-gray-300'
                    }`}
                  />
                </div>
                {choice.upfrontCost > 0 && (
                  <div className="flex items-center gap-1">
                    <span className={darkMode ? 'text-blue-400' : 'text-blue-600'}>Sell:</span>
                    <span className={darkMode ? 'text-blue-400' : 'text-blue-600'}>$</span>
                    <input
                      type="number"
                      value={choice.sellValue || ''}
                      onChange={(e) => {
                        const updatedChoices = lifestyleChoices.map(c =>
                          c.id === choice.id ? { ...c, sellValue: e.target.value === '' ? 0 : parseInt(e.target.value) } : c
                        );
                        setLifestyleChoices(updatedChoices);
                      }}
                      className={`w-20 font-bold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        darkMode ? 'text-blue-400 bg-blue-900/30 border border-blue-700' : 'text-blue-700 bg-blue-50 border border-blue-200'
                      }`}
                      placeholder="0"
                    />
                  </div>
                )}
              </div>

              {/* Monthly costs/income inline */}
              <div className="flex flex-wrap gap-3 mb-3 text-sm">
                {(choice.monthlySavings > 0) ? (
                  <div className="flex items-center gap-1">
                    <span className={darkMode ? 'text-green-400' : 'text-green-600'}>Saves:</span>
                    <span className={darkMode ? 'text-green-400' : 'text-green-600'}>$</span>
                    <input
                      type="number"
                      value={choice.monthlySavings || ''}
                      onChange={(e) => {
                        const updatedChoices = lifestyleChoices.map(c =>
                          c.id === choice.id ? { ...c, monthlySavings: e.target.value === '' ? 0 : parseInt(e.target.value) } : c
                        );
                        setLifestyleChoices(updatedChoices);
                      }}
                      className={`w-16 font-bold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-green-500 ${
                        darkMode ? 'text-green-400 bg-green-900/30 border border-green-700' : 'text-green-700 bg-green-50 border border-green-200'
                      }`}
                    />
                    <span className={darkMode ? 'text-green-400' : 'text-green-600'}>/mo</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Cost:</span>
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>$</span>
                    <input
                      type="number"
                      value={choice.monthlyCost || ''}
                      onChange={(e) => {
                        const updatedChoices = lifestyleChoices.map(c =>
                          c.id === choice.id ? { ...c, monthlyCost: e.target.value === '' ? 0 : parseInt(e.target.value) } : c
                        );
                        setLifestyleChoices(updatedChoices);
                      }}
                      className={`w-16 font-bold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        darkMode ? 'text-white bg-slate-600 border border-slate-500' : 'text-gray-900 bg-white border border-gray-300'
                      }`}
                    />
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>/mo</span>
                  </div>
                )}

                <div className="flex items-center gap-1">
                  <span className={choice.monthlyIncome > 0 ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-gray-400' : 'text-gray-500')}>Income:</span>
                  <span className={choice.monthlyIncome > 0 ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-gray-400' : 'text-gray-500')}>$</span>
                  <input
                    type="number"
                    value={choice.monthlyIncome || ''}
                    onChange={(e) => {
                      const updatedChoices = lifestyleChoices.map(c =>
                        c.id === choice.id ? { ...c, monthlyIncome: e.target.value === '' ? 0 : parseInt(e.target.value) } : c
                      );
                      setLifestyleChoices(updatedChoices);
                    }}
                    className={`w-16 font-bold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-green-500 border ${
                      choice.monthlyIncome > 0
                        ? (darkMode ? 'text-green-400 bg-green-900/30 border-green-700' : 'text-green-700 bg-green-50 border-green-200')
                        : (darkMode ? 'text-white bg-slate-600 border-slate-500' : 'text-gray-900 bg-white border-gray-300')
                    }`}
                  />
                  <span className={choice.monthlyIncome > 0 ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-gray-400' : 'text-gray-500')}>/mo</span>
                </div>
              </div>

              {/* Special UI for Debt Payoff vs Investment Scenarios */}
              {choice.isDebtComparison && choice.debtDetails && (
                <div className={`rounded-lg p-3 mb-3 border ${
                  darkMode ? 'bg-purple-900/30 border-purple-600' : 'bg-purple-50 border-purple-300'
                }`}>
                  <div className={`text-sm font-bold mb-2 ${darkMode ? 'text-purple-400' : 'text-purple-900'}`}>
                    💡 Debt vs Investment Analysis
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {/* Option A: Pay minimum + invest extra */}
                    <div className={`rounded p-2 border ${
                      !choice.debtDetails.isHighInterest
                        ? (darkMode ? 'bg-green-900/50 border-green-600' : 'bg-green-100 border-green-400')
                        : (darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300')
                    }`}>
                      <div className={`font-semibold mb-1 ${
                        !choice.debtDetails.isHighInterest
                          ? (darkMode ? 'text-green-400' : 'text-green-800')
                          : (darkMode ? 'text-gray-300' : 'text-gray-700')
                      }`}>
                        {!choice.debtDetails.isHighInterest ? '✓ ' : ''}Option A: Invest Extra
                      </div>
                      <div className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                        Pay minimum + invest ${choice.debtDetails.extraPayment}/mo
                      </div>
                      <div className={`font-bold mt-1 ${
                        !choice.debtDetails.isHighInterest
                          ? (darkMode ? 'text-green-400' : 'text-green-700')
                          : (darkMode ? 'text-gray-300' : 'text-gray-600')
                      }`}>
                        ~{(choice.debtDetails.marketReturn * 100).toFixed(1)}% expected return
                      </div>
                    </div>

                    {/* Option B: Pay extra toward debt */}
                    <div className={`rounded p-2 border ${
                      choice.debtDetails.isHighInterest
                        ? (darkMode ? 'bg-green-900/50 border-green-600' : 'bg-green-100 border-green-400')
                        : (darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300')
                    }`}>
                      <div className={`font-semibold mb-1 ${
                        choice.debtDetails.isHighInterest
                          ? (darkMode ? 'text-green-400' : 'text-green-800')
                          : (darkMode ? 'text-gray-300' : 'text-gray-700')
                      }`}>
                        {choice.debtDetails.isHighInterest ? '✓ ' : ''}Option B: Pay Down Debt
                      </div>
                      <div className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                        Extra ${choice.debtDetails.extraPayment}/mo toward debt
                      </div>
                      <div className={`font-bold mt-1 ${
                        choice.debtDetails.isHighInterest
                          ? (darkMode ? 'text-green-400' : 'text-green-700')
                          : (darkMode ? 'text-gray-300' : 'text-gray-600')
                      }`}>
                        {(choice.debtDetails.interestRate * 100).toFixed(1)}% guaranteed return
                      </div>
                    </div>
                  </div>

                  {/* Recommendation */}
                  <div className={`mt-2 pt-2 border-t text-xs ${
                    darkMode ? 'border-purple-700 text-purple-300' : 'border-purple-200 text-purple-900'
                  }`}>
                    <span className="font-semibold">Recommendation:</span> {choice.debtDetails.isHighInterest
                      ? `Pay down debt first (${(choice.debtDetails.interestRate * 100).toFixed(1)}% guaranteed return beats risky ${(choice.debtDetails.marketReturn * 100).toFixed(1)}% market)`
                      : `Invest instead (${(choice.debtDetails.marketReturn * 100).toFixed(1)}% market return likely beats ${(choice.debtDetails.interestRate * 100).toFixed(1)}% debt interest)`
                    }
                  </div>

                  {/* Editable debt details */}
                  <div className={`mt-2 pt-2 border-t text-xs ${
                    darkMode ? 'border-purple-700 text-gray-400' : 'border-purple-200 text-gray-600'
                  }`}>
                    <div className="flex justify-between items-center">
                      <span>Debt amount:</span>
                      <div className="flex items-center gap-1">
                        <span>$</span>
                        <input
                          type="number"
                          value={choice.debtDetails.debtAmount}
                          onChange={(e) => {
                            const newAmount = parseInt(e.target.value) || 0;
                            const updatedChoices = lifestyleChoices.map(c =>
                              c.id === choice.id ? {
                                ...c,
                                debtDetails: { ...c.debtDetails, debtAmount: newAmount }
                              } : c
                            );
                            setLifestyleChoices(updatedChoices);
                          }}
                          className={`w-24 font-semibold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                            darkMode ? 'text-white bg-slate-600 border border-slate-500' : 'text-gray-900 bg-white border border-gray-300'
                          }`}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span>Interest rate:</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          step="0.1"
                          value={(choice.debtDetails.interestRate * 100).toFixed(2)}
                          onChange={(e) => {
                            const newRate = parseFloat(e.target.value) / 100 || 0;
                            const isHighInterest = newRate > 0.06;
                            const marketReturn = 0.08;
                            const updatedChoices = lifestyleChoices.map(c => {
                              if (c.id === choice.id) {
                                // Recalculate monthlySavings with new interest rate
                                const opportunityCostMultiplier = isHighInterest
                                  ? (newRate / marketReturn)
                                  : -((marketReturn - newRate) / marketReturn);
                                return {
                                  ...c,
                                  debtDetails: { ...c.debtDetails, interestRate: newRate, isHighInterest },
                                  monthlySavings: Math.round(c.debtDetails.extraPayment * opportunityCostMultiplier)
                                };
                              }
                              return c;
                            });
                            setLifestyleChoices(updatedChoices);
                          }}
                          className={`w-16 font-semibold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                            darkMode ? 'text-white bg-slate-600 border border-slate-500' : 'text-gray-900 bg-white border border-gray-300'
                          }`}
                        />
                        <span>%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span>Current payment:</span>
                      <div className="flex items-center gap-1">
                        <span>$</span>
                        <input
                          type="number"
                          value={choice.debtDetails.monthlyPayment}
                          onChange={(e) => {
                            const newPayment = parseInt(e.target.value) || 0;
                            const updatedChoices = lifestyleChoices.map(c =>
                              c.id === choice.id ? {
                                ...c,
                                debtDetails: { ...c.debtDetails, monthlyPayment: newPayment }
                              } : c
                            );
                            setLifestyleChoices(updatedChoices);
                          }}
                          className={`w-16 font-semibold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                            darkMode ? 'text-white bg-slate-600 border border-slate-500' : 'text-gray-900 bg-white border border-gray-300'
                          }`}
                        />
                        <span>/mo</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span>Extra payment:</span>
                      <div className="flex items-center gap-1">
                        <span>$</span>
                        <input
                          type="number"
                          value={choice.debtDetails.extraPayment}
                          onChange={(e) => {
                            const newExtra = parseInt(e.target.value) || 0;
                            const marketReturn = 0.08;
                            const updatedChoices = lifestyleChoices.map(c => {
                              if (c.id === choice.id) {
                                const isHighInterest = c.debtDetails.isHighInterest;
                                const interestRate = c.debtDetails.interestRate;
                                // Calculate opportunity cost: negative for low-rate debt, positive for high-rate
                                const opportunityCostMultiplier = isHighInterest
                                  ? (interestRate / marketReturn)
                                  : -((marketReturn - interestRate) / marketReturn);
                                return {
                                  ...c,
                                  debtDetails: { ...c.debtDetails, extraPayment: newExtra },
                                  monthlySavings: Math.round(newExtra * opportunityCostMultiplier)
                                };
                              }
                              return c;
                            });
                            setLifestyleChoices(updatedChoices);
                          }}
                          className={`w-16 font-semibold rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                            darkMode ? 'text-white bg-slate-600 border border-slate-500' : 'text-gray-900 bg-white border border-gray-300'
                          }`}
                        />
                        <span>/mo</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Impact Analysis */}
              <div className={`rounded-lg p-3 border ${
                impact.isBeneficial
                  ? (darkMode ? 'bg-green-900/30 border-green-600' : 'bg-green-50 border-green-300')
                  : Math.abs(impact.retirementAgeChange) <= 1
                  ? (darkMode ? 'bg-green-900/30 border-green-600' : 'bg-green-50 border-green-200')
                  : Math.abs(impact.retirementAgeChange) <= 3
                  ? (darkMode ? 'bg-yellow-900/30 border-yellow-600' : 'bg-yellow-50 border-yellow-200')
                  : (darkMode ? 'bg-red-900/30 border-red-600' : 'bg-red-50 border-red-200')
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Impact on Retirement</div>
                    <div className={`text-lg font-bold ${
                      impact.isBeneficial
                        ? (darkMode ? 'text-green-400' : 'text-green-700')
                        : Math.abs(impact.retirementAgeChange) <= 1
                        ? (darkMode ? 'text-green-400' : 'text-green-900')
                        : Math.abs(impact.retirementAgeChange) <= 3
                        ? (darkMode ? 'text-yellow-400' : 'text-yellow-900')
                        : (darkMode ? 'text-red-400' : 'text-red-900')
                    }`}>
                      {impact.retirementAgeChange >= 0 ? '+' : ''}{impact.retirementAgeChange} years
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>New retirement age</div>
                    <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{impact.newRetirementAge}</div>
                  </div>
                </div>
                <div className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {impact.isBeneficial ? (
                    <>
                      {impact.isCostCutting ? (
                        <>
                          Savings from age {impact.purchaseAge} to {vision.retireAge}: <span className={`font-bold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>${(Math.abs(impact.totalImpact) / 1000).toFixed(0)}k</span>
                          <br />
                          (${impact.monthlySavings}/mo saved × {impact.yearsUntilRetirement} years, compounded at 7%)
                        </>
                      ) : (
                        <>
                          Net benefit from age {impact.purchaseAge} to {vision.retireAge}: <span className={`font-bold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>${(Math.abs(impact.totalImpact) / 1000).toFixed(0)}k</span>
                          <br />
                          (${Math.abs(impact.monthlyImpact)}/mo net income × {impact.yearsUntilRetirement} years, compounded at 7%)
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      Cost from age {impact.purchaseAge} to {vision.retireAge}: ${(impact.totalImpact / 1000).toFixed(0)}k
                      <br />
                      (${Math.abs(impact.monthlyImpact)}/mo × {impact.yearsUntilRetirement} years + upfront, with 7% opportunity cost)
                    </>
                  )}
                </div>
              </div>

              {/* Comparison View - only show for expenses, not beneficial choices */}
              {!impact.isBeneficial && (
                <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-slate-600' : 'border-gray-200'}`}>
                  <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>OR you could:</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className={`rounded p-2 ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50'}`}>
                      <div className={darkMode ? 'text-blue-400' : 'text-blue-900'}>💰 Invest it instead</div>
                      <div className={`font-bold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>${((impact.totalCost * 1.5) / 1000).toFixed(0)}k at retirement</div>
                    </div>
                    <div className={`rounded p-2 ${darkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50'}`}>
                      <div className={darkMode ? 'text-green-400' : 'text-green-900'}>🎓 Fund college</div>
                      <div className={`font-bold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>{Math.floor(impact.totalCost / 50000)} years tuition</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Accept/Undo Button */}
              <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-slate-600' : 'border-gray-200'}`}>
                {choice.accepted ? (
                  // Accepted state - show Undo option
                  <div className="space-y-2">
                    <div className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium ${
                      darkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'
                    }`}>
                      <span>✓</span> Accepted
                    </div>
                    <button
                      onClick={() => {
                        // Remove from oneTimeEvents (upfront cost)
                        const oneTimeEvents = (vision.oneTimeEvents || []).filter(
                          e => e.name !== `${choice.name} (upfront)`
                        );

                        // Remove from lifestyleExpenses (ongoing cost/income)
                        const lifestyleExpenses = (vision.lifestyleExpenses || []).filter(
                          e => e.name !== choice.name
                        );

                        // Update vision
                        onUpdate({
                          oneTimeEvents,
                          lifestyleExpenses
                        });

                        // Mark choice as not accepted
                        setLifestyleChoices(lifestyleChoices.map(c =>
                          c.id === choice.id ? { ...c, accepted: false } : c
                        ));
                      }}
                      className={`w-full px-4 py-2 rounded-lg transition-all font-medium text-sm ${
                        darkMode ? 'bg-slate-600 text-gray-200 hover:bg-slate-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Undo
                    </button>
                  </div>
                ) : (
                  // Not yet accepted - show Accept button
                  <>
                    <button
                      onClick={() => {
                        // Add upfront cost as one-time event at the specified age
                        const oneTimeEvents = [...(vision.oneTimeEvents || [])];
                        if (choice.upfrontCost > 0) {
                          oneTimeEvents.push({
                            age: choice.age,
                            name: `${choice.name} (upfront)`,
                            type: 'major_purchase',
                            amount: -choice.upfrontCost // Negative for expense
                          });
                        }

                        // Add to lifestyle expenses array for ongoing tracking
                        const lifestyleExpenses = [...(vision.lifestyleExpenses || [])];
                        // CRITICAL: Include monthlySavings in the net cost calculation
                        // monthlySavings reduces expenses (makes net cost more negative)
                        const netMonthlyCost = (choice.monthlyCost || 0) - (choice.monthlyIncome || 0) - (choice.monthlySavings || 0);

                        if (netMonthlyCost !== 0 || (choice.monthlySavings || 0) !== 0) {
                          lifestyleExpenses.push({
                            id: Date.now(),
                            name: choice.name,
                            monthlyCost: netMonthlyCost, // Positive = expense, Negative = income boost or savings
                            monthlyIncome: choice.monthlyIncome || 0,
                            monthlySavings: choice.monthlySavings || 0, // Store for reference
                            startAge: choice.age,
                            endAge: null, // null means ongoing indefinitely
                            description: choice.description,
                            isIncomeBoost: (choice.monthlyIncome || 0) > (choice.monthlyCost || 0),
                            isSavings: (choice.monthlySavings || 0) > 0
                          });
                        }

                        // Update vision
                        onUpdate({
                          oneTimeEvents,
                          lifestyleExpenses
                        });

                        // Mark choice as accepted (don't remove it)
                        setLifestyleChoices(lifestyleChoices.map(c =>
                          c.id === choice.id ? { ...c, accepted: true } : c
                        ));
                      }}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium shadow-md"
                    >
                      Accept into Model
                    </button>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      {impact.isBeneficial ? (
                        impact.isCostCutting ? (
                          <>Saves ${choice.monthlySavings}/mo starting at age {choice.age}</>
                        ) : (
                          <>Adds +${(choice.monthlyIncome - choice.monthlyCost)}/mo net income starting at age {choice.age}</>
                        )
                      ) : (
                        <>Adds ${choice.upfrontCost.toLocaleString()} at age {choice.age} + ${choice.monthlyCost}/mo ongoing</>
                      )}
                    </p>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LifeMathVision = () => {
  const [visions, setVisions] = useState([]);
  const [activeVision, setActiveVision] = useState(null);
  const [aiInput, setAiInput] = useState('');
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showPresetExplorer, setShowPresetExplorer] = useState(false);
  const [showScenarios, setShowScenarios] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    'personal': true,
    'housing': true,
    'assets': true,
    'family': true,
    'transportation': true,
    'daily-living': true,
    'health-entertainment': true,
    'kids': true,
    'other': true,
    'ai-panel': typeof window !== 'undefined' && window.innerWidth < 1024 // Collapsed on mobile by default, expanded on desktop
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Touch gesture state for swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px) to trigger action
  const minSwipeDistance = 50;

  // Handle touch events for swipe gestures
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    // Swipe left to hide sidebar
    if (isLeftSwipe && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
    // Swipe right to show sidebar (only from edge)
    if (isRightSwipe && sidebarCollapsed && touchStart < 50) {
      setSidebarCollapsed(false);
    }
  };

  const [darkMode, setDarkMode] = useState(() => {
    // Load from localStorage or default to false
    const saved = localStorage.getItem('lifemath-dark-mode');
    return saved === 'true';
  });

  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem('lifemath-dark-mode', darkMode.toString());
  }, [darkMode]);

  // Helper function to format decimal age to years/months/days
  const formatRetirementAge = (decimalAge) => {
    if (!decimalAge || isNaN(decimalAge)) return 'Not set';
    const years = Math.floor(decimalAge);
    const fractionalYear = decimalAge - years;
    const months = Math.floor(fractionalYear * 12);
    const days = Math.floor((fractionalYear * 12 - months) * 30);
    return `${years}y ${months}m ${days}d`;
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Load visions from localStorage on mount, or create sample visions for first-time users
  useEffect(() => {
    const savedVisions = localStorage.getItem('lifemath-visions');
    console.log('Loading from localStorage:', savedVisions ? `${savedVisions.length} chars` : 'none');
    if (savedVisions) {
      try {
        const parsed = JSON.parse(savedVisions);
        console.log('📦 Loaded', parsed.length, 'visions from localStorage');

        // MIGRATION: Remove lifestyle expenses that were saved without monthlySavings field
        // These need to be re-added after the bug fix to work correctly
        const migratedVisions = parsed.map(vision => {
          if (!vision.lifestyleExpenses || vision.lifestyleExpenses.length === 0) {
            return vision;
          }

          const hasInvalidExpenses = vision.lifestyleExpenses.some(exp =>
            exp.monthlySavings === undefined &&
            (exp.name || '').toLowerCase().includes('cut') ||
            (exp.name || '').toLowerCase().includes('save') ||
            (exp.name || '').toLowerCase().includes('reduce')
          );

          if (hasInvalidExpenses) {
            console.log('⚠️ MIGRATION: Clearing invalid lifestyle expenses from:', vision.name);
            console.log('   Please re-add your expense reduction choices');
            return { ...vision, lifestyleExpenses: [] };
          }

          return vision;
        });

        setVisions(migratedVisions);
      } catch (error) {
        console.error('Error loading visions:', error);
        // On error, load sample visions
        const samples = createSampleVisions();
        setVisions(samples);
      }
    } else {
      // First-time user: load sample visions
      console.log('🆕 First-time user: loading sample visions');
      const samples = createSampleVisions();
      setVisions(samples);
      setActiveVision(samples[0].id); // Auto-select first vision
    }
  }, []);

  // Save visions to localStorage whenever they change
  useEffect(() => {
    if (visions.length > 0) {
      console.log('Saving to localStorage:', visions.length, 'visions');
      localStorage.setItem('lifemath-visions', JSON.stringify(visions));
    }
  }, [visions]);

  const createEmptyVision = () => ({
    id: Date.now(),
    name: `Vision ${visions.length + 1}`,
    createdAt: Date.now(),
    lastModified: Date.now(),
    age: 29,
    retireAge: 65,
    autoCalculateRetireAge: false, // DEPRECATED - use autoCalculateMode instead
    // Three-lever auto-calculation system
    // User can set 2 of 3: income, savingsRate, retireAge
    // System auto-calculates the 3rd based on the other 2
    autoCalculateMode: null, // 'retireAge' | 'income' | 'savingsRate' | null
    targetSavingsRate: null, // Target savings rate % (0-75), used when autoCalculateMode !== 'savingsRate'
    housingStatus: 'will-buy', // 'already-own', 'will-buy', 'renting-forever'
    homePurchaseAge: 35,
    currentMortgagePayment: 0, // If already own with mortgage
    yearsLeftOnMortgage: 0,
    hasPartner: true,
    partnerAge: 30, // Partner's current age
    partnerIncomeStart: 60000,
    partnerIncomeStartsInYears: 0,
    partnerRetireAge: 65, // Partner can retire at different age
    kids: 2,
    firstChildAge: 32,
    childSpacing: 3,
    currentIncome: 85000,
    incomeGrowth: 0.03,
    location: 'medium-cost',
    homePrice: 400000,
    downPayment: 80000,
    mortgageRate: 7, // Annual mortgage interest rate percentage
    // Current Assets
    currentCash: 0,
    current401k: 0,
    currentInvestments: 0,
    currentHomeEquity: 0,
    currentOtherAssets: 0,
    // NEW: Debts
    debts: [],
    // NEW: One-time events (windfalls, major purchases, career changes)
    oneTimeEvents: [],
    // NEW: Career timeline
    careerTimeline: [],
    // NEW: Income overrides (user-specified income at specific ages)
    incomeOverrides: {},
    // NEW: College savings (529 plans)
    collegeSavings: {
      enabled: false,
      monthlyContribution: 0,  // DEPRECATED: Now auto-calculated based on targetCoveragePercent
      currentBalance: 0,
      costPerYear: 30000, // estimated college cost per year
      targetCoveragePercent: 100  // What % of college costs to cover (default 100%)
    },
    // NEW: Financial settings (advanced assumptions)
    financialSettings: undefined, // Will use defaults from careerGrowth.js
    // NEW: Lifestyle expenses (accepted lifestyle trade-offs)
    lifestyleExpenses: [],
    // NEW: Lifestyle choices (trade-off examples in the panel)
    lifestyleChoices: [],
    // Precision mode expenses (monthly amounts)
    precisionExpenses: {
      // Housing
      monthlyRent: 0,
      monthlyMortgage: 2800,
      propertyTax: 400,
      homeInsurance: 150,
      utilities: 250,
      homeMaintenance: 200,
      // Transportation
      carPayment: 400,
      carInsurance: 150,
      gas: 200,
      publicTransit: 0,
      carMaintenance: 100,
      // Daily Living
      groceries: 600,
      diningOut: 400,
      shopping: 300,
      personalCare: 100,
      // Healthcare
      healthInsurance: 300,
      medicalExpenses: 150,
      // Entertainment & Lifestyle
      entertainment: 200,
      hobbies: 150,
      travel: 300,
      subscriptions: 100,
      gym: 50,
      // Financial
      retirement401k: 500,
      otherSavings: 200,
      debtPayments: 0,
      // Kids (per child)
      childcare: 1500,
      childEducation: 200,
      childActivities: 150,
      childOther: 200,
      // Other
      gifts: 100,
      charity: 100,
      petExpenses: 0,
      other: 200
    }
  });

  // Derive a complete precision model from life values (for video game mode)
  // eslint-disable-next-line no-unused-vars
  const derivePrecisionFromValues = (vision) => {
    const v = vision.values || { security: 50, freedom: 50, family: 50, career: 50, experiences: 50 };
    const age = vision.age || 29;
    const income = vision.currentIncome || 85000;

    // Derived life decisions based on values
    const derived = {
      // FAMILY VALUE → Partner, Kids, Housing
      hasPartner: v.family > 30,
      kids: v.family < 40 ? 0 : v.family < 60 ? 1 : v.family < 80 ? 2 : 3,
      firstChildAge: v.family > 40 ? age + Math.max(2, Math.floor((35 - age) * (1 - v.family / 200))) : age + 10,
      childSpacing: 3,
      partnerIncomeStart: v.family > 30 ? income * 0.7 : 0,
      partnerIncomeStartsInYears: v.family > 30 ? 0 : 999,
      partnerStopsWorkAtChild: v.family > 70 ? 1 : v.family > 50 ? 2 : 0,

      // FAMILY + FREEDOM → Housing decisions
      housingStatus: v.family > 50 && v.freedom < 60 ? 'will-buy' : v.freedom > 70 ? 'renting-forever' : 'will-buy',
      homePurchaseAge: v.family > 60 ? age + 4 : age + 8,
      homePrice: vision.location === 'high-cost' ? 650000 : vision.location === 'low-cost' ? 300000 : 450000,
      downPayment: 0, // Will save for it

      // CAREER VALUE → Income growth
      incomeGrowth: 0.01 + (v.career / 100) * 0.05, // 1% to 6% based on career focus

      // FREEDOM VALUE → Retirement age
      retireAge: v.freedom > 70 ? 55 : v.freedom > 50 ? 60 : 65,

      // Location (keep existing or default to medium)
      location: vision.location || 'medium-cost'
    };

    // CONSERVATIVE PRECISION EXPENSES - Base monthly amounts
    const locationMultiplier = derived.location === 'high-cost' ? 1.4 : derived.location === 'low-cost' ? 0.7 : 1.0;

    // SECURITY VALUE: Higher security = lower discretionary spending
    const securityFactor = 1.2 - (v.security / 100) * 0.5; // 1.2x at low security, 0.7x at high security

    // FREEDOM VALUE: Higher freedom = lower housing costs, more flexibility
    const housingFactor = 1.1 - (v.freedom / 100) * 0.4; // Lower housing budget if high freedom

    // EXPERIENCES VALUE: Direct impact on travel, entertainment, hobbies
    const experiencesFactor = 0.5 + (v.experiences / 100) * 1.0; // 0.5x to 1.5x

    // Calculate mortgage payment based on actual home price and rate
    const calculateMortgagePayment = (homePrice, downPayment, annualRate) => {
      const loanAmount = homePrice - (downPayment || homePrice * 0.20); // Default 20% down
      const rate = (annualRate || 7) / 100; // Convert percentage to decimal
      const monthlyRate = rate / 12;
      const numPayments = 30 * 12; // 30-year mortgage
      // Monthly payment formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
      const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
      return Math.round(monthlyPayment);
    };

    // Get mortgage based on home price if set, otherwise use default estimate
    const mortgageFromHomePrice = (derived.housingStatus === 'will-buy' || derived.housingStatus === 'already-own')
      ? calculateMortgagePayment(derived.homePrice || 450000, derived.downPayment || 0, vision.mortgageRate || 7)
      : 0;

    const precisionExpenses = {
      // Housing - Conservative estimates
      monthlyRent: derived.housingStatus === 'renting-forever' ? Math.round(1800 * locationMultiplier * housingFactor) : 0,
      monthlyMortgage: mortgageFromHomePrice,
      propertyTax: derived.housingStatus !== 'renting-forever' ? Math.round((derived.homePrice || 450000) * 0.012 / 12) : 0, // ~1.2% of home value annually
      homeInsurance: derived.housingStatus !== 'renting-forever' ? Math.round((derived.homePrice || 450000) * 0.004 / 12) : 100, // ~0.4% of home value annually
      utilities: Math.round(250 * locationMultiplier),
      homeMaintenance: derived.housingStatus !== 'renting-forever' ? Math.round(300 * locationMultiplier) : 0,

      // Transportation - Conservative
      carPayment: v.freedom > 60 ? 0 : Math.round(350 * securityFactor),
      carInsurance: 150,
      gas: v.freedom > 60 ? 100 : 200,
      publicTransit: v.freedom > 60 ? 150 : 0,
      carMaintenance: v.freedom > 60 ? 0 : 100,

      // Daily Living - Conservative, security-adjusted
      groceries: Math.round(700 * locationMultiplier * securityFactor),
      diningOut: Math.round(350 * locationMultiplier * securityFactor),
      shopping: Math.round(250 * securityFactor),
      personalCare: Math.round(100 * securityFactor),

      // Healthcare - Conservative
      healthInsurance: Math.round(400 * locationMultiplier),
      medicalExpenses: 200,

      // Entertainment - Experience-driven
      entertainment: Math.round(200 * experiencesFactor * securityFactor),
      hobbies: Math.round(150 * experiencesFactor * securityFactor),
      travel: Math.round(400 * experiencesFactor * securityFactor), // Conservative monthly travel budget
      subscriptions: Math.round(100 * securityFactor),
      gym: Math.round(80 * securityFactor),

      // Financial - Conservative savings
      retirement401k: Math.round(income * 0.15 / 12), // 15% of income to retirement
      otherSavings: Math.round(income * 0.05 / 12), // 5% to other savings
      debtPayments: 0,

      // Kids - Conservative estimates
      childcare: Math.round(1800 * locationMultiplier),
      childEducation: Math.round(300 * locationMultiplier),
      childActivities: Math.round(200 * locationMultiplier),
      childOther: Math.round(250 * locationMultiplier),

      // Other
      gifts: Math.round(150 * securityFactor),
      charity: Math.round(100 * securityFactor),
      petExpenses: v.family > 50 ? 100 : 0,
      other: Math.round(200 * securityFactor)
    };

    return { ...derived, precisionExpenses };
  };

  const processAIInput = async () => {
    if (!aiInput.trim()) return;

    console.log('🚀 processAIInput called');
    console.log('activeVision:', activeVision);
    console.log('visions:', visions);
    console.log('visions.length:', visions.length);

    const userMessage = aiInput.trim();
    const newConversation = [...conversation, { role: 'user', content: userMessage }];
    setConversation(newConversation);
    setAiInput('');
    setIsProcessingAI(true);

    try {
      // Get AI configuration (API key, endpoint, model)
      // eslint-disable-next-line no-unused-vars
      const aiConfig = getAIConfig();

      // Check if we're updating an existing vision
      const currentVision = activeVision ? visions.find(v => v.id === activeVision) : null;
      const isUpdatingExisting = !!currentVision;

      console.log('currentVision:', currentVision);
      console.log('isUpdatingExisting:', isUpdatingExisting);

      // Safety check - if activeVision is set but vision not found, log error
      if (activeVision && !currentVision) {
        console.error('❌ ERROR: activeVision set but vision not found!', { activeVision, visions });
        throw new Error('Vision not found. Please select a vision from the list.');
      }

      // Build messages for chat completion
      const messages = [
        {
          role: 'system',
          content: isUpdatingExisting
            ? `You are an intelligent life planning assistant helping update an existing life vision.

CURRENT VISION: "${currentVision.name}"
Current details:
- Age: ${currentVision.age}, Retire at: ${currentVision.retireAge}
- Income: $${currentVision.currentIncome.toLocaleString()}
- Partner: ${currentVision.hasPartner ? 'Yes' : 'No'}${currentVision.hasPartner ? ` (Income: $${currentVision.partnerIncomeStart.toLocaleString()}, Retires at: ${currentVision.partnerRetireAge || currentVision.retireAge})` : ''}
- Kids: ${currentVision.kids}${currentVision.kids > 0 ? (currentVision.childrenBirthAges ? ` (Birth ages: ${currentVision.childrenBirthAges.join(', ')})` : ` (First child at age ${currentVision.firstChildAge}, spacing: ${currentVision.childSpacing} years)`) : ''}
- Location: ${currentVision.location}
- Home: ${currentVision.housingStatus === 'will-buy' ? `Will buy at age ${currentVision.homePurchaseAge} ($${currentVision.homePrice.toLocaleString()})` : currentVision.housingStatus === 'already-own' ? 'Already own' : 'Renting forever'}
- Assets: 401k: $${currentVision.current401k.toLocaleString()}, Investments: $${currentVision.currentInvestments.toLocaleString()}, Cash: $${currentVision.currentCash.toLocaleString()}, Home Equity: $${(currentVision.currentHomeEquity || 0).toLocaleString()}, Other: $${currentVision.currentOtherAssets.toLocaleString()}
- Financial Settings: ${currentVision.financialSettings ? `
  * Withdrawal Rate: ${(currentVision.financialSettings.withdrawalRate * 100).toFixed(1)}%
  * Investment Return: ${(currentVision.financialSettings.investmentReturn * 100).toFixed(1)}%
  * Inflation: ${(currentVision.financialSettings.inflationRate * 100).toFixed(1)}%
  * Smart Career Mode: ${currentVision.financialSettings.useSmartCareerMode ? 'ON' : 'OFF'}
  * Auto Promotions: ${currentVision.financialSettings.enableAutoPromotions ? 'ON' : 'OFF'}` : 'Using defaults'}
- Monthly Expenses (precisionExpenses): ${currentVision.precisionExpenses ? `
  * Housing: Rent $${currentVision.precisionExpenses.monthlyRent || 0}, Mortgage $${currentVision.precisionExpenses.monthlyMortgage || 0}, PropTax $${currentVision.precisionExpenses.propertyTax || 0}, Insurance $${currentVision.precisionExpenses.homeInsurance || 0}, Utilities $${currentVision.precisionExpenses.utilities || 0}, Maintenance $${currentVision.precisionExpenses.homeMaintenance || 0}
  * Transport: CarPayment $${currentVision.precisionExpenses.carPayment || 0}, CarIns $${currentVision.precisionExpenses.carInsurance || 0}, Gas $${currentVision.precisionExpenses.gas || 0}, Transit $${currentVision.precisionExpenses.publicTransit || 0}
  * Living: Groceries $${currentVision.precisionExpenses.groceries || 0}, DiningOut $${currentVision.precisionExpenses.diningOut || 0}, Shopping $${currentVision.precisionExpenses.shopping || 0}
  * Fun: Entertainment $${currentVision.precisionExpenses.entertainment || 0}, Travel $${currentVision.precisionExpenses.travel || 0}, Hobbies $${currentVision.precisionExpenses.hobbies || 0}, Subscriptions $${currentVision.precisionExpenses.subscriptions || 0}
  * Other: Gifts $${currentVision.precisionExpenses.gifts || 0}, Charity $${currentVision.precisionExpenses.charity || 0}, Other $${currentVision.precisionExpenses.other || 0}` : 'Using defaults'}

IMPORTANT: The user wants to UPDATE this vision. Be CONFIDENT and DIRECT:

1. If the instruction is CLEAR, respond IMMEDIATELY with ONLY a JSON object - NO questions, NO clarifications!
2. Respond with ONLY the SPECIFIC fields they mentioned (NOT all fields).
3. DO NOT include "name" unless they specifically ask to rename the vision.
4. DO NOT include template placeholders like "string - descriptive name".
5. ONLY ask clarifying questions if the instruction is genuinely AMBIGUOUS (missing numbers, unclear what field to change).

Examples of CLEAR instructions (respond with JSON immediately):
User: "I'm 38"
You: {"age": 38}

User: "Set my age to 35"
You: {"age": 35}

User: "Change retirement age to 70"
You: {"retireAge": 70}

User: "Increase my income to $95,000"
You: {"currentIncome": 95000}

User: "2 kids, one at 36 one at 38"
You: {"kids": 2, "childrenBirthAges": [36, 38]}

User: "fix number of kids! it is 2! one at 36 one at 38!"
You: {"kids": 2, "childrenBirthAges": [36, 38]}

User: "3 kids at ages 30, 32, and 35"
You: {"kids": 3, "childrenBirthAges": [30, 32, 35]}

User: "Add a partner earning $70k"
You: {"hasPartner": true, "partnerIncomeStart": 70000, "partnerIncomeStartsInYears": 0}

User: "partner income needs to be 200k"
You: {"partnerIncomeStart": 200000}

User: "fix partner income needs to be 200k"
You: {"partnerIncomeStart": 200000}

User: "My partner wants to retire at 62"
You: {"partnerRetireAge": 62}

User: "Move to a high-cost city and buy a $600k home"
You: {"location": "high-cost", "housingStatus": "will-buy", "homePrice": 600000}

User: "we already own our home"
You: {"housingStatus": "already-own"}

User: "we own our home, 25 years left on mortgage at $2200/month"
You: {"housingStatus": "already-own", "yearsLeftOnMortgage": 25, "currentMortgagePayment": 2200}

User: "mortgage is $2500/month with 20 years left, property tax is $600/month"
You: {"housingStatus": "already-own", "currentMortgagePayment": 2500, "yearsLeftOnMortgage": 20, "precisionExpenses": {"propertyTax": 600}}

User: "property tax is $700/month"
You: {"precisionExpenses": {"propertyTax": 700}}

User: "buy a home at age 40 for $500k"
You: {"housingStatus": "will-buy", "homePurchaseAge": 40, "homePrice": 500000}

User: "we're going to rent forever"
You: {"housingStatus": "renting-forever"}

User: "fix assets we have 1.2m 401k and 300k investments and 100k cash"
You: {"current401k": 1200000, "currentInvestments": 300000, "currentCash": 100000}

User: "we have $50k in savings"
You: {"currentCash": 50000}

User: "add a one-time purchase of a $50k car at age 35"
You: {"oneTimeEvents": [{"name": "Car Purchase", "type": "car_purchase", "age": 35, "amount": -50000, "id": ${Date.now()}}]}

User: "I'll receive a $100k inheritance at age 45"
You: {"oneTimeEvents": [{"name": "Inheritance", "type": "inheritance", "age": 45, "amount": 100000, "id": ${Date.now()}}]}

User: "wedding at 32 for $30k"
You: {"oneTimeEvents": [{"name": "Wedding", "type": "wedding", "age": 32, "amount": -30000, "id": ${Date.now()}}]}

User: "set withdrawal rate to 3.5%"
You: {"financialSettings": {"withdrawalRate": 0.035}}

User: "I want to use a more conservative 3% withdrawal rate"
You: {"financialSettings": {"withdrawalRate": 0.03}}

User: "change investment returns to 8%"
You: {"financialSettings": {"investmentReturn": 0.08}}

User: "set inflation to 3%"
You: {"financialSettings": {"inflationRate": 0.03}}

User: "turn on smart career mode"
You: {"financialSettings": {"useSmartCareerMode": true}}

User: "disable auto promotions"
You: {"financialSettings": {"enableAutoPromotions": false}}

User: "make it more conservative - 3% withdrawal and 7% returns"
You: {"financialSettings": {"withdrawalRate": 0.03, "investmentReturn": 0.07}}

IMPORTANT FOR EVENTS:
- For expenses (purchases, wedding, etc.), use NEGATIVE amounts (e.g., -50000)
- For income (windfall, inheritance, bonus), use POSITIVE amounts (e.g., 100000)
- Always include: name, type, age, amount, and id (use timestamp)
- Event types: "windfall", "inheritance", "bonus", "major_purchase", "car_purchase", "wedding", "home_renovation", "vacation"

IMPORTANT FOR FINANCIAL SETTINGS:
- withdrawalRate: Decimal (0.04 = 4%), the safe withdrawal rate in retirement (typically 3-5%)
- investmentReturn: Decimal (0.10 = 10%), expected annual investment returns
- inflationRate: Decimal (0.03 = 3%), expected annual inflation
- useSmartCareerMode: Boolean, realistic age-based income growth
- enableAutoPromotions: Boolean, automatic promotion events at career milestones
- When updating financialSettings, merge with existing settings (only update specified fields)

IMPORTANT FOR RETIREMENT INCOME (PENSIONS):
- retirementIncome: Annual pension/retirement income in today's dollars (automatically inflation-adjusted)
- Use for teachers, military, government workers, or anyone with guaranteed retirement income
- For age-specific retirement income (e.g., delayed retirement, COLA adjustments), use incomeOverrides

Examples:
User: "I'll have a $50k pension"
You: {"retirementIncome": 50000}

User: "50k a year after retiring"
You: {"retirementIncome": 50000}

User: "Teacher pension of $35k per year"
You: {"retirementIncome": 35000}

User: "Military pension $40k starting at retirement"
You: {"retirementIncome": 40000}

User: "Pension increases to $60k at age 68"
You: {"retirementIncome": 50000, "incomeOverrides": {"68": {"income": 60000}}}

IMPORTANT FOR CHILDREN:
- Use "childrenBirthAges" array to specify the exact age at which each child is born
- Example: {"kids": 2, "childrenBirthAges": [36, 38]} means 2 kids born when user is age 36 and 38
- Childcare costs are automatically adjusted by age:
  * Ages 0-4 (preschool): Full childcare cost applies
  * Ages 5-17 (school age): 30% of childcare cost (after-school care, summer camps)
  * Age 18+: No childcare costs
- Still support old format with firstChildAge and childSpacing for backwards compatibility

IMPORTANT FOR ADJUSTING MONTHLY EXPENSES (precisionExpenses):
When user asks to "adjust expenses", "reduce costs", "make retirement possible", or "cut spending", modify the precisionExpenses object.
All values are MONTHLY amounts in dollars. Here are the fields you can adjust:

Housing: monthlyRent, monthlyMortgage, propertyTax, homeInsurance, utilities, homeMaintenance
Transportation: carPayment, carInsurance, gas, publicTransit, carMaintenance
Daily Living: groceries, diningOut, shopping, personalCare
Healthcare: healthInsurance, medicalExpenses
Entertainment: entertainment, travel, hobbies, subscriptions
Children: childcare (automatically reduces at age 5), childEducation, childActivities, childOther
Other: gifts, charity, petExpenses, other

Example - User says "cut expenses to make retirement possible":
You: {"precisionExpenses": {"diningOut": 200, "entertainment": 150, "travel": 100, "shopping": 150, "hobbies": 50, "subscriptions": 50}}

Example - User says "reduce monthly spending by $1000":
You: {"precisionExpenses": {"diningOut": 100, "entertainment": 50, "travel": 50, "shopping": 100, "subscriptions": 30}}

Example - User says "get a cheaper car":
You: {"precisionExpenses": {"carPayment": 300, "carInsurance": 100, "gas": 150}}

IMPORTANT: When adjusting expenses:
- Be aggressive with discretionary spending (dining, entertainment, shopping, travel)
- Keep essentials reasonable (groceries, utilities, insurance)
- Consider housing alternatives (cheaper home, renting instead of buying)
- A typical frugal budget might be: groceries $500, diningOut $100, entertainment $50, travel $100

Examples of AMBIGUOUS instructions (ask questions):
User: "I want to retire earlier"
You: "How much earlier would you like to retire? What age are you thinking?"

User: "Add a partner"
You: "Great! What's your partner's income?"

RULE: If you see numbers and clear intent, just do it. Don't ask unnecessary questions!`
            : `You are an intelligent life planning assistant. Your job is to help users create detailed life vision models.

When a user describes their life goals, you should:

1. If you have enough information to create a complete vision, respond with ONLY a JSON object (no other text) in this format:
{
  "name": "Early Retirement Dream",
  "age": 29,
  "retireAge": 50,
  "housingStatus": "will-buy",
  "homePurchaseAge": 35,
  "hasPartner": true,
  "partnerIncomeStart": 80000,
  "partnerIncomeStartsInYears": 0,
  "partnerStopsWorkAtChild": 0,
  "partnerRetireAge": 55,
  "kids": 2,
  "firstChildAge": 32,
  "childSpacing": 3,
  "currentIncome": 120000,
  "incomeGrowth": 3,
  "location": "medium-cost",
  "homePrice": 500000,
  "downPayment": 100000,
  "currentCash": 50000,
  "current401k": 80000,
  "currentInvestments": 30000,
  "currentOtherAssets": 0,
  "oneTimeEvents": [
    {
      "name": "New Car Purchase",
      "type": "car_purchase",
      "age": 35,
      "amount": -45000,
      "id": 1234567890
    }
  ]
}

CRITICAL: Always create a descriptive name based on what the user described!
Examples:
- User says "retire at 45 and travel": name = "Early Retirement Travel"
- User says "start family and buy home": name = "Family & Homeowner"
- User says "FIRE by 50": name = "Financial Independence at 50"

2. If the user's input is vague or missing critical information, ask 2-3 intelligent clarifying questions. Be conversational and helpful. Do NOT respond with JSON in this case.

Make reasonable assumptions when possible. For example:
- If they mention wanting kids but not when, assume early 30s
- If they mention a partner but no income, assume similar to theirs
- If they mention a partner but no retirement age, assume same as user's retirement age
- If no location specified, assume medium-cost
- If buying a home but no purchase age specified, assume 5 years from current age
- If no housing mentioned, assume "will-buy"
- Infer values from their goals (e.g., "travel the world" = high experiences value)

IMPORTANT FIELD NAMES:
- Housing: Use "housingStatus" (NOT "homeBought"), values: "will-buy", "already-own", "renting-forever"
- Home purchase timing: Use "homePurchaseAge" (NOT "homeBuyAge")
- Existing mortgage: Use "currentMortgagePayment" (monthly), "yearsLeftOnMortgage" (integer years)
- Property tax: Use "precisionExpenses": {"propertyTax": X} where X is monthly amount
- Assets: Use "current401k", "currentInvestments", "currentCash", "currentHomeEquity", "currentOtherAssets"

Be smart and helpful. Never fail - either ask questions or make reasonable assumptions.`
        },
        ...newConversation.map(msg => ({
          role: msg.role,
          content: msg.content
        }))
      ];

      const response = await makeAIRequest(messages, { temperature: 0.7, max_tokens: 2000 });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        let errorMsg = `API Error: ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMsg = errorData.error?.message || errorData.error || errorMsg;
        } catch {
          errorMsg = errorText || errorMsg;
        }
        console.error('AI Request failed:', response.status, errorMsg, errorText);
        throw new Error(errorMsg);
      }

      const data = await response.json();
      let responseText = data.choices[0].message.content.trim();

      // Try to parse as JSON first
      let visionParams = null;
      let isVisionReady = false;

      // Strip markdown code blocks if present
      let cleanedText = responseText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();

      // Try to extract JSON from conversational text (e.g., "Sure! {json}")
      const jsonMatch = cleanedText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        cleanedText = jsonMatch[0];
      }

      console.log('=== AI Response Debug ===');
      console.log('Raw Response:', responseText);
      console.log('Cleaned Text:', cleanedText);
      console.log('Is Updating Existing?', isUpdatingExisting);

      try {
        visionParams = JSON.parse(cleanedText);
        isVisionReady = true;
        console.log('✅ JSON Parse SUCCESS - Vision Params:', visionParams);
      } catch (error) {
        // Not JSON, it's a conversational response with questions
        isVisionReady = false;
        console.log('❌ JSON Parse FAILED - Treating as conversational response');
        console.log('Parse Error:', error.message);
      }

      if (isVisionReady && visionParams) {
        // Clean up template placeholder values
        const cleanParams = {};
        for (const [key, value] of Object.entries(visionParams)) {
          // Skip fields with template placeholder text
          if (typeof value === 'string' && (
            value.includes('string -') ||
            value.includes('descriptive name') ||
            value.includes('number') ||
            value.toLowerCase().includes('placeholder')
          )) {
            continue; // Skip this field
          }
          cleanParams[key] = value;
        }

        // Map common AI field name variations to correct field names
        if (cleanParams.currentAge !== undefined) {
          console.log('🔄 Mapping currentAge to age:', cleanParams.currentAge);
          cleanParams.age = cleanParams.currentAge;
          delete cleanParams.currentAge;
        }

        console.log('Clean Params (after mapping):', cleanParams);

        if (isUpdatingExisting) {
          console.log('🔄 UPDATING EXISTING VISION');
          console.log('Clean Params to Apply:', cleanParams);

          // Update in visions array - use functional update to get fresh state
          setVisions(prevVisions => {
            // Find the current vision from fresh state (not stale closure)
            const freshVision = prevVisions.find(v => v.id === activeVision);
            if (!freshVision) {
              console.error('❌ Could not find vision to update:', activeVision);
              return prevVisions;
            }

            // Special handling for financialSettings - merge instead of replace
            const mergedFinancialSettings = cleanParams.financialSettings
              ? {
                  ...(freshVision.financialSettings || {}),
                  ...cleanParams.financialSettings
                }
              : freshVision.financialSettings;

            // Special handling for precisionExpenses - merge instead of replace
            const mergedPrecisionExpenses = cleanParams.precisionExpenses
              ? {
                  ...(freshVision.precisionExpenses || {}),
                  ...cleanParams.precisionExpenses
                }
              : freshVision.precisionExpenses;

            // Build updated vision from fresh state
            const updatedVision = {
              ...freshVision,
              ...cleanParams,
              lastModified: Date.now(),
              // Preserve existing arrays if not provided in update
              debts: cleanParams.debts !== undefined ? cleanParams.debts : freshVision.debts,
              oneTimeEvents: cleanParams.oneTimeEvents !== undefined ? cleanParams.oneTimeEvents : freshVision.oneTimeEvents,
              careerTimeline: cleanParams.careerTimeline !== undefined ? cleanParams.careerTimeline : freshVision.careerTimeline,
              incomeOverrides: cleanParams.incomeOverrides !== undefined ? cleanParams.incomeOverrides : freshVision.incomeOverrides,
              collegeSavings: cleanParams.collegeSavings !== undefined ? cleanParams.collegeSavings : freshVision.collegeSavings,
              // Merge financial settings
              financialSettings: mergedFinancialSettings,
              // Merge precision expenses
              precisionExpenses: mergedPrecisionExpenses
            };

            console.log('Updated Vision:', updatedVision);
            const updated = prevVisions.map(v => v.id === activeVision ? updatedVision : v);
            console.log('✅ Vision array updated via functional setVisions');
            return updated;
          });

          // Show confirmation (append to conversation history)
          const changedFields = Object.keys(cleanParams).filter(k => k !== 'name').join(', ');
          setConversation([
            ...newConversation,
            {
              role: 'assistant',
              content: `Perfect! I've updated "${currentVision.name}" with your changes${changedFields ? `: ${changedFields}` : ''}. The charts should now reflect your updated vision. Let me know if you'd like to adjust anything else!`
            }
          ]);
          console.log('✅ Confirmation message added to conversation');
        } else {
          // Create new vision
          const emptyVision = createEmptyVision();
          const newVision = {
            ...emptyVision,
            id: Date.now(),
            createdAt: Date.now(),
            lastModified: Date.now(),
            ...cleanParams,
            // Ensure new fields exist
            debts: cleanParams.debts || [],
            oneTimeEvents: cleanParams.oneTimeEvents || [],
            careerTimeline: cleanParams.careerTimeline || [],
            incomeOverrides: cleanParams.incomeOverrides || {},
            collegeSavings: cleanParams.collegeSavings || {
              enabled: false,
              monthlyContribution: 0,
              currentBalance: 0,
              costPerYear: 30000,
              targetCoveragePercent: 100
            }
          };

          setVisions([...visions, newVision]);
          setActiveVision(newVision.id);

          // Show a friendly confirmation message instead of JSON (append to conversation history)
          const visionName = cleanParams.name || 'Your Vision';
          setConversation([
            ...newConversation,
            {
              role: 'assistant',
              content: `Great! I've created a vision called "${visionName}" based on your goals. You can see it in the list on the left and customize it further using the panels below. Feel free to add debts, one-time events, or career changes to make it even more accurate!`
            }
          ]);
        }
      } else {
        // AI asked clarifying questions - show conversational response
        console.log('💬 CONVERSATIONAL RESPONSE - Not JSON, showing as text');
        console.log('Response shown to user:', responseText);
        setConversation([...newConversation, { role: 'assistant', content: responseText }]);
      }
    } catch (error) {
      console.error("Error processing AI input:", error);

      // Provide helpful error messages based on error type
      let errorMessage = "I'm having trouble connecting to the AI service. ";

      if (error.message.includes("API key not configured")) {
        errorMessage += "Server API key not configured. Please add GROQ_API_KEY to Vercel environment variables and redeploy.";
      } else if (error.message.includes("401") || error.message.includes("authentication")) {
        errorMessage += "API key authentication failed.";
      } else if (error.message.includes("429")) {
        errorMessage += "Too many requests. Please wait a moment.";
      } else if (error.message.includes("fetch") || error.message.includes("network")) {
        errorMessage += "Please check your internet connection.";
      } else {
        errorMessage += `Error: ${error.message}`;
      }

      setConversation([...newConversation, {
        role: 'assistant',
        content: errorMessage
      }]);
    } finally {
      setIsProcessingAI(false);
    }
  };

  // Helper: Calculate required monthly 529 contribution based on target coverage %
  const calculateRequired529Contribution = (vision) => {
    const collegeSavings = vision.collegeSavings || {};
    if (!collegeSavings.enabled) return 0;

    const numKids = vision.numKids || 0;
    const firstChildAtAge = vision.firstChildAtAge || 0;
    const currentAge = vision.age || 30;
    const retireAge = vision.retireAge || 65;
    const currentBalance = collegeSavings.currentBalance || 0;
    const costPerYear = collegeSavings.costPerYear || 30000;
    const targetCoveragePercent = collegeSavings.targetCoveragePercent !== undefined
      ? collegeSavings.targetCoveragePercent
      : 100;

    if (numKids === 0 || firstChildAtAge === 0) return 0;

    // Calculate years until first child starts college (child will be 18)
    const childCurrentAge = currentAge - firstChildAtAge;
    const yearsUntilCollege = Math.max(0, 18 - childCurrentAge);

    if (yearsUntilCollege === 0) return 0; // Too late

    // Calculate how many years we can contribute (until retirement or college starts)
    const yearsToContribute = Math.min(yearsUntilCollege, retireAge - currentAge);
    if (yearsToContribute <= 0) return 0;

    // Assume 6% annual return on 529 investments
    const annualReturn = 0.06;
    const monthlyReturn = annualReturn / 12;

    // Calculate total college costs needed with 3% inflation
    const costInflation = 0.03;
    let totalCostNeeded = 0;

    for (let kid = 0; kid < numKids; kid++) {
      const yearsFromFirstChild = kid * 2; // Kids spaced 2 years apart
      const yearsFromNow = yearsUntilCollege + yearsFromFirstChild;

      for (let year = 0; year < 4; year++) {
        const yearFromNow = yearsFromNow + year;
        const inflatedCost = costPerYear * Math.pow(1 + costInflation, yearFromNow);
        totalCostNeeded += inflatedCost;
      }
    }

    // Apply target coverage percentage
    const targetAmount = totalCostNeeded * (targetCoveragePercent / 100);

    // Calculate future value of current balance
    const monthsToContribute = yearsToContribute * 12;
    const futureValueOfCurrent = currentBalance * Math.pow(1 + monthlyReturn, monthsToContribute);

    const futureValueNeeded = targetAmount - futureValueOfCurrent;
    if (futureValueNeeded <= 0) return 0; // Already funded

    // Calculate required monthly contribution
    const fvFactor = (Math.pow(1 + monthlyReturn, monthsToContribute) - 1) / monthlyReturn;
    const requiredMonthly = Math.ceil(futureValueNeeded / fvFactor);

    return requiredMonthly;
  };

  const calculateFinancials = (vision) => {
    // ALWAYS use the precision values directly - sliders are just for setting defaults
    // This ensures video game mode and precision mode stay in sync
    let effectiveVision = vision;

    // If auto-calculate retirement age is enabled, calculate based on safe withdrawal rate
    // (but don't do this if we're already in the middle of calculating it to avoid infinite loop)
    if (effectiveVision.autoCalculateRetireAge && !effectiveVision._calculatingRetireAge) {
      const calculatedAge = calculateOptimalRetirementAge({ ...effectiveVision, _calculatingRetireAge: true });
      effectiveVision = { ...effectiveVision, retireAge: calculatedAge };
      console.log('🔍 Auto-calculated retirement age (safe withdrawal rate):', calculatedAge);
    }

    const years = [];
    const startAge = effectiveVision.age;
    const endAge = Math.max(effectiveVision.retireAge + 30, 95); // Project to at least age 95
    // eslint-disable-next-line no-unused-vars
    const locationMultiplier = effectiveVision.location === 'high-cost' ? 1.3 : effectiveVision.location === 'medium-cost' ? 1.0 : 0.75;

    // Get financial settings (user-configured or defaults)
    const settings = getFinancialSettings(effectiveVision.financialSettings);

    // Only use dynamic withdrawal rate if user hasn't set a custom one
    // Check if user has explicitly set withdrawalRate in financialSettings
    if (!effectiveVision.financialSettings || effectiveVision.financialSettings.withdrawalRate === undefined) {
      settings.withdrawalRate = calculateWithdrawalRate(effectiveVision.age, effectiveVision.retireAge);
    }
    // Otherwise, keep the user's custom withdrawal rate from settings

    // Financial constants (now from settings)
    const INFLATION_RATE = settings.inflationRate;
    const NOMINAL_RETURN = settings.investmentReturn;
    // eslint-disable-next-line no-unused-vars
    const REAL_RETURN = NOMINAL_RETURN - INFLATION_RATE;
    const SOCIAL_SECURITY_AGE = 67;
    const SOCIAL_SECURITY_AMOUNT = 25000; // Base amount, will be modified by settings.socialSecurityMultiplier

    // Start with current assets (EXCLUDING home equity - not withdrawable)
    // Home equity reduces expenses when mortgage is paid off but cannot be withdrawn
    let portfolio = (effectiveVision.currentCash || 0) + (effectiveVision.current401k || 0) +
                    (effectiveVision.currentInvestments || 0) + (effectiveVision.currentOtherAssets || 0);

    // Debug: Log starting portfolio
    if (effectiveVision.autoCalculateRetireAge && !effectiveVision._calculatingRetireAge) {
      console.log('💰 Starting portfolio (withdrawable assets only):', {
        cash: effectiveVision.currentCash || 0,
        '401k': effectiveVision.current401k || 0,
        investments: effectiveVision.currentInvestments || 0,
        homeEquity: '(excluded - not withdrawable)',
        other: effectiveVision.currentOtherAssets || 0,
        total: portfolio
      });
    }

    // Note: Withdrawals are calculated dynamically based on expense deficit (netCashFlow < 0)
    // This naturally adjusts for inflation since expenses are inflated each year
    let college529Balance = effectiveVision.collegeSavings?.currentBalance || 0;

    // Track active debts
    const activeDebts = (effectiveVision.debts || []).map(debt => ({...debt}));

    // Track mortgage payoff
    const mortgageYearsLeft = effectiveVision.yearsLeftOnMortgage || 0;
    const mortgagePayoffAge = effectiveVision.age + mortgageYearsLeft;

    // Track home equity dynamically (for net worth display)
    let homeEquity = effectiveVision.currentHomeEquity || 0;
    let homeValue = 0;
    let mortgageBalance = 0;

    // Initialize home values based on housing status
    if (effectiveVision.housingStatus === 'already-own') {
      // Estimate home value from equity + remaining mortgage
      const currentMortgage = (effectiveVision.currentMortgagePayment || 0) * 12 * mortgageYearsLeft;
      homeValue = homeEquity + currentMortgage;
      mortgageBalance = currentMortgage;
    } else if (effectiveVision.housingStatus === 'will-buy') {
      // Home not yet purchased
      homeValue = 0;
      mortgageBalance = 0;
    }

    const HOME_APPRECIATION_RATE = 0.03; // 3% annual appreciation
    const currentMortgagePayment = effectiveVision.currentMortgagePayment || 0;

    // Track career progression for smart growth
    let currentSalary = effectiveVision.currentIncome;
    let yearsAtCurrentSalary = 0;

    for (let age = startAge; age <= endAge; age++) {
      const yearsFromStart = age - startAge;
      const inflationMultiplier = Math.pow(1 + INFLATION_RATE, yearsFromStart);

      const yearData = {
        age,
        grossIncome: 0,
        workIncome: 0,
        retirementIncome: 0,
        portfolioDistribution: 0, // Track withdrawals from portfolio during retirement
        totalIncome: 0,
        netIncome: 0,
        taxes: 0,
        expenses: 0,
        netCashFlow: 0,
        portfolioGrowth: 0,
        portfolioValue: 0,
        debtPayments: 0,
        college529: 0,
        stress: 0
      };

      // ============ INCOME ============

      // DEBUG: Check why income calculation might be skipped
      if (age === effectiveVision.age) {
        console.log('🔎 INCOME CONDITION CHECK (Age ' + age + '):');
        console.log('  age:', age);
        console.log('  effectiveVision.retireAge:', effectiveVision.retireAge);
        console.log('  age < retireAge:', age < effectiveVision.retireAge);
        console.log('  effectiveVision.currentIncome:', effectiveVision.currentIncome);
      }

      // Working income (before YOUR retirement)
      if (age < effectiveVision.retireAge) {
        // Check for income overrides first (from Income Projections table)
        const incomeOverrides = effectiveVision.incomeOverrides || {};
        const override = incomeOverrides[age];

        if (override && override.income !== undefined) {
          // User explicitly set income for this age
          currentSalary = override.income;
          yearsAtCurrentSalary = 0; // Reset since income was manually set
          yearData.workIncome = currentSalary;
        } else {
          // Check for legacy career timeline events
          const careerEvent = (effectiveVision.careerTimeline || []).find(e => e.age === age);

          if (careerEvent) {
            if (careerEvent.type === 'job_change' || careerEvent.type === 'promotion') {
              currentSalary = careerEvent.newIncome || currentSalary;
              yearsAtCurrentSalary = 0; // Reset counter on promotion/job change
              yearData.workIncome = currentSalary;
            } else if (careerEvent.type === 'layoff' || careerEvent.type === 'career_break') {
              yearData.workIncome = careerEvent.unemploymentIncome || 0;
              // Don't update currentSalary or reset counter during layoffs
            } else {
              // Use smart career growth
              const growthRate = calculateSmartGrowthRate(age, yearsAtCurrentSalary, settings);
              currentSalary = currentSalary * (1 + growthRate);
              yearsAtCurrentSalary++;
              yearData.workIncome = currentSalary;
            }
          } else {
            // No manual event - use smart career growth
            const growthRate = calculateSmartGrowthRate(age, yearsAtCurrentSalary, settings);

            // DEBUG: Log income calculation at current age
            if (age === effectiveVision.age) {
              console.log('💼 YOUR INCOME CALCULATION (Age ' + age + '):');
              console.log('  Starting currentIncome from vision:', effectiveVision.currentIncome);
              console.log('  currentSalary before growth:', currentSalary);
              console.log('  Growth rate:', growthRate);
              console.log('  Years at current salary:', yearsAtCurrentSalary);
            }

            currentSalary = currentSalary * (1 + growthRate);
            yearsAtCurrentSalary++;
            yearData.workIncome = currentSalary;

            if (age === effectiveVision.age) {
              console.log('  currentSalary after growth:', Math.round(currentSalary));
              console.log('  yearData.workIncome:', Math.round(yearData.workIncome));
            }
          }
        }
      }

      // Partner income - partner works until THEIR retirement age (independent of yours)
      // partnerRetireAge is the PARTNER'S actual age when they retire (not user's age)
      // So if user is 39 and partner is 33, and partnerRetireAge is 65, partner works until
      // PARTNER turns 65 (when user is 71)
      if (effectiveVision.hasPartner && effectiveVision.partnerIncomeStart > 0) {
        const yearsFromNow = age - effectiveVision.age; // How many years in the future
        const partnerCurrentAge = effectiveVision.partnerAge || effectiveVision.age; // Default to same age if not specified
        const partnerAgeThisYear = partnerCurrentAge + yearsFromNow;
        const partnerStartDelay = effectiveVision.partnerIncomeStartsInYears || 0; // Default to 0 (starts now)
        const partnerStartAge = partnerCurrentAge + partnerStartDelay;

        // partnerRetireAge is the partner's ACTUAL retirement age, default to user's retirement age
        const partnerRetireAge = effectiveVision.partnerRetireAge || effectiveVision.retireAge;

        // DEBUG: Log at current age only
        if (age === effectiveVision.age) {
          console.log('🔍 PARTNER INCOME DEBUG (Age ' + age + '):');
          console.log('  Partner current age:', partnerCurrentAge);
          console.log('  Partner age this year:', partnerAgeThisYear);
          console.log('  Partner retire age:', partnerRetireAge);
          console.log('  Partner start age:', partnerStartAge);
          console.log('  Partner income start:', effectiveVision.partnerIncomeStart);
          console.log('  Your work income BEFORE partner:', Math.round(yearData.workIncome));
          console.log('  Check: ' + partnerAgeThisYear + ' >= ' + partnerStartAge + ' && ' + partnerAgeThisYear + ' < ' + partnerRetireAge + ' = ' + (partnerAgeThisYear >= partnerStartAge && partnerAgeThisYear < partnerRetireAge));
        }

        // Partner earns income from their start age until THEY reach their retirement age
        // Check: partner has started working AND partner hasn't reached THEIR retirement age yet
        if (partnerAgeThisYear >= partnerStartAge && partnerAgeThisYear < partnerRetireAge) {
          // Check for partner income override
          const incomeOverrides = effectiveVision.incomeOverrides || {};
          const override = incomeOverrides[age];

          let partnerIncome;
          if (override && override.partnerIncome !== undefined) {
            // User explicitly set partner income for this age
            partnerIncome = override.partnerIncome;
          } else {
            // Use default growth model (2% annual)
            const yearsWorked = partnerAgeThisYear - partnerStartAge;
            partnerIncome = effectiveVision.partnerIncomeStart * Math.pow(1.02, yearsWorked);
          }
          yearData.workIncome += partnerIncome;
          yearData.partnerIncome = partnerIncome; // Track separately for debugging/display

          if (age === effectiveVision.age) {
            console.log('  ✅ PARTNER INCOME ADDED:', Math.round(partnerIncome));
            console.log('  Total workIncome after partner:', Math.round(yearData.workIncome));
          }
        } else {
          if (age === effectiveVision.age) {
            console.log('  ❌ PARTNER INCOME SKIPPED - condition failed');
          }
        }
      }

      // Retirement income
      if (age >= effectiveVision.retireAge) {
        // Check for income override first (allows per-age pension customization)
        const ageOverride = effectiveVision.incomeOverrides?.[age];
        const overridePension = ageOverride?.income;

        // Pension income (if specified - e.g., teacher, military, government)
        // Use override if available, otherwise use default retirementIncome
        // This is in today's dollars from the vision, so adjust for inflation
        const basePension = overridePension !== undefined ? overridePension : (effectiveVision.retirementIncome || 0);

        if (basePension > 0) {
          const pensionAmount = basePension * inflationMultiplier;
          yearData.retirementIncome += pensionAmount;
          if (age === effectiveVision.retireAge || overridePension !== undefined) {
            console.log(`💼 Pension at age ${age}: $${Math.round(pensionAmount)} (${basePension} in today's dollars)${overridePension !== undefined ? ' [OVERRIDE]' : ''}`);
          }
        }

        // Social Security (inflation adjusted from today's dollars, with user multiplier)
        if (age >= SOCIAL_SECURITY_AGE) {
          const ssBenefit = SOCIAL_SECURITY_AMOUNT * inflationMultiplier * settings.socialSecurityMultiplier;
          yearData.retirementIncome += ssBenefit;
          if (age === SOCIAL_SECURITY_AGE) {
            console.log(`📋 SS at age ${age}: $${Math.round(ssBenefit)} (multiplier: ${settings.socialSecurityMultiplier})`);
          }
          if (effectiveVision.hasPartner) {
            yearData.retirementIncome += ssBenefit * 0.5; // Spousal benefit
          }
        }

        // Portfolio withdrawals are handled dynamically after expenses are calculated
        // based on netCashFlow deficit - no need to lock in withdrawal amount here
      }

      // One-time events (windfalls, inheritances, bonuses, major expenses)
      // Validate events: must have valid age (number >= startAge) and amount (number)
      const validEvents = (effectiveVision.oneTimeEvents || []).filter(e => {
        const hasValidAge = typeof e.age === 'number' && e.age >= startAge && e.age <= endAge;
        const hasValidAmount = typeof e.amount === 'number' && !isNaN(e.amount);
        return hasValidAge && hasValidAmount;
      });
      const eventsThisYear = validEvents.filter(e => e.age === age);
      let oneTimeIncome = 0;
      let oneTimeExpenses = 0;

      eventsThisYear.forEach(event => {
        if (event.type === 'windfall' || event.type === 'inheritance' || event.type === 'bonus') {
          oneTimeIncome += event.amount || 0;
        } else if (event.type === 'major_purchase' || event.type === 'car_purchase' ||
                   event.type === 'wedding' || event.type === 'home_renovation' || event.type === 'home_purchase') {
          // Expenses should be positive for adding to total expenses
          oneTimeExpenses += Math.abs(event.amount || 0);
        }
      });

      // Auto-add home down payment at purchase age (if will-buy and not already in oneTimeEvents)
      const homePurchaseAgeLocal = effectiveVision.homePurchaseAge || 35;
      if (effectiveVision.housingStatus === 'will-buy' && age === homePurchaseAgeLocal) {
        // ALWAYS add the down payment - don't skip if there's an existing event
        // The existing event might be for something else or have the wrong amount
        const homePrice = effectiveVision.homePrice || 450000;
        const downPayment = effectiveVision.downPayment || (homePrice * 0.20);
        oneTimeExpenses += downPayment;
        console.log(`🏠 HOME PURCHASE at age ${age}: Down payment $${downPayment.toLocaleString()} on $${homePrice.toLocaleString()} home`);
      }

      yearData.grossIncome = yearData.workIncome + yearData.retirementIncome + oneTimeIncome;

      // ============ TAXES ============

      // Ensure all precision expense fields have defaults to prevent NaN
      const pe = {
        monthlyRent: 0, monthlyMortgage: 0, propertyTax: 0, homeInsurance: 0,
        utilities: 0, homeMaintenance: 0, carPayment: 0, carInsurance: 0,
        gas: 0, publicTransit: 0, carMaintenance: 0, groceries: 0, diningOut: 0,
        entertainment: 0, shopping: 0, travel: 0, hobbies: 0, subscriptions: 0,
        gym: 0, childcare: 0, childEducation: 0, childActivities: 0, childOther: 0,
        medicalExpenses: 0, healthInsurance: 0, lifeInsurance: 0, gifts: 0,
        charity: 0, other: 0, retirement401k: 0,
        ...(effectiveVision.precisionExpenses || {})
      };
      const annual401k = (pe.retirement401k || 0) * 12;

      if (yearData.workIncome > 0) {
        const taxCalc = calculateNetIncome(yearData.grossIncome, {
          isMarried: effectiveVision.hasPartner || false,
          location: effectiveVision.location || 'medium-cost',
          retirement401kContribution: annual401k,
          inflationMultiplier: inflationMultiplier
        });

        yearData.taxes = taxCalc.totalTax;
        yearData.netIncome = taxCalc.netIncome;
      } else {
        // In retirement, lower tax burden (no FICA on SS, lower income)
        yearData.taxes = yearData.grossIncome * 0.12; // Simplified retirement tax rate
        yearData.netIncome = yearData.grossIncome - yearData.taxes;
      }

      yearData.totalIncome = yearData.netIncome;

      // Calculate after-tax breakdown for charting
      // Split taxes proportionally across income sources
      if (yearData.grossIncome > 0) {
        const taxRate = yearData.taxes / yearData.grossIncome;
        yearData.workIncomeAfterTax = yearData.workIncome * (1 - taxRate);
        yearData.retirementIncomeAfterTax = yearData.retirementIncome * (1 - taxRate);
        yearData.portfolioDistributionAfterTax = yearData.portfolioDistribution * (1 - taxRate);
      } else {
        yearData.workIncomeAfterTax = 0;
        yearData.retirementIncomeAfterTax = 0;
        yearData.portfolioDistributionAfterTax = 0;
      }

      // ============ EXPENSES ============

      // Always use precision expenses (derived from values in video game mode)

      // Housing - Handle mortgage based on housing status and timing
      let housingMortgageCost = 0;
      const homePurchaseAge = effectiveVision.homePurchaseAge || 35;
      const housingStatus = effectiveVision.housingStatus || 'will-buy';

      if (housingStatus === 'already-own') {
        // Already owns - use current mortgage payment until paid off
        if (currentMortgagePayment > 0 && age < mortgagePayoffAge) {
          housingMortgageCost = currentMortgagePayment * 12;
        }
      } else if (housingStatus === 'will-buy') {
        // Will buy at homePurchaseAge - mortgage starts then, lasts 30 years
        const mortgageEndAge = homePurchaseAge + 30;
        if (age >= homePurchaseAge && age < mortgageEndAge) {
          // Calculate mortgage payment directly from vision fields (not precisionExpenses)
          const homePrice = effectiveVision.homePrice || 400000;
          const downPayment = effectiveVision.downPayment || (homePrice * 0.20);
          const loanAmount = homePrice - downPayment;
          const rate = (effectiveVision.mortgageRate || 7) / 100;
          const monthlyRate = rate / 12;
          const numPayments = 360; // 30 years
          const monthlyMortgage = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
          // Mortgage payment is fixed (doesn't inflate)
          housingMortgageCost = monthlyMortgage * 12;
        }
        // Before home purchase, pay rent
        if (age < homePurchaseAge) {
          // Temporary rent before buying (use rent value if set, or estimate)
          const tempRent = pe.monthlyRent > 0 ? pe.monthlyRent : 1800;
          yearData.expenses += tempRent * 12 * inflationMultiplier;
        }
      }
      // renting-forever: no mortgage, handled by monthlyRent below

      yearData.expenses += (housingStatus === 'renting-forever' ? pe.monthlyRent * 12 * inflationMultiplier : 0) + housingMortgageCost;

      // Property-related expenses - only apply once home is owned
      const ownsHome = housingStatus === 'already-own' || (housingStatus === 'will-buy' && age >= homePurchaseAge);
      if (ownsHome) {
        // Calculate property tax and insurance from home price if will-buy, or use precisionExpenses
        let propertyTax = pe.propertyTax;
        let homeInsurance = pe.homeInsurance;
        if (housingStatus === 'will-buy') {
          const homePrice = effectiveVision.homePrice || 400000;
          propertyTax = Math.round(homePrice * 0.012 / 12); // ~1.2% annually
          homeInsurance = Math.round(homePrice * 0.004 / 12); // ~0.4% annually
        }
        yearData.expenses += (propertyTax + homeInsurance) * 12 * inflationMultiplier;
        yearData.expenses += (pe.utilities + pe.homeMaintenance) * 12 * inflationMultiplier;
      } else {
        // Just utilities when renting
        yearData.expenses += pe.utilities * 12 * inflationMultiplier;
      }

      // Update home equity tracking
      if (ownsHome) {
        // Home appreciates annually
        homeValue *= (1 + HOME_APPRECIATION_RATE);

        // Mortgage principal paydown (simplified - assumes equal principal reduction each year)
        if (housingStatus === 'will-buy' && age >= homePurchaseAge && age < homePurchaseAge + 30) {
          const homePrice = effectiveVision.homePrice || 400000;
          const downPayment = effectiveVision.downPayment || (homePrice * 0.20);
          const loanAmount = homePrice - downPayment;
          // First year of ownership - initialize
          if (age === homePurchaseAge) {
            homeValue = homePrice;
            mortgageBalance = loanAmount;
          }
          // Reduce mortgage balance by principal portion (roughly 1/30th per year in early years, more later)
          const yearOfMortgage = age - homePurchaseAge + 1;
          const principalPaydown = loanAmount / 30 * (1 + yearOfMortgage * 0.02); // Increases over time
          mortgageBalance = Math.max(0, mortgageBalance - principalPaydown);
        } else if (housingStatus === 'already-own' && age < mortgagePayoffAge) {
          // For existing homeowners, reduce balance proportionally
          const yearsToPayoff = mortgagePayoffAge - effectiveVision.age;
          if (yearsToPayoff > 0) {
            // eslint-disable-next-line no-unused-vars
            const yearOfMortgage = age - effectiveVision.age + 1;
            const principalPaydown = mortgageBalance / yearsToPayoff;
            mortgageBalance = Math.max(0, mortgageBalance - principalPaydown);
          }
        } else if (age >= (housingStatus === 'will-buy' ? homePurchaseAge + 30 : mortgagePayoffAge)) {
          // Mortgage paid off
          mortgageBalance = 0;
        }

        homeEquity = homeValue - mortgageBalance;
      }
      yearData.homeEquity = homeEquity;
      yearData.homeValue = homeValue;
      yearData.mortgageBalance = mortgageBalance;

      // Transportation
      yearData.expenses += (pe.carPayment + pe.carInsurance + pe.gas + pe.publicTransit + pe.carMaintenance) * 12 * inflationMultiplier;

      // Daily Living
      yearData.expenses += (pe.groceries + pe.diningOut + pe.shopping + pe.personalCare) * 12 * inflationMultiplier;

      // Healthcare (smart ACA/Medicare calculation)
      let healthcareCost = 0;
      if (age >= effectiveVision.retireAge && age < 65) {
        // EARLY RETIREMENT: ACA marketplace healthcare (before Medicare at 65)
        // Base ACA premium: ~$800-1200/month for individual, ~$1600-2400/month for family
        const baseACAMonthly = effectiveVision.hasPartner ? 1800 : 900;

        // ACA subsidies are based on income as % of Federal Poverty Level (FPL)
        // FPL 2024: ~$15k individual, ~$20k family + $5k per additional person
        const fpl = effectiveVision.hasPartner ? 20000 + (effectiveVision.kids * 5000) : 15000 + (effectiveVision.kids * 5000);
        const incomeToFPLRatio = yearData.grossIncome / fpl;

        // Subsidies: Full subsidy under 150% FPL, sliding scale 150-400% FPL, none above 400%
        let subsidyMultiplier = 1.0; // No subsidy by default
        if (incomeToFPLRatio < 1.5) {
          subsidyMultiplier = 0.1; // 90% subsidy (pay 10%)
        } else if (incomeToFPLRatio < 2.0) {
          subsidyMultiplier = 0.3; // 70% subsidy (pay 30%)
        } else if (incomeToFPLRatio < 3.0) {
          subsidyMultiplier = 0.5; // 50% subsidy (pay 50%)
        } else if (incomeToFPLRatio < 4.0) {
          subsidyMultiplier = 0.7; // 30% subsidy (pay 70%)
        }

        // Apply ACA costs with subsidy
        healthcareCost = baseACAMonthly * 12 * inflationMultiplier * subsidyMultiplier;

        // Add out-of-pocket medical expenses
        healthcareCost += pe.medicalExpenses * 12 * inflationMultiplier;

      } else if (age >= 65) {
        // MEDICARE AGE: Much more affordable
        // Medicare Part B + D + Supplemental (Medigap): ~$300-500/month
        // Out-of-pocket: ~$200-400/month depending on age
        const medicareCost = 400 * 12 * inflationMultiplier; // Base Medicare + supplements

        let ageMultiplier = 1.0;
        if (age >= 85) {
          ageMultiplier = 2.5; // Long-term care potential
        } else if (age >= 75) {
          ageMultiplier = 1.5; // Increased medical needs
        }

        healthcareCost = medicareCost * ageMultiplier;
        healthcareCost += pe.medicalExpenses * 12 * inflationMultiplier * ageMultiplier;

      } else {
        // WORKING AGE: Employer or marketplace insurance
        healthcareCost = (pe.healthInsurance + pe.medicalExpenses) * 12 * inflationMultiplier;
      }

      yearData.expenses += healthcareCost;

      // Entertainment & Lifestyle
      yearData.expenses += (pe.entertainment + pe.hobbies + pe.travel + pe.subscriptions + pe.gym) * 12 * inflationMultiplier;

      // Kids - Track individual children with age-dependent costs
      let kidsUnder18 = 0;
      let kidsInPreschool = 0; // Ages 0-4 (full childcare)
      let kidsInSchool = 0; // Ages 5-17 (reduced/no childcare, but other expenses)
      let kidsInCollege = 0;

      // Support both old format (kids/firstChildAge/childSpacing) and new format (childrenBirthAges array)
      const childrenBirthAges = effectiveVision.childrenBirthAges ||
        (effectiveVision.kids > 0
          ? Array.from({ length: effectiveVision.kids }, (_, i) =>
              effectiveVision.firstChildAge + (i * effectiveVision.childSpacing))
          : []);

      for (let i = 0; i < childrenBirthAges.length; i++) {
        const childBirthAge = childrenBirthAges[i];
        const childAge = age - childBirthAge;

        if (childAge >= 0 && childAge < 18) {
          kidsUnder18++;
          if (childAge < 5) {
            kidsInPreschool++; // Ages 0-4: Full childcare costs
          } else {
            kidsInSchool++; // Ages 5-17: Reduced childcare (after-school care, summers)
          }
        }
        if (childAge >= 18 && childAge < 22) kidsInCollege++; // College years 18-21
      }

      // Age-dependent childcare costs:
      // - Preschool (0-4): Full childcare cost
      // - School age (5-17): 30% of childcare cost (after-school care, summer camps)
      const fullChildcareCost = pe.childcare * 12 * inflationMultiplier * kidsInPreschool;
      const schoolAgeChildcareCost = (pe.childcare * 0.3) * 12 * inflationMultiplier * kidsInSchool;

      yearData.expenses += fullChildcareCost + schoolAgeChildcareCost;
      yearData.expenses += (pe.childEducation + pe.childActivities + pe.childOther) * 12 * inflationMultiplier * kidsUnder18;

      // College expenses
      if (kidsInCollege > 0 && effectiveVision.collegeSavings?.enabled) {
        const collegeCostPerYear = (effectiveVision.collegeSavings.costPerYear || 30000) * inflationMultiplier;
        const totalCollegeCost = collegeCostPerYear * kidsInCollege;

        // Apply target coverage % - only pay this % of costs
        const targetCoveragePercent = effectiveVision.collegeSavings.targetCoveragePercent !== undefined
          ? effectiveVision.collegeSavings.targetCoveragePercent
          : 100;
        const targetCollegeCost = totalCollegeCost * (targetCoveragePercent / 100);

        // Try to pay from 529 first (up to target coverage)
        const from529 = Math.min(college529Balance, targetCollegeCost);
        college529Balance -= from529;

        // Rest (up to target coverage) comes from cash flow
        yearData.expenses += (targetCollegeCost - from529);
        yearData.college529 = from529;
      }

      // 529 contributions (while working and kids not yet in college)
      if (effectiveVision.collegeSavings?.enabled && age < effectiveVision.retireAge) {
        // Calculate required contribution based on target coverage %
        const requiredMonthly = calculateRequired529Contribution({
          ...effectiveVision,
          age: effectiveVision.age // Use starting age for calculation
        });
        const annualContribution = requiredMonthly * 12;
        yearData.expenses += annualContribution;

        // Grow 529 balance (using 6% to match calculation assumption)
        college529Balance = college529Balance * 1.06 + annualContribution;
      }

      // Debt payments (tracked individually)
      activeDebts.forEach((debt, index) => {
        const debtStatus = getDebtStatusAtAge(debt, effectiveVision.age, age);
        if (!debtStatus.isPaidOff) {
          yearData.debtPayments += debtStatus.monthlyPayment * 12;
          yearData.expenses += debtStatus.monthlyPayment * 12;

          // Update debt balance
          activeDebts[index].balance = debtStatus.balance;
        }
      });

      // One-time expenses (track separately for visualization)
      yearData.oneTimeExpenses = oneTimeExpenses;
      yearData.expenses += oneTimeExpenses;

      // Other
      yearData.expenses += (pe.gifts + pe.charity + pe.petExpenses + pe.other) * 12 * inflationMultiplier;

      // Lifestyle expenses (from accepted trade-offs)
      const lifestyleExpenses = effectiveVision.lifestyleExpenses || [];
      lifestyleExpenses.forEach(lifestyle => {
        // Only add if within the age range
        if (age >= lifestyle.startAge && (lifestyle.endAge === null || age <= lifestyle.endAge)) {
          yearData.expenses += lifestyle.monthlyCost * 12 * inflationMultiplier;
        }
      });

      // Retirement adjustments
      if (age >= effectiveVision.retireAge) {
        // Remove work-related expenses
        yearData.expenses -= (pe.carPayment + pe.gas * 0.5) * 12 * inflationMultiplier; // Reduce transportation
        // Remove childcare costs (already accounted for in the age-based calculation above)
        yearData.expenses -= (fullChildcareCost + schoolAgeChildcareCost); // No childcare in retirement
      }

      // ============ CASH FLOW & PORTFOLIO ============

      // If in savings rate mode, override expenses to achieve target savings rate
      // This models "what if we cut expenses to save X%?"
      // Skip if we're just getting the baseline rate for display
      if (effectiveVision.autoCalculateMode === 'savingsRate' && effectiveVision.targetSavingsRate && age < effectiveVision.retireAge && !effectiveVision._gettingBaselineRate) {
        const targetRate = effectiveVision.targetSavingsRate / 100;
        const targetSavings = yearData.totalIncome * targetRate;
        const impliedExpenses = Math.max(0, yearData.totalIncome - targetSavings);

        console.log(`💰 Savings Rate Mode (Age ${age}): Target=${effectiveVision.targetSavingsRate}%, Income=$${Math.round(yearData.totalIncome)}, Implied Expenses=$${Math.round(impliedExpenses)} (was $${Math.round(yearData.expenses)})`);

        yearData.expenses = impliedExpenses;
      }

      // Calculate net cash flow from actual income (no portfolio withdrawals counted as income)
      yearData.netCashFlow = yearData.totalIncome - yearData.expenses;

      // Debug: Log retirement years cash flow
      if (age >= effectiveVision.retireAge && age <= effectiveVision.retireAge + 3) {
        console.log(`📊 Age ${age} (Retired): Income=$${Math.round(yearData.totalIncome)}, Expenses=$${Math.round(yearData.expenses)}, CashFlow=$${Math.round(yearData.netCashFlow)}, WorkIncome=$${Math.round(yearData.workIncome)}, RetirementIncome=$${Math.round(yearData.retirementIncome)}`);
      }

      // Track portfolio withdrawals separately (for chart display)
      // This applies anytime expenses exceed income, not just in retirement
      if (yearData.netCashFlow < 0) {
        // Deficit: withdrawal = the amount needed from portfolio to cover expenses
        yearData.portfolioDistribution = Math.abs(yearData.netCashFlow);
      } else {
        yearData.portfolioDistribution = 0;
      }

      // Investment returns on existing portfolio
      // Use more conservative returns in retirement to account for:
      // - Lower risk tolerance (more bonds, less stocks)
      // - Sequence of returns risk
      // - Market volatility over long timeframes
      let effectiveReturn = NOMINAL_RETURN;

      if (age >= effectiveVision.retireAge) {
        // In retirement, use more conservative assumptions
        const yearsRetired = age - effectiveVision.retireAge;

        // Gradually reduce returns as you age (shift to more conservative allocation)
        if (yearsRetired < 10) {
          effectiveReturn = NOMINAL_RETURN * 0.85; // 85% of normal (more conservative mix)
        } else if (yearsRetired < 20) {
          effectiveReturn = NOMINAL_RETURN * 0.75; // 75% (even more bonds)
        } else {
          effectiveReturn = NOMINAL_RETURN * 0.65; // 65% (very conservative for longevity)
        }
      }

      // For the FIRST year (current age), don't apply growth - just show current assets
      if (age === startAge) {
        yearData.portfolioGrowth = 0;
        portfolio = portfolio + yearData.netCashFlow; // Just add/subtract cash flow
        yearData.portfolioValue = portfolio;
      } else {
        // For subsequent years, apply growth then add cash flow
        yearData.portfolioGrowth = portfolio * effectiveReturn;
        portfolio = (portfolio * (1 + effectiveReturn)) + yearData.netCashFlow;

        // Portfolio can't go negative (bankruptcy scenario)
        if (portfolio < 0) portfolio = 0;

        yearData.portfolioValue = portfolio;
      }

      // ============ SAVINGS RATE & STRESS CALCULATION ============

      if (age < effectiveVision.retireAge) {
        // Working years: stress based on savings rate and debt-to-income
        const savingsRate = yearData.totalIncome > 0 ? yearData.netCashFlow / yearData.totalIncome : -1;
        const debtToIncome = yearData.totalIncome > 0 ? yearData.debtPayments / yearData.totalIncome : 0;

        // DEBUG: Log savings rate calculation for current age
        if (age === effectiveVision.age) {
          console.log(`💰 Savings Rate Calculation (Age ${age}):`);
          console.log(`  Work Income (you): $${(yearData.workIncome - (yearData.partnerIncome || 0)).toFixed(0)}`);
          console.log(`  Partner Income: $${(yearData.partnerIncome || 0).toFixed(0)}`);
          console.log(`  Gross Income: $${yearData.grossIncome.toFixed(0)}`);
          console.log(`  Taxes: $${yearData.taxes.toFixed(0)}`);
          console.log(`  Net Income (after-tax): $${yearData.netIncome.toFixed(0)}`);
          console.log(`  Total Income Used: $${yearData.totalIncome.toFixed(0)}`);
          console.log(`  Expenses: $${yearData.expenses.toFixed(0)}`);
          console.log(`  Net Cash Flow: $${yearData.netCashFlow.toFixed(0)}`);
          console.log(`  Savings Rate: ${(savingsRate * 100).toFixed(1)}% = $${yearData.netCashFlow.toFixed(0)} / $${yearData.totalIncome.toFixed(0)}`);
          if (effectiveVision.hasPartner) {
            console.log(`  👥 Partner: hasPartner=${effectiveVision.hasPartner}, income=$${effectiveVision.partnerIncomeStart}, startsIn=${effectiveVision.partnerIncomeStartsInYears}, retireAge=${effectiveVision.partnerRetireAge}`);
          }
        }

        // Store savings rate for charting
        yearData.savingsRate = savingsRate * 100; // Convert to percentage

        let baseStress = 0;
        if (savingsRate < 0) baseStress = 100;
        else if (savingsRate < 0.05) baseStress = 85;
        else if (savingsRate < 0.10) baseStress = 70;
        else if (savingsRate < 0.15) baseStress = 50;
        else if (savingsRate < 0.20) baseStress = 30;
        else baseStress = 10;

        // Adjust for debt burden
        let debtStress = 0;
        if (debtToIncome > 0.43) debtStress = 40; // High debt-to-income
        else if (debtToIncome > 0.36) debtStress = 30;
        else if (debtToIncome > 0.28) debtStress = 20;
        else if (debtToIncome > 0.20) debtStress = 10;

        yearData.stress = Math.min(100, baseStress + debtStress);
      } else {
        // Retirement years: no traditional savings rate
        yearData.savingsRate = null;
        // Retirement years: stress based on portfolio health
        const yearsOfExpensesRemaining = yearData.expenses > 0 ? portfolio / yearData.expenses : 999;
        if (portfolio <= 0) yearData.stress = 100;
        else if (yearsOfExpensesRemaining < 5) yearData.stress = 95;
        else if (yearsOfExpensesRemaining < 10) yearData.stress = 80;
        else if (yearsOfExpensesRemaining < 15) yearData.stress = 60;
        else if (yearsOfExpensesRemaining < 25) yearData.stress = 30;
        else yearData.stress = 10;
      }

      years.push(yearData);
    }

    return years;
  };

  // Helper function to calculate dynamic withdrawal rate
  // Simplified: 4% for retirement at 55+, lower rates only for very early retirement
  const calculateWithdrawalRate = (originalCurrentAge, retireAge) => {
    // Standard 4% rule applies for retirement at age 55 or later
    if (retireAge >= 55) {
      return 0.04; // 4% - the classic safe withdrawal rate (need 25x expenses)
    }
    // Early retirement (before 55) needs more conservative rates
    else if (retireAge >= 45) {
      return 0.035; // 3.5% for 45-54 retirement (need 28.6x expenses)
    } else {
      return 0.03; // 3% for very early retirement under 45 (need 33.3x expenses)
    }
  };

  // Calculate portfolio value at target age using FULL calculateFinancials logic
  const projectPortfolioGrowth = (vision, targetAge) => {
    // CRITICAL: When testing retirement viability at targetAge, we need to know the portfolio
    // value BEFORE retirement (at the target age), so we should assume they KEEP WORKING until targetAge
    // Otherwise the calculation assumes they retire at vision.retireAge which breaks the binary search
    const visionWorkingUntilTarget = { ...vision, retireAge: targetAge };

    // Use the SAME comprehensive calculation as charts and Monte Carlo
    // This ensures ALL calculations use identical income/expense modeling
    const fullProjection = calculateFinancials(visionWorkingUntilTarget);

    // Find the year data for the target age
    const targetYearData = fullProjection.find(yearData => yearData.age === targetAge);

    if (!targetYearData) {
      console.error(`⚠️ Target age ${targetAge} not found in projection (range: ${fullProjection[0]?.age} to ${fullProjection[fullProjection.length - 1]?.age})`);
      return 0;
    }

    // DEBUG: Log what we found
    console.log(`📊 projectPortfolioGrowth for age ${targetAge}:`, {
      age: targetYearData.age,
      portfolioValue: targetYearData.portfolioValue,
      netCashFlow: targetYearData.netCashFlow,
      workIncome: targetYearData.workIncome,
      expenses: targetYearData.expenses
    });

    return targetYearData.portfolioValue || 0;
  };

  // OLD SIMPLIFIED VERSION - REPLACED ABOVE
  const projectPortfolioGrowth_OLD_SIMPLIFIED = (vision, targetAge) => {
    const currentAge = vision.age;
    const years = targetAge - currentAge;

    let portfolio = (vision.current401k || 0) + (vision.currentInvestments || 0) +
                    (vision.currentCash || 0) + (vision.currentOtherAssets || 0);

    // Get settings
    const settings = vision.financialSettings || {};
    const annualReturn = settings.investmentReturn || 0.08;
    const inflationRate = settings.inflationRate || 0.03;
    const realReturn = (1 + annualReturn) / (1 + inflationRate) - 1;

    // Project forward year by year
    for (let year = 0; year < years; year++) {
      const ageThisYear = currentAge + year;

      // Calculate YOUR income for this year
      let userIncome = 0;
      if (ageThisYear < vision.retireAge) {
        userIncome = (vision.currentIncome || 0) * Math.pow(1 + (vision.incomeGrowth || 0.03), year);
      }

      // Calculate PARTNER income for this year
      let partnerIncome = 0;
      if (vision.hasPartner && vision.partnerIncomeStart > 0) {
        const partnerCurrentAge = vision.partnerAge || vision.age;
        const partnerAgeThisYear = partnerCurrentAge + year;
        const partnerStartDelay = vision.partnerIncomeStartsInYears || 0;
        const partnerStartAge = partnerCurrentAge + partnerStartDelay;
        const partnerRetireAge = vision.partnerRetireAge || vision.retireAge;

        // DEBUG: Log partner income calculation for first year
        if (year === 0) {
          console.log('👥 PARTNER INCOME in projectPortfolioGrowth (Year 0):');
          console.log('  Partner current age:', partnerCurrentAge);
          console.log('  Partner retire age:', partnerRetireAge);
          console.log('  Partner age this year:', partnerAgeThisYear);
          console.log('  Will partner work? ' + partnerAgeThisYear + ' >= ' + partnerStartAge + ' && ' + partnerAgeThisYear + ' < ' + partnerRetireAge + ' = ' + (partnerAgeThisYear >= partnerStartAge && partnerAgeThisYear < partnerRetireAge));
        }

        // Partner works if they've started and haven't retired yet
        if (partnerAgeThisYear >= partnerStartAge && partnerAgeThisYear < partnerRetireAge) {
          const partnerYearsWorking = Math.max(0, year - partnerStartDelay);
          partnerIncome = vision.partnerIncomeStart * Math.pow(1 + (vision.incomeGrowth || 0.03), partnerYearsWorking);
        } else if (year === 0 || (year > 0 && ageThisYear - 1 < partnerRetireAge && ageThisYear >= partnerRetireAge)) {
          // Log when partner retires
          if (partnerAgeThisYear >= partnerRetireAge) {
            console.log(`👋 Partner retired at partner age ${partnerAgeThisYear} (your age ${ageThisYear})`);
          }
        }
      }

      const income = userIncome + partnerIncome;

      // Calculate actual expenses from precisionExpenses
      // IMPORTANT: Only sum flat numeric values, NOT nested objects (Housing, Transportation, etc.)
      const pe = vision.precisionExpenses || {};
      let monthlyExpenses = Object.entries(pe).reduce((sum, [key, val]) => {
        // Skip nested objects (they're just organizational, not real expenses)
        if (typeof val === 'object' && val !== null) {
          return sum;
        }
        const numVal = parseFloat(val) || 0;
        return sum + (isNaN(numVal) ? 0 : numVal);
      }, 0);

      // Include lifestyle expenses (income boosts and expense reductions)
      const lifestyleExpenses = vision.lifestyleExpenses || [];
      lifestyleExpenses.forEach(lifestyle => {
        if (ageThisYear >= lifestyle.startAge && (lifestyle.endAge === null || ageThisYear <= lifestyle.endAge)) {
          // monthlyCost is net (already includes income and savings reductions)
          monthlyExpenses += lifestyle.monthlyCost;
        }
      });

      const annualExpenses = monthlyExpenses * 12 * Math.pow(1 + inflationRate, year);

      // DEBUG: Log first year only
      if (year === 0) {
        const lifestyleAdjustment = lifestyleExpenses.reduce((sum, ls) => {
          if (ageThisYear >= ls.startAge && (ls.endAge === null || ageThisYear <= ls.endAge)) {
            return sum + ls.monthlyCost;
          }
          return sum;
        }, 0);
        console.log('💰 PORTFOLIO PROJECTION (Year 0):');
        console.log('  Your income:', Math.round(userIncome));
        console.log('  Partner income:', Math.round(partnerIncome));
        console.log('  Total income:', Math.round(income));
        console.log('  Base monthly expenses:', Math.round(monthlyExpenses - lifestyleAdjustment));
        console.log('  Lifestyle adjustments:', Math.round(lifestyleAdjustment), '(' + lifestyleExpenses.length + ' active)');
        console.log('  Total monthly expenses:', Math.round(monthlyExpenses));
        console.log('  Annual expenses:', Math.round(annualExpenses));
        console.log('  Starting portfolio:', Math.round(portfolio));
        console.log('⚠️ WARNING: Simplified projection does NOT include:');
        console.log('  - Childcare costs changing as kids age');
        console.log('  - Mortgage payments ending');
        console.log('  - One-time events');
        console.log('  - Partner income STOPPING at partner retire age (see year-by-year projection)');

        if (isNaN(income) || isNaN(annualExpenses) || isNaN(portfolio)) {
          console.log('💥 NaN DETECTED in projectPortfolioGrowth:');
          console.log('  vision.currentIncome:', vision.currentIncome);
          console.log('  income:', income);
          console.log('  monthlyExpenses:', monthlyExpenses);
          console.log('  annualExpenses:', annualExpenses);
          console.log('  portfolio:', portfolio);
          console.log('  precisionExpenses sample:', Object.entries(pe).slice(0, 5));
        }
      }

      // Annual savings = income - expenses - taxes (simplified 25% effective rate)
      const taxes = income * 0.25;
      const annualSavings = Math.max(0, income - annualExpenses - taxes);

      // Grow portfolio
      portfolio = portfolio * (1 + realReturn) + annualSavings;
    }

    return portfolio;
  };

  // Helper: Check if retirement is viable at a given age
  const canRetireAtAge = (vision, testAge) => {
    const portfolioAtAge = projectPortfolioGrowth(vision, testAge);

    // Use custom withdrawal rate if user set one, otherwise calculate dynamically
    const safeWithdrawalRate = vision.financialSettings?.withdrawalRate
      ? vision.financialSettings.withdrawalRate
      : calculateWithdrawalRate(vision.age, testAge);

    // CRITICAL: Calculate expenses AS IF we retire at testAge
    // This ensures consistent required portfolio across all age tests
    const testVision = { ...vision, retireAge: testAge };
    const fullProjection = calculateFinancials(testVision);
    const retirementYearData = fullProjection.find(yearData => yearData.age === testAge);

    if (!retirementYearData) {
      console.error(`⚠️ Retirement age ${testAge} not found in projection`);
      return { viable: false, portfolio: 0, required: 0 };
    }

    // Use actual projected expenses at retirement age
    // IMPORTANT: Subtract retirement income (pension, etc.) since it covers expenses
    // But ONLY if pension is actually available at this age!
    // Don't count pension if retiring before the planned retirement age
    const annualExpenses = retirementYearData.expenses || 0;
    const annualRetirementIncome = retirementYearData.retirementIncome || 0;

    // CRITICAL: If user has a pension (retirementIncome set) but we're testing early retirement,
    // the pension won't be available yet! Only count it if testAge >= original planned retireAge
    const pensionAvailable = !vision.retirementIncome || testAge >= vision.retireAge;
    const effectiveRetirementIncome = pensionAvailable ? annualRetirementIncome : 0;

    let expenseGap = Math.max(0, annualExpenses - effectiveRetirementIncome);

    // SAVINGS RATE MODE: Adjust retirement expenses based on implied lifestyle
    // If saving 40%, they're living on 60% of income during working years
    // In retirement, they only need to support that 60% lifestyle, not 100%
    if (vision.targetSavingsRate && vision.autoCalculateMode === 'savingsRate') {
      const targetRate = vision.targetSavingsRate / 100;
      const impliedExpenseRatio = 1 - targetRate; // If saving 40%, living on 60%

      // Reduce retirement expenses to match the lifestyle they're actually living
      const adjustedExpenseGap = expenseGap * impliedExpenseRatio;

      console.log(`💰 Savings Rate Mode: ${vision.targetSavingsRate}% savings → living on ${(impliedExpenseRatio * 100).toFixed(0)}% of income`);
      console.log(`   Original retirement gap: $${Math.round(expenseGap/1000)}k → Adjusted: $${Math.round(adjustedExpenseGap/1000)}k`);

      expenseGap = adjustedExpenseGap;
    }

    if (effectiveRetirementIncome > 0) {
      console.log(`💼 Retirement income at age ${testAge}: $${Math.round(effectiveRetirementIncome/1000)}k covers expenses, gap: $${Math.round(expenseGap/1000)}k`);
    }
    if (!pensionAvailable && annualRetirementIncome > 0) {
      console.log(`⚠️ Pension NOT available at age ${testAge} (starts at ${vision.retireAge}) - not counting toward retirement viability`);
    }

    const requiredPortfolio = (expenseGap / safeWithdrawalRate) * 1.1;

    return {
      viable: portfolioAtAge >= requiredPortfolio,
      portfolio: portfolioAtAge,
      required: requiredPortfolio
    };
  };

  // Calculate optimal retirement age using BINARY SEARCH - O(log n) instead of O(n)
  const calculateOptimalRetirementAge = (vision) => {
    console.log(`\n🔍 Binary search for optimal retirement age starting from ${vision.age}...`);

    let low = vision.age;
    let high = 75;
    let earliestViableAge = null;

    // Binary search to find earliest viable retirement age
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const result = canRetireAtAge(vision, mid);

      console.log(`Testing age ${mid}: Portfolio $${Math.round(result.portfolio/1000)}k, Required $${Math.round(result.required/1000)}k - ${result.viable ? '✓ VIABLE' : '✗ Not yet'}`);

      if (result.viable) {
        // Can retire at this age - try earlier
        earliestViableAge = mid;
        high = mid - 1;
      } else {
        // Can't retire yet - try later
        low = mid + 1;
      }
    }

    if (earliestViableAge === null) {
      console.log('❌ Retirement not possible by age 75');
      return null;
    }

    console.log(`✅ Found earliest retirement age: ${earliestViableAge}`);

    // NOW calculate the EXACT DAY within that year when retirement becomes viable
    // First, check if we can retire during the PREVIOUS year
    const previousYearAge = earliestViableAge - 1;

    // Check if we can retire DURING the previous year
    // This handles cases where withdrawal rate or expenses change at age boundary
    if (previousYearAge >= vision.age) {
      const prevResult = canRetireAtAge(vision, previousYearAge);
      const viableResult = canRetireAtAge(vision, earliestViableAge);

      // Portfolio growth during the previous year (age 44 -> age 45)
      const portfolioAtStartOfYear = prevResult.portfolio;
      const portfolioAtEndOfYear = viableResult.portfolio;
      const yearlyGrowth = portfolioAtEndOfYear - portfolioAtStartOfYear;
      const dailyGrowth = yearlyGrowth / 365;

      // Calculate when we can retire by checking the age 44 requirement
      // We might reach it during age 44 OR during early age 45 (before requirements change)
      console.log(`📊 Fractional age check:`);
      console.log(`  Portfolio at start of age ${previousYearAge}: $${Math.round(portfolioAtStartOfYear/1000)}k`);
      console.log(`  Portfolio at start of age ${earliestViableAge}: $${Math.round(portfolioAtEndOfYear/1000)}k`);
      console.log(`  Required (age ${previousYearAge} rules): $${Math.round(prevResult.required/1000)}k`);
      console.log(`  Daily growth: $${Math.round(dailyGrowth)}`);

      if (portfolioAtStartOfYear < prevResult.required) {
        // We don't meet requirement at start, calculate when we will
        const daysNeeded = Math.ceil((prevResult.required - portfolioAtStartOfYear) / dailyGrowth);
        console.log(`  Need ${daysNeeded} days to reach $${Math.round(prevResult.required/1000)}k`);

        if (daysNeeded >= 1 && daysNeeded <= 365) {
          // We reach it during age 44
          const fractionalAge = Math.round((previousYearAge + daysNeeded / 365) * 100) / 100;
          console.log(`✅ Can retire during age ${previousYearAge} at day ${daysNeeded}: age ${fractionalAge}`);
          return fractionalAge;
        } else if (daysNeeded > 365) {
          // We reach it during age 45 (before requirements change at birthday)
          // Days into age 45 when we hit the threshold
          const daysIntoNextYear = daysNeeded - 365;
          const fractionalAge = Math.round((earliestViableAge + daysIntoNextYear / 365) * 100) / 100;
          console.log(`✅ Can retire during age ${earliestViableAge} at day ${daysIntoNextYear}: age ${fractionalAge}`);
          return fractionalAge;
        }
      } else {
        console.log(`✅ Already meet requirement at start of age ${previousYearAge}`);
        return previousYearAge;
      }
    }

    // If not viable in previous year, find exact day in viable year
    const viableYearAge = earliestViableAge;
    const viableResult = canRetireAtAge(vision, viableYearAge);
    const nextYearResult = canRetireAtAge(vision, viableYearAge + 1);

    // Portfolio values - we're looking at growth DURING the viable year
    const portfolioAtStartOfViableYear = viableResult.portfolio;
    const portfolioAtEndOfViableYear = nextYearResult.portfolio;
    const requiredPortfolio = viableResult.required;

    // Daily growth during the viable year
    const yearlyGrowth = portfolioAtEndOfViableYear - portfolioAtStartOfViableYear;
    const dailyGrowth = yearlyGrowth / 365;

    // Check if already viable at start of year
    if (portfolioAtStartOfViableYear >= requiredPortfolio) {
      return viableYearAge;
    }

    // Calculate exact day when portfolio crosses threshold
    const daysNeeded = Math.ceil((requiredPortfolio - portfolioAtStartOfViableYear) / dailyGrowth);

    if (daysNeeded >= 1 && daysNeeded <= 365) {
      const fractionalAge = viableYearAge + (daysNeeded / 365);
      return Math.round(fractionalAge * 100) / 100;
    }

    // Fallback
    return viableYearAge + 1;
  };

  // THREE-LEVER AUTO-CALCULATION SYSTEM
  // Calculate required income to retire at target age WITH CURRENT EXPENSES
  // This finds the income needed to maintain current lifestyle and retire at target age
  const calculateRequiredIncome = (vision) => {
    console.log(`\n💰 Calculating required income to retire at age ${vision.retireAge} (with current expenses)...`);

    // First, calculate what retirement age we can achieve with CURRENT income
    // This gives us the baseline/reference point
    const currentRetireAge = calculateOptimalRetirementAge({ ...vision, _calculatingRetireAge: true });

    console.log(`  With current income ($${Math.round(vision.currentIncome/1000)}k), you can retire at age ${currentRetireAge}`);
    console.log(`  Target retirement age: ${vision.retireAge}`);

    // If current retirement age matches target, return current income
    if (Math.abs(currentRetireAge - vision.retireAge) < 0.5) {
      console.log(`✅ Current income of $${Math.round(vision.currentIncome/1000)}k allows retirement at target age ${vision.retireAge}`);
      return vision.currentIncome;
    }

    // Otherwise, we need to find the income that gives us the target retirement age
    // Use binary search to find income where retirement age = target
    let low = 10000;
    let high = 2000000;
    let targetIncome = null;

    for (let attempt = 0; attempt < 30; attempt++) {
      const testIncome = Math.floor((low + high) / 2);
      const testVision = { ...vision, currentIncome: testIncome };

      const testRetireAge = calculateOptimalRetirementAge({ ...testVision, _calculatingRetireAge: true });

      console.log(`  Test $${Math.round(testIncome/1000)}k: Can retire at age ${testRetireAge.toFixed(1)}, target ${vision.retireAge}`);

      // If this income allows retiring at or before target age, it's viable
      if (testRetireAge <= vision.retireAge + 0.1) {
        // Can retire at target age or earlier - try lower income
        targetIncome = testIncome;
        high = testIncome - 1;
      } else {
        // Need higher income to retire earlier
        low = testIncome + 1;
      }

      if (high < low) break;

      // Close enough
      if (Math.abs(testRetireAge - vision.retireAge) < 0.1) {
        targetIncome = testIncome;
        break;
      }
    }

    if (targetIncome === null) {
      console.log(`❌ Could not find income that allows retirement at ${vision.retireAge}`);
      return null;
    }

    console.log(`✅ Required income: $${Math.round(targetIncome/1000)}k to retire at age ${vision.retireAge}`);
    return targetIncome;
  };

  // Calculate MINIMUM NECESSARY savings rate to retire at target age
  // Uses year-by-year income projection with compound interest
  const calculateRequiredSavingsRate = (vision) => {
    console.log(`\n📊 Calculating MINIMUM NECESSARY HH savings rate to retire at ${vision.retireAge}...`);

    // Use ceiling of retirement age for calculations
    // Fractional ages like 44.67 mean "can retire 67% through age 44"
    // Portfolio projections use end-of-year values, so age 44 = end of age 44 = start of age 45
    // To check if we can retire at 44.67, we look at the portfolio at start of age 45
    // Use Math.ceil to round up to the next age boundary
    const retireAgeWhole = Math.ceil(vision.retireAge);

    // Get baseline projection using whole number retirement age
    const baseProjection = calculateFinancials({ ...vision, retireAge: retireAgeWhole, _calculatingSavingsRate: true });
    const workingYears = baseProjection.filter(y => y.age >= vision.age && y.age < retireAgeWhole);

    if (workingYears.length === 0) {
      console.log('❌ No working years');
      return null;
    }

    // Use the same viability check that retirement age calculation uses
    // This properly accounts for partner income during transition years
    const viabilityCheck = canRetireAtAge(vision, retireAgeWhole);
    const requiredPortfolio = viabilityCheck.required;
    const projectedPortfolio = viabilityCheck.portfolio;

    console.log(`  Viability check: required=$${Math.round(requiredPortfolio/1000)}k, projected=$${Math.round(projectedPortfolio/1000)}k`);

    // Use FIRST year's savings rate (current year) instead of average
    // This matches what's shown in the UI and what user expects
    const currentYearData = workingYears.find(y => y.age === vision.age);
    const currentSavingsRate = currentYearData?.savingsRate || 0;

    // Use the projected portfolio from viability check as baseline
    // This accounts for partner income transitions properly
    if (projectedPortfolio >= requiredPortfolio) {
      console.log(`✅ Already viable! Projected $${Math.round(projectedPortfolio/1000)}k >= Required $${Math.round(requiredPortfolio/1000)}k`);
      console.log(`   Current savings rate: ${currentSavingsRate.toFixed(1)}% is sufficient`);

      // If this calculation was triggered by Retirement Age auto-calc mode,
      // the required rate should equal current rate (since retirement age was calculated from current rate)
      const isFromRetirementAgeCalc = vision._fromRetirementAgeCalc === true;

      const minimumRate = isFromRetirementAgeCalc
        ? currentSavingsRate  // In Retirement Age mode, required = current
        : (requiredPortfolio / projectedPortfolio) * currentSavingsRate; // Otherwise scale

      console.log(`   Minimum rate needed: ${minimumRate.toFixed(1)}% (${isFromRetirementAgeCalc ? 'matches current for auto-calc mode' : 'scaled from current'})`);

      return {
        required: Math.max(0, Math.min(100, minimumRate)),
        current: currentSavingsRate,
        onTrack: true
      };
    }

    console.log(`  Required portfolio at ${vision.retireAge}: $${Math.round(requiredPortfolio/1000)}k`);
    console.log(`  Starting portfolio: $${Math.round((vision.currentPortfolio || 0)/1000)}k`);
    console.log(`  Current savings rate (from expenses): ${currentSavingsRate.toFixed(1)}%`);

    // Calculate year-by-year compound sum
    // For X% savings rate, final portfolio = startingPortfolio×(1+r)^n + Σ(income_i × X% × (1+r)^(n-i))
    const r = 0.07; // Investment return
    const startingPortfolio = vision.currentPortfolio || 0;
    const yearsToRetire = retireAgeWhole - vision.age;

    // Calculate portfolio we'd get with CURRENT expenses maintained
    // Then see if that's enough
    let currentSavingsCompounded = 0;

    workingYears.forEach((year, index) => {
      const yearsUntilRetirement = yearsToRetire - index;
      const compoundFactor = Math.pow(1 + r, yearsUntilRetirement);

      // With current expenses, how much do we save this year?
      const yearSavings = year.netCashFlow || 0;
      currentSavingsCompounded += yearSavings * compoundFactor;

      console.log(`  Year ${year.age}: income=$${Math.round(year.totalIncome/1000)}k, expenses=$${Math.round(year.expenses/1000)}k, saves=$${Math.round(yearSavings/1000)}k, grows ${yearsUntilRetirement}yrs`);
    });

    // Portfolio at retirement from existing savings growing
    const portfolioFromExisting = startingPortfolio * Math.pow(1 + r, yearsToRetire);

    // Total portfolio with current savings pattern
    const totalPortfolioWithCurrentPlan = portfolioFromExisting + currentSavingsCompounded;

    console.log(`  Portfolio from existing $${Math.round(startingPortfolio/1000)}k growing: $${Math.round(portfolioFromExisting/1000)}k`);
    console.log(`  Portfolio from current savings: $${Math.round(currentSavingsCompounded/1000)}k`);
    console.log(`  Total portfolio with current plan: $${Math.round(totalPortfolioWithCurrentPlan/1000)}k`);
    console.log(`  Required portfolio: $${Math.round(requiredPortfolio/1000)}k`);

    // Calculate minimum savings rate algebraically
    // We need: portfolioFromExisting + Σ(income_i × X% × (1+r)^(n-i)) = requiredPortfolio
    // Solve for X:
    // Σ(income_i × X% × (1+r)^(n-i)) = requiredPortfolio - portfolioFromExisting
    // X% × Σ(income_i × (1+r)^(n-i)) = requiredPortfolio - portfolioFromExisting
    // X% = (requiredPortfolio - portfolioFromExisting) / Σ(income_i × (1+r)^(n-i))

    const portfolioNeededFromSavings = requiredPortfolio - portfolioFromExisting;

    // Calculate the total compounded income factor
    // This is how much $1 saved each year will compound to
    let totalCompoundedIncomeFactor = 0;
    workingYears.forEach((year, index) => {
      const yearsUntilRetirement = yearsToRetire - index;
      const compoundFactor = Math.pow(1 + r, yearsUntilRetirement);
      totalCompoundedIncomeFactor += year.totalIncome * compoundFactor;
    });

    // The minimum savings rate is the portfolio needed divided by the total compounded income
    const minimumSavingsRate = totalCompoundedIncomeFactor > 0
      ? (portfolioNeededFromSavings / totalCompoundedIncomeFactor) * 100
      : 100;

    console.log(`  Portfolio needed from savings: $${Math.round(portfolioNeededFromSavings/1000)}k`);
    console.log(`  Total compounded income factor: $${Math.round(totalCompoundedIncomeFactor/1000)}k`);
    console.log(`  Minimum rate calculation: ${Math.round(portfolioNeededFromSavings/1000)}k / ${Math.round(totalCompoundedIncomeFactor/1000)}k = ${minimumSavingsRate.toFixed(1)}%`);

    console.log(`✅ MINIMUM NECESSARY HH savings rate: ${minimumSavingsRate.toFixed(1)}%`);
    console.log(`   Current: ${currentSavingsRate.toFixed(1)}%`);
    if (currentSavingsRate >= minimumSavingsRate) {
      const buffer = currentSavingsRate - minimumSavingsRate;
      console.log(`   ✓ EXCEEDS minimum by ${buffer.toFixed(1)}% — you're on track!`);
    } else {
      const shortfall = minimumSavingsRate - currentSavingsRate;
      console.log(`   ✗ SHORT by ${shortfall.toFixed(1)}%`);
    }

    // If this calculation was triggered by Retirement Age or Income Required auto-calc mode,
    // the required rate should equal current rate (since retirement age was calculated from current rate)
    const isFromRetirementAgeCalc = vision._fromRetirementAgeCalc === true;
    const finalRequiredRate = isFromRetirementAgeCalc ? currentSavingsRate : minimumSavingsRate;

    return {
      required: Math.max(0, Math.min(100, finalRequiredRate)),
      current: currentSavingsRate,
      onTrack: currentSavingsRate >= minimumSavingsRate
    };
  };

  const getVisionSummary = (vision) => {
    const data = calculateFinancials(vision);
    const workingYears = data.filter(y => y.age < vision.retireAge);
    const retirementYears = data.filter(y => y.age >= vision.retireAge);

    const avgSavings = workingYears.length > 0
      ? workingYears.reduce((sum, y) => sum + y.netCashFlow, 0) / workingYears.length
      : 0;
    const monthlySavings = avgSavings / 12;

    // Calculate average savings rate
    const avgSavingsRate = workingYears.length > 0
      ? workingYears.reduce((sum, y) => sum + (y.savingsRate || 0), 0) / workingYears.length
      : 0;

    // Calculate lifetime taxes
    const lifetimeTaxes = data.reduce((sum, y) => sum + (y.taxes || 0), 0);
    const avgTaxRate = workingYears.length > 0
      ? workingYears.reduce((sum, y) => sum + (y.taxes / (y.grossIncome || 1)), 0) / workingYears.length
      : 0;

    // Calculate total debt paid
    const totalDebtPaid = data.reduce((sum, y) => sum + (y.debtPayments || 0), 0);

    const peakStress = Math.max(...data.map(y => y.stress));

    const retirementPortfolio = data.find(y => y.age === vision.retireAge)?.portfolioValue || 0;
    const finalPortfolio = data[data.length - 1]?.portfolioValue || 0;

    // Check if portfolio runs out before death
    const portfolioDepletedAge = retirementYears.find(y => y.portfolioValue <= 0)?.age;
    const portfolioLastsUntilDeath = !portfolioDepletedAge || portfolioDepletedAge >= data[data.length - 1].age;

    return {
      monthlySavings,
      avgSavingsRate,
      peakStress,
      retirementPortfolio,
      finalPortfolio,
      portfolioLastsUntilDeath,
      portfolioDepletedAge,
      lifetimeTaxes,
      avgTaxRate,
      totalDebtPaid,
      feasibility: portfolioLastsUntilDeath && retirementPortfolio > 1000000 ? 'high'
        : portfolioLastsUntilDeath && retirementPortfolio > 500000 ? 'moderate'
        : 'challenging'
    };
  };

  const updateVision = (id, updates) => {
    console.log('📝 updateVision called:', { id, updates });
    setVisions(prevVisions => prevVisions.map(v => {
      if (v.id === id) {
        const updated = { ...v, ...updates, lastModified: Date.now() };
        console.log('📝 Vision updated:', updated);
        return updated;
      }
      return v;
    }));
  };

  const deleteVision = (id) => {
    setVisions(prevVisions => prevVisions.filter(v => v.id !== id));
    if (activeVision === id) setActiveVision(null);
  };

  const duplicateVision = (vision) => {
    const newVision = {
      ...vision,
      id: Date.now(),
      name: `${vision.name} (Copy)`,
      createdAt: Date.now(),
      lastModified: Date.now()
    };
    setVisions(prevVisions => [...prevVisions, newVision]);
    setActiveVision(newVision.id);
  };

  // Load a preset from the preset explorer
  const loadPreset = (presetId) => {
    // Map preset IDs to sample vision array indices
    const presetMap = {
      'fire-45': 0,
      'coast-fire': 1,
      'dink': 2,
      'teacher': 3,
      'military': 4,
      'mid-career': 7  // Mid-Career Professional is at index 7
    };

    const index = presetMap[presetId];
    if (index !== undefined && sampleVisions[index]) {
      const preset = sampleVisions[index];
      const newVision = {
        id: Date.now(),
        createdAt: Date.now(),
        lastModified: Date.now(),
        // Map old field names to new ones
        name: preset.name,
        age: preset.currentAge,
        currentIncome: preset.currentSalary,
        retireAge: preset.retireAge,
        autoCalculateRetireAge: preset.autoCalculateRetireAge || false,
        // Add full structure from createSampleVisions
        housingStatus: preset.housing.type === 'own' ? 'already-own' : 'renting-forever',
        homePurchaseAge: preset.housing.type === 'own' ? preset.currentAge : 35,
        currentMortgagePayment: preset.housing.monthlyPayment,
        yearsLeftOnMortgage: preset.housing.type === 'own' ? preset.housing.endYear : 0,
        hasPartner: preset.currentSalary > 150000,
        partnerAge: preset.currentAge + 1,
        partnerIncomeStart: 0,
        partnerIncomeStartsInYears: 0,
        partnerRetireAge: preset.retireAge,
        kids: preset.kids?.length || 0,
        childrenBirthAges: [],
        firstChildAge: 32,
        childSpacing: 3,
        incomeGrowth: (preset.salaryGrowthRate || 3) / 100,
        location: 'medium-cost',
        homePrice: preset.housing.propertyValue || 400000,
        downPayment: preset.housing.downPayment || 80000,
        mortgageRate: preset.housing.mortgageRate || 7,
        currentCash: 0,
        current401k: preset.currentPortfolio || 0,
        currentInvestments: 0,
        currentHomeEquity: 0,
        currentOtherAssets: 0,
        debts: [],
        oneTimeEvents: [],
        careerTimeline: [],
        incomeOverrides: {},
        collegeSavings: {
          enabled: false,
          monthlyContribution: 0,
          currentBalance: 0,
          costPerYear: 30000,
          targetCoveragePercent: 100
        },
        financialSettings: undefined,
        lifestyleExpenses: [],
        lifestyleChoices: [],
        precisionExpenses: {
          // Housing expenses - EXPLICIT from preset data
          monthlyRent: preset.housing.type === 'rent' ? preset.housing.monthlyPayment : 0,
          monthlyMortgage: preset.housing.type === 'own' ? preset.housing.monthlyPayment : 0,
          propertyTax: preset.housing.type === 'own' ? Math.round((preset.housing.propertyValue * (preset.housing.propertyTaxRate / 100) / 12)) : 0,
          homeInsurance: preset.housing.insuranceMonthly || 0,
          utilities: 250,
          homeMaintenance: preset.housing.type === 'own' ? Math.round((preset.housing.propertyValue * (preset.housing.maintenanceRate / 100) / 12)) : 0,

          // Transportation - EXPLICIT from preset
          carPayment: Math.round((preset.lifestyle.transportation || 600) * 0.5),
          carInsurance: 150,
          gas: Math.round((preset.lifestyle.transportation || 600) * 0.3),
          publicTransit: 0,
          carMaintenance: Math.round((preset.lifestyle.transportation || 600) * 0.2),

          // Food - distribute the REMAINING monthlyExpenses after accounting for known categories
          // monthlyExpenses should ONLY cover: food, shopping, personal care, hobbies, subscriptions, gym, gifts, charity
          // NOT: housing, transportation, healthcare, entertainment (those are separate fields)
          groceries: Math.round((preset.lifestyle.monthlyExpenses || 3000) * 0.35),
          diningOut: Math.round((preset.lifestyle.monthlyExpenses || 3000) * 0.20),

          // Personal
          shopping: Math.round((preset.lifestyle.monthlyExpenses || 3000) * 0.15),
          personalCare: Math.round((preset.lifestyle.monthlyExpenses || 3000) * 0.05),

          // Healthcare - EXPLICIT from preset
          healthInsurance: preset.lifestyle.healthcare || 300,
          medicalExpenses: 100,

          // Entertainment - EXPLICIT from preset
          entertainment: Math.round((preset.lifestyle.entertainment || 500) * 0.40),
          hobbies: Math.round((preset.lifestyle.entertainment || 500) * 0.30),
          travel: Math.round((preset.lifestyle.entertainment || 500) * 0.30),
          subscriptions: Math.round((preset.lifestyle.monthlyExpenses || 3000) * 0.05),
          gym: Math.round((preset.lifestyle.monthlyExpenses || 3000) * 0.03),

          // Savings - EXPLICIT from preset
          retirement401k: preset.monthlyContribution || 500,
          otherSavings: 0,

          // Debt & Children
          debtPayments: 0,
          childcare: 0,
          childEducation: 0,
          childActivities: 0,
          childOther: 0,

          // Other - remaining from monthlyExpenses
          gifts: Math.round((preset.lifestyle.monthlyExpenses || 3000) * 0.10),
          charity: Math.round((preset.lifestyle.monthlyExpenses || 3000) * 0.05),
          petExpenses: 0,
          other: Math.round((preset.lifestyle.other || 500) * 0.02)
        }
      };

      setVisions(prevVisions => [...prevVisions, newVision]);
      setActiveVision(newVision.id);
      setShowPresetExplorer(false);
      // Auto-close sidebar on mobile
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      }
    }
  };

  const currentVision = visions.find(v => v.id === activeVision);

  // Create stable stringified versions of complex objects for dependency checking
  const precisionExpensesStr = useMemo(() => JSON.stringify(currentVision?.precisionExpenses), [currentVision?.precisionExpenses]);
  const financialSettingsStr = useMemo(() => JSON.stringify(currentVision?.financialSettings), [currentVision?.financialSettings]);
  const financialEventsStr = useMemo(() => JSON.stringify(currentVision?.financialEvents), [currentVision?.financialEvents]);
  const debtsStr = useMemo(() => JSON.stringify(currentVision?.debts), [currentVision?.debts]);
  const collegeSavingsStr = useMemo(() => JSON.stringify(currentVision?.collegeSavings), [currentVision?.collegeSavings]);
  const lifestyleExpensesStr = useMemo(() => JSON.stringify(currentVision?.lifestyleExpenses), [currentVision?.lifestyleExpenses]);
  const oneTimeEventsStr = useMemo(() => JSON.stringify(currentVision?.oneTimeEvents), [currentVision?.oneTimeEvents]);

  // Sync auto-calculated retirement age to the vision's retireAge field
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!currentVision) return;

    // THREE-LEVER AUTO-CALCULATION SYSTEM
    // Support both new autoCalculateMode and legacy autoCalculateRetireAge
    const mode = currentVision.autoCalculateMode || (currentVision.autoCalculateRetireAge ? 'retireAge' : null);

    if (!mode || currentVision._calculating) return;

    // IMPORTANT: Don't recalculate retirement age when only financialSettings change
    // Changing withdrawal rate shouldn't trigger a new retirement age calculation
    // The new withdrawal rate will be used in the NEXT calculation when income/expenses change

    console.log(`⚡ Auto-calculating: ${mode}`);

    if (mode === 'retireAge') {
      // Calculate retirement age given income and expenses
      const calculatedAge = calculateOptimalRetirementAge({
        ...currentVision,
        retireAge: 75,
        _calculatingRetireAge: true,
        _calculating: true,
        partnerRetireAge: currentVision.partnerRetireAge
      });

      // Use tolerance of 0.1 years (~5 weeks) to prevent oscillation when withdrawal rate changes
      if (calculatedAge !== null && Math.abs(calculatedAge - (currentVision.retireAge || 0)) > 0.1) {
        console.log(`✅ Updating retirement age to ${calculatedAge}`);
        const roundedAge = Math.round(calculatedAge * 100) / 100; // Round to 2 decimals
        const updates = { retireAge: roundedAge };

        // If syncing partner retirement age, update it too
        if (currentVision.syncPartnerRetireAge) {
          const ageDiff = (currentVision.partnerAge || currentVision.age) - currentVision.age;
          updates.partnerRetireAge = Math.round((roundedAge + ageDiff) * 100) / 100; // Round to 2 decimals
          console.log(`  Also updating partner retire age to ${updates.partnerRetireAge} (synced)`);
        }

        updateVision(currentVision.id, updates);
      } else if (calculatedAge === null) {
        console.log('⚠️ Cannot calculate retirement age - not viable');
      }
    } else if (mode === 'income') {
      // Calculate MINIMUM required income given retirement age (with fixed expenses)
      if (!currentVision.retireAge) {
        console.log('⚠️ Cannot calculate income - missing retireAge');
        return;
      }

      const calculatedIncome = calculateRequiredIncome({
        ...currentVision,
        _calculatingIncome: true,
        _calculating: true
      });

      if (calculatedIncome !== null) {
        console.log(`✅ Required income: $${Math.round(calculatedIncome/1000)}k`);
        // Store in separate field - don't overwrite user's currentIncome input
        updateVision(currentVision.id, { calculatedRequiredIncome: calculatedIncome });
      } else {
        console.log('⚠️ Cannot calculate required income - goal not achievable');
        updateVision(currentVision.id, { calculatedRequiredIncome: null });
      }
    } else if (mode === 'savingsRate') {
      // Calculate retirement age given target savings rate
      // This models "if we save X%, when can we retire?"
      if (!currentVision.targetSavingsRate) {
        console.log('⚠️ Cannot calculate retirement age - missing targetSavingsRate');
        return;
      }

      const calculatedAge = calculateOptimalRetirementAge({
        ...currentVision,
        retireAge: 75,
        _calculatingRetireAge: true,
        _calculating: true,
        partnerRetireAge: currentVision.partnerRetireAge
      });

      // Use tolerance of 0.1 years (~5 weeks) to prevent oscillation
      if (calculatedAge !== null && Math.abs(calculatedAge - (currentVision.retireAge || 0)) > 0.1) {
        console.log(`✅ Updating retirement age to ${calculatedAge} with ${currentVision.targetSavingsRate}% savings rate`);
        const roundedAge = Math.round(calculatedAge * 100) / 100; // Round to 2 decimals
        const updates = { retireAge: roundedAge };

        // If syncing partner retirement age, update it too
        if (currentVision.syncPartnerRetireAge) {
          const ageDiff = (currentVision.partnerAge || currentVision.age) - currentVision.age;
          updates.partnerRetireAge = Math.round((roundedAge + ageDiff) * 100) / 100; // Round to 2 decimals
          console.log(`  Also updating partner retire age to ${updates.partnerRetireAge} (synced)`);
        }

        updateVision(currentVision.id, updates);
      } else if (calculatedAge === null) {
        console.log('⚠️ Cannot calculate retirement age - not viable even with target savings rate');
      }
    }
  }, [
    currentVision?.id,
    currentVision?.autoCalculateMode,
    currentVision?.autoCalculateRetireAge, // Legacy support
    currentVision?.targetSavingsRate,
    currentVision?.retireAge,
    currentVision?.age,
    currentVision?.currentIncome,
    currentVision?.partnerIncomeStart,
    currentVision?.partnerAge,
    currentVision?.partnerRetireAge,
    currentVision?.hasPartner,
    currentVision?.kids,
    currentVision?.firstChildAge,
    currentVision?.childSpacing,
    JSON.stringify(currentVision?.childrenBirthAges),
    currentVision?.currentCash,
    currentVision?.current401k,
    currentVision?.currentInvestments,
    currentVision?.currentHomeEquity,
    currentVision?.currentOtherAssets,
    currentVision?.housingStatus,
    currentVision?.homePurchaseAge,
    currentVision?.homePrice,
    currentVision?.downPayment,
    currentVision?.mortgageRate,
    currentVision?.currentMortgagePayment,
    currentVision?.yearsLeftOnMortgage,
    // Memoized stringified versions of complex objects to detect changes
    precisionExpensesStr,
    // financialSettingsStr, // REMOVED: Changing withdrawal rate shouldn't trigger recalculation
    financialEventsStr,
    debtsStr,
    collegeSavingsStr,
    lifestyleExpensesStr,
    oneTimeEventsStr,
  ]);

  return (
    <div className="min-h-screen bg-sky-50 flex flex-col">
      {/* Header */}
      <header className="shadow-lg" style={{ backgroundColor: brandColors.navyDark }}>
        <div className="px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Left: Logo + Hamburger */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile: Hamburger to toggle sidebar */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="lg:hidden p-1.5 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all"
                title={sidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}
              >
                {sidebarCollapsed ? (
                  <Menu className="w-5 h-5 text-white" />
                ) : (
                  <X className="w-5 h-5 text-white" />
                )}
              </button>
              <ChartIcon className="w-8 h-8 sm:w-10 sm:h-10" bgColor={brandColors.navyMedium} lineColor={brandColors.bluePrimary} />
              <h1 className="text-lg sm:text-2xl font-bold text-white">
                LifeMath <span className="text-xs font-medium bg-orange-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full align-middle">Beta</span>
              </h1>
            </div>

            {/* Right: Controls */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Dark Mode Toggle */}
              <div className="flex items-center gap-1">
                <span className="text-white/70 text-sm sm:text-base leading-none hidden sm:inline">☀️</span>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className={`relative w-9 h-5 sm:w-11 sm:h-6 rounded-full transition-colors duration-300 flex-shrink-0 ${
                    darkMode ? 'bg-slate-600' : 'bg-blue-400'
                  }`}
                  title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${
                      darkMode ? 'translate-x-4 sm:translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className="text-white/70 text-sm sm:text-base leading-none hidden sm:inline">🌙</span>
              </div>

              {/* Feedback Button */}
              <a
                href="https://forms.gle/aUWFpfJSw2kYuc1S8"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-white/20 backdrop-blur-sm text-white rounded-lg transition-all font-medium text-xs sm:text-sm flex items-center gap-1.5 border border-white/20"
                title="Send Feedback"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <span className="hidden sm:inline">Feedback</span>
              </a>

              {/* Desktop: Advanced Settings Button */}
              <button
                onClick={() => setShowAdvancedSettings(true)}
                className="hidden md:flex px-3 sm:px-4 py-1.5 sm:py-2 hover:bg-white/20 backdrop-blur-sm text-white rounded-lg transition-all font-medium text-sm items-center gap-2 border border-white/20"
                style={{ backgroundColor: 'rgba(59, 130, 246, 0.3)' }}
                title="Investment returns, inflation, withdrawal rate - all customizable"
              >
                <SliderIcon className="w-4 h-4" trackColor="white" dotColor="white" />
                <span className="hidden lg:inline">Advanced Settings</span>
                <span className="lg:hidden">Advanced</span>
              </button>

              {/* Mobile: Settings Icon Only */}
              <button
                onClick={() => setShowAdvancedSettings(true)}
                className="md:hidden p-1.5 sm:p-2 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all border border-white/20"
                title="Investment returns, inflation, withdrawal rate - all customizable"
              >
                <SliderIcon className="w-4 h-4 sm:w-5 sm:h-5" trackColor="white" dotColor="white" />
              </button>

              {/* Settings */}
              <button
                onClick={() => setShowSettings(true)}
                className="p-1.5 sm:p-2 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all border border-white/20"
                title="API Settings"
              >
                <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </button>

              {/* About - Hide text on mobile */}
              <button
                onClick={() => setShowAbout(true)}
                className="px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all border border-white/20 text-white text-xs sm:text-sm font-medium"
                title="About LifeMath"
              >
                <span className="hidden sm:inline">About</span>
                <span className="sm:hidden">ℹ️</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Settings Modal */}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} visions={visions} setVisions={setVisions} />}

      {/* About Modal */}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      {/* Advanced Settings Modal */}
      {showAdvancedSettings && (
        <AdvancedSettingsModal
          vision={visions.find(v => v.id === activeVision)}
          onUpdate={(updates) => {
            if (activeVision) {
              updateVision(activeVision, updates);
            }
          }}
          onClose={() => setShowAdvancedSettings(false)}
        />
      )}

      {/* Preset Explorer Modal */}
      {showPresetExplorer && (
        <PresetExplorer
          onLoadPreset={loadPreset}
          darkMode={darkMode}
          onClose={() => {
            setShowPresetExplorer(false);
            // Keep sidebar closed on mobile after closing preset explorer
            if (window.innerWidth < 1024) {
              setSidebarCollapsed(true);
            }
          }}
        />
      )}

      {/* Main Content - Sidebar Layout */}
      <main className="flex-1 flex overflow-hidden relative">
        {/* Overlay for mobile when sidebar is open */}
        {!sidebarCollapsed && (
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}

        {/* Mobile Backdrop Overlay */}
        {!sidebarCollapsed && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}

        {/* Left Sidebar */}
        <aside
          className={`
            flex flex-col overflow-hidden transition-all duration-300 shadow-xl
            ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-teal-200'}
            fixed left-0 top-0 bottom-0 w-80 sm:w-96 z-50
            lg:relative lg:w-96 lg:opacity-100 lg:translate-x-0 lg:border-r-2
            ${sidebarCollapsed
              ? '-translate-x-full lg:w-0 lg:opacity-0'
              : 'translate-x-0'
            }
          `}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* AI Input Section */}
          <div className={`border-b-2 flex flex-col ${darkMode ? 'border-teal-800 bg-slate-900' : 'border-teal-200 bg-cyan-50'}`}>
            <div
              className={`flex items-center gap-2 p-4 cursor-pointer select-none transition-all ${darkMode ? 'hover:bg-slate-800/50' : 'hover:bg-white/50'}`}
              onClick={() => toggleSection('ai-panel')}
            >
              <Sparkles className={`w-5 h-5 ${darkMode ? 'text-teal-400' : 'text-teal-600'}`} />
              <h2 className={`font-bold text-lg flex-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {activeVision ? 'Update Vision with AI' : 'Create Vision with AI'}
              </h2>
              {/* Show chevron icon on all screen sizes */}
              <ChevronDown className={`w-5 h-5 transition-transform ${darkMode ? 'text-teal-400' : 'text-teal-600'} ${collapsedSections['ai-panel'] ? '-rotate-90' : ''}`} />
            </div>

            {!collapsedSections['ai-panel'] && (
            <div className="px-6 pb-6">
              {conversation.length > 0 && (
                <button
                  onClick={() => setConversation([])}
                  className={`text-xs mb-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Clear Conversation
                </button>
              )}

            {/* Active Vision Indicator with option to create new */}
            {activeVision && (() => {
              const currentVision = visions.find(v => v.id === activeVision);
              return currentVision ? (
                <div className={`mb-3 p-3 border-2 rounded-lg shadow-sm ${
                  darkMode ? 'bg-teal-900/30 border-teal-700' : 'bg-teal-50 border-teal-300'
                }`}>
                  <div className="flex items-center gap-2">
                    <Edit3 className={`w-4 h-4 ${darkMode ? 'text-teal-400' : 'text-teal-600'}`} />
                    <div className="flex-1">
                      <div className={`text-xs font-medium ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>Editing:</div>
                      <div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-teal-900'}`}>{currentVision.name}</div>
                    </div>
                    <button
                      onClick={() => setActiveVision(null)}
                      className={`text-xs underline ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                      title="Deselect to create a new vision"
                    >
                      + New
                    </button>
                  </div>
                  <div className={`text-xs mt-2 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    💡 Tip: Ask me to change specific details, or click "+ New" to create a fresh vision
                  </div>
                </div>
              ) : null;
            })()}

            {/* Conversation History */}
            {conversation.length > 0 && (
              <div className="mb-4 space-y-3 max-h-64 overflow-y-auto">
                {conversation.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg text-sm ${
                      msg.role === 'user'
                        ? (darkMode ? 'bg-blue-900/40 border border-blue-700 text-white' : 'bg-blue-50 border border-blue-200 text-gray-900')
                        : (darkMode ? 'bg-slate-800 border border-slate-600 text-gray-200' : 'bg-gray-50 border border-gray-200 text-gray-800')
                    }`}
                  >
                    <div className={`font-bold text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {msg.role === 'user' ? 'You' : 'AI Assistant'}
                    </div>
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  </div>
                ))}
              </div>
            )}

            <textarea
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (aiInput.trim() && !isProcessingAI) {
                    processAIInput();
                  }
                }
              }}
              placeholder={
                conversation.length > 0
                  ? "Continue the conversation..."
                  : activeVision
                  ? "Ask me to update this vision...\n\nExamples:\n- 'Change retirement age to 70'\n- 'Increase income to $95,000'\n- 'Add a partner who earns $70k'\n- 'Move retirement to 65 and add 1 more kid'"
                  : "Describe your ideal life vision...\n\nExample: I'm 28 and want to retire at 60. I'd like to buy a house at 33 in a medium-cost city for around $450k. I want 2 kids starting at age 31. Family and freedom are most important to me..."
              }
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y text-base ${
                darkMode ? 'bg-slate-800 border-slate-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'
              }`}
              style={{ minHeight: '225px' }}
              disabled={isProcessingAI}
            />
            <button
              onClick={processAIInput}
              disabled={isProcessingAI || !aiInput.trim()}
              className="mt-3 w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessingAI ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Thinking...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {conversation.length > 0 ? 'Send' : activeVision ? 'Update Vision' : 'Generate Vision'}
                </>
              )}
            </button>

            {conversation.length === 0 && (
              <>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    const newVision = createEmptyVision();
                    setVisions([...visions, newVision]);
                    setActiveVision(newVision.id);
                    // Auto-close sidebar on mobile
                    if (window.innerWidth < 1024) {
                      setSidebarCollapsed(true);
                    }
                  }}
                  className={`w-full px-4 py-2 border rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                    darkMode ? 'bg-slate-700 border-slate-500 text-white hover:bg-slate-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Plus className="w-5 h-5" />
                  Create Manually
                </button>
              </>
            )}
            </div>
            )}
          </div>

          {/* Visions List */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-bold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>Your Visions</h3>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{visions.length} {visions.length === 1 ? 'vision' : 'visions'}</span>
            </div>

            {/* Explore Presets Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Always close sidebar on mobile first, then open modal
                const isMobile = window.innerWidth < 1024;
                if (isMobile) {
                  setSidebarCollapsed(true);
                  // Small delay to ensure sidebar closes before modal opens
                  setTimeout(() => setShowPresetExplorer(true), 50);
                } else {
                  setShowPresetExplorer(true);
                }
              }}
              className={`w-full mb-3 p-3 rounded-lg border-2 border-dashed transition-all hover:scale-[1.02] ${
                darkMode
                  ? 'bg-teal-900/20 border-teal-600 hover:bg-teal-900/40 text-teal-400'
                  : 'bg-teal-50 border-teal-400 hover:bg-teal-100 text-teal-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2 text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                <span>Explore Life Presets</span>
              </div>
              <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                6 scenarios to compare lifestyle decisions
              </div>
            </button>

            {visions.length === 0 ? (
              <div className={`text-center py-8 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <TrendingUp className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <p>No visions yet.</p>
                <p className="text-xs mt-1">Create one using AI above!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {[...visions]
                  .sort((a, b) => (b.lastModified || 0) - (a.lastModified || 0))
                  .map(vision => {
                  const summary = getVisionSummary(vision);
                  const isActive = activeVision === vision.id;
                  return (
                    <div
                      key={vision.id}
                      onClick={() => {
                        setActiveVision(vision.id);
                        // Auto-close sidebar on mobile when selecting a vision
                        if (window.innerWidth < 1024) {
                          setSidebarCollapsed(true);
                        }
                      }}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isActive
                          ? (darkMode ? 'bg-cyan-900/40 border-cyan-600 shadow-lg' : 'bg-cyan-50 border-cyan-400 shadow-lg')
                          : (darkMode ? 'bg-slate-800 border-slate-600 hover:border-blue-500 hover:shadow-md' : 'bg-white border-gray-300 hover:border-blue-400 hover:shadow-md')
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className={`font-bold text-sm flex-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{vision.name}</h4>
                        <div className="flex gap-1 ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateVision(vision);
                            }}
                            className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                            title="Duplicate"
                          >
                            <Copy className={`w-3 h-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteVision(vision.id);
                            }}
                            className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-red-900/50' : 'hover:bg-red-50'}`}
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </button>
                        </div>
                      </div>
                      <div className={`space-y-1 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p className="font-medium">Age {vision.age} → Retire {formatRetirementAge(
                          vision.autoCalculateRetireAge
                            ? calculateOptimalRetirementAge({ ...vision, _calculatingRetireAge: true })
                            : vision.retireAge
                        )}</p>
                        <p className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          Avg Savings: ${Math.round(summary.monthlySavings).toLocaleString()}/mo
                        </p>
                        <div className="flex items-center justify-between">
                          <span className={`font-bold ${
                            summary.feasibility === 'high' ? (darkMode ? 'text-green-400' : 'text-green-600') :
                            summary.feasibility === 'moderate' ? (darkMode ? 'text-yellow-400' : 'text-yellow-600') : (darkMode ? 'text-red-400' : 'text-red-600')
                          }`}>
                            {summary.feasibility === 'high' ? 'Highly Feasible' :
                             summary.feasibility === 'moderate' ? 'Moderately Feasible' : 'Challenging'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        {/* Right Main Area - Vision Builder */}
        <div className={`flex-1 overflow-hidden flex flex-col lg:flex-row relative transition-colors duration-300 w-full ${
          darkMode ? 'bg-slate-900' : 'bg-gray-50'
        }`}>
          {currentVision && (
          <>
            {/* Toggle Buttons - positioned on the right side */}
            <div className="absolute top-2 sm:top-4 right-2 sm:right-4 z-30 flex gap-1.5 sm:gap-2">
              {/* Sidebar Toggle Button */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1.5 sm:p-2 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 transition-all border border-blue-400"
                title={sidebarCollapsed ? "Show AI Sidebar" : "Hide AI Sidebar"}
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Lifestyle Trade-offs Button - Desktop Only */}
              <button
                onClick={() => setShowScenarios(!showScenarios)}
                className={`hidden lg:flex p-1.5 sm:p-2 rounded-lg shadow-lg shadow-blue-500/50 transition-all border border-blue-400 ${
                  showScenarios
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                title="Lifestyle Trade-offs"
              >
                <Scale className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Life Vision Toggle Button - Desktop Only */}
              <button
                onClick={() => toggleSection('life-vision')}
                className="hidden lg:block p-1.5 sm:p-2 bg-green-600 text-white rounded-lg shadow-lg hover:bg-green-700 transition-all border border-green-400"
                title={collapsedSections['life-vision'] ? "Show Life Vision Inputs" : "Hide Life Vision Inputs"}
              >
                <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </>
          )}

          {currentVision ? (
            <>
              {/* Left: Inputs */}
              <div className={`overflow-y-auto p-3 sm:p-4 lg:p-6 transition-all duration-300 ${
                darkMode ? 'lg:border-r-2 border-slate-700' : 'lg:border-r-2 border-blue-200'
              } ${
                // On mobile (< lg), inputs are always visible; on desktop, they can be collapsed
                collapsedSections['life-vision']
                  ? 'w-full lg:w-0 lg:h-0 lg:opacity-0 lg:overflow-hidden'
                  : showScenarios
                    ? 'w-full lg:w-1/3 opacity-100'
                    : 'w-full lg:w-1/2 opacity-100'
              }`}>
                <div className="mb-4">
                  <input
                    type="text"
                    value={currentVision.name}
                    onBlur={(e) => updateVision(currentVision.id, { name: e.target.value })}
                    className={`text-2xl font-bold border-b-2 border-transparent focus:outline-none px-2 py-1 w-full ${
                      darkMode
                        ? 'text-emerald-400 hover:border-emerald-600 focus:border-emerald-400 bg-transparent'
                        : 'text-blue-700 hover:border-blue-300 focus:border-emerald-500'
                    }`}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 mb-4">
                  {/* Personal Info */}
                  <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${
                    darkMode
                      ? 'bg-slate-800 border-slate-600 hover:border-emerald-500'
                      : 'bg-blue-50 border-blue-300 hover:border-blue-400'
                  }`}>
                    <h3
                      className={`text-sm font-semibold flex items-center justify-between cursor-pointer select-none ${darkMode ? 'text-white' : 'text-gray-900'}`}
                      onClick={() => toggleSection('personal')}
                    >
                      <div className="flex items-center gap-1">
                        <Users className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        Personal
                      </div>
                      <div className="flex items-center gap-2">
                        {collapsedSections['personal'] && (
                          <span className={`text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Age {currentVision.age} | ${(currentVision.currentIncome/1000).toFixed(0)}k | Retire {formatRetirementAge(
                              currentVision.autoCalculateRetireAge
                                ? calculateOptimalRetirementAge({ ...currentVision, _calculatingRetireAge: true })
                                : currentVision.retireAge
                            )}
                          </span>
                        )}
                        <ChevronDown className={`w-4 h-4 transition-transform ${darkMode ? 'text-blue-400' : 'text-blue-600'} ${collapsedSections['personal'] ? '-rotate-90' : ''}`} />
                      </div>
                    </h3>

                    {!collapsedSections['personal'] && (<div className="space-y-2">
                    <div>
                      <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current Age</label>
                      <input
                        type="number"
                        value={currentVision.age}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            updateVision(currentVision.id, { age: '' });
                          } else {
                            updateVision(currentVision.id, { age: parseInt(value) || 0 });
                          }
                        }}
                        onBlur={(e) => {
                          const value = parseInt(e.target.value);
                          if (isNaN(value) || value <= 0) {
                            updateVision(currentVision.id, { age: 30 });
                          }
                        }}
                        className={`w-full px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-blue-200 text-gray-900'
                        }`}
                      />
                    </div>

                    <div>
                      <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current Income</label>

                      {currentVision.autoCalculateMode === 'income' ? (
                        currentVision.calculatedRequiredIncome === null ? (
                          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-xs">
                            <div className="flex items-start gap-2">
                              <span className="text-red-600 font-bold text-lg">⚠️</span>
                              <div>
                                <p className="font-semibold text-red-900 mb-1">Goal Not Achievable</p>
                                <p className="text-red-700">
                                  Cannot find income level that allows retirement at age {currentVision.retireAge} with current expenses.
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (() => {
                          // Calculate what the savings rate will be at this income
                          const retireAgeWhole = Math.round(currentVision.retireAge);
                          const testVision = { ...currentVision, currentIncome: currentVision.calculatedRequiredIncome, retireAge: retireAgeWhole };
                          const projection = calculateFinancials(testVision);
                          const workingYears = projection.filter(y => y.age >= currentVision.age && y.age < retireAgeWhole);
                          const savingsRate = workingYears.length > 0
                            ? workingYears.reduce((sum, y) => sum + (y.savingsRate || 0), 0) / workingYears.length
                            : 0;

                          return (
                            <div className="px-3 py-2 bg-green-50 border border-green-200 rounded text-xs">
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-medium text-green-900">Minimum Required Income:</span>
                                <span className="text-lg font-bold text-green-900">
                                  ${Math.round((currentVision.calculatedRequiredIncome || 0) / 1000)}k/yr
                                </span>
                              </div>
                              <p className="text-green-700 text-[10px]">
                                To retire at {currentVision.retireAge} (requires {savingsRate.toFixed(1)}% household savings rate{currentVision.hasPartner && currentVision.partnerIncomeStart > 0 ? ', includes partner' : ''})
                              </p>
                            </div>
                          );
                        })()
                      ) : (
                        <>
                          <input
                            type="number"
                            defaultValue={currentVision.currentIncome || ''}
                            key={`income-${currentVision.id}-${currentVision.currentIncome}`}
                            onBlur={(e) => {
                              const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                              updateVision(currentVision.id, { currentIncome: Math.max(0, value) });
                            }}
                            className={`w-full px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                              darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-blue-200 text-gray-900'
                            }`}
                          />
                          <p className={`text-[10px] mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Annual pre-tax income (salary + bonuses)
                          </p>
                        </>
                      )}
                    </div>

                    {/* THREE-LEVER AUTO-CALCULATION SYSTEM */}
                    <div className={`p-3 rounded-lg mb-3 ${darkMode ? 'bg-slate-800/50 border border-slate-600' : 'bg-blue-50/50 border border-blue-200'}`}>
                      <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        Auto-Calculate (Choose One)
                      </label>
                      <p className={`text-[10px] mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Pick which variable to solve for - the others stay fixed
                      </p>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="autoCalculateMode"
                            checked={currentVision.autoCalculateMode === 'retireAge' || (currentVision.autoCalculateRetireAge && !currentVision.autoCalculateMode)}
                            onChange={() => updateVision(currentVision.id, { autoCalculateMode: 'retireAge', autoCalculateRetireAge: true })}
                            className={`w-4 h-4 text-blue-600 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500' : 'bg-white border-gray-300'}`}
                          />
                          <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <strong>Retirement Age</strong> - when can I retire?
                          </span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="autoCalculateMode"
                            checked={currentVision.autoCalculateMode === 'income'}
                            onChange={() => updateVision(currentVision.id, { autoCalculateMode: 'income', autoCalculateRetireAge: false })}
                            className={`w-4 h-4 text-blue-600 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500' : 'bg-white border-gray-300'}`}
                          />
                          <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <strong>Income Required</strong> - how much must I earn?
                            <span className={`block text-[10px] mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              (with current expenses)
                            </span>
                          </span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="autoCalculateMode"
                            checked={currentVision.autoCalculateMode === 'savingsRate'}
                            onChange={() => updateVision(currentVision.id, { autoCalculateMode: 'savingsRate', autoCalculateRetireAge: false })}
                            className={`w-4 h-4 text-blue-600 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500' : 'bg-white border-gray-300'}`}
                          />
                          <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <strong>Household Savings Rate</strong> - what % must we save?
                            <span className={`block text-[10px] mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              (with current income & expenses)
                            </span>
                          </span>
                        </label>
                      </div>

                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => updateVision(currentVision.id, { autoCalculateMode: null, autoCalculateRetireAge: false })}
                          className={`text-xs underline ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
                        >
                          Turn off auto-calculate
                        </button>
                      </div>

                    </div>

                    {/* SAVINGS RATE INPUT (when in savingsRate mode) */}
                    {currentVision.autoCalculateMode === 'savingsRate' && (() => {
                      // Calculate current savings rate from financial data WITHOUT applying target rate override
                      const financialData = calculateFinancials({ ...currentVision, _gettingBaselineRate: true });
                      const currentYearData = financialData.find(d => d.age === currentVision.age);
                      const currentRate = currentYearData?.savingsRate || 50;

                      return (
                        <div className="mb-3">
                          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Target Savings Rate (%)
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            defaultValue={currentVision.targetSavingsRate || currentRate}
                            key={`target-rate-${currentVision.id}-${currentVision.targetSavingsRate || currentRate}`}
                            onBlur={(e) => {
                              e.preventDefault();
                              const value = e.target.value === '' ? currentRate : parseFloat(e.target.value) || currentRate;
                              // Store current scroll position before update
                              const scrollY = window.scrollY;
                              updateVision(currentVision.id, { targetSavingsRate: Math.max(0, Math.min(100, value)) });
                              // Restore scroll position after React renders
                              requestAnimationFrame(() => {
                                window.scrollTo(0, scrollY);
                              });
                            }}
                            placeholder="0-100"
                            className={`w-full px-2 py-1 text-xs border rounded ${
                              darkMode
                                ? 'bg-slate-700 border-slate-600 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                          <p className={`text-[10px] mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Current rate: {currentRate.toFixed(1)}% - adjust to model expense changes
                          </p>
                        </div>
                      );
                    })()}

                    {/* SAVINGS RATE CALCULATION RESULT */}
                    {currentVision.autoCalculateMode && currentVision.autoCalculateMode !== 'savingsRate' && (
                      <div className="mb-3">
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {currentVision.autoCalculateMode === 'retireAge' ? 'Savings Rate (Implied)' : 'Required Savings Rate'}
                        </label>
                        {(() => {
                          const rateResult = calculateRequiredSavingsRate({
                            ...currentVision,
                            _calculatingSavingsRate: true,
                            _fromRetirementAgeCalc: currentVision.autoCalculateMode === 'retireAge' || currentVision.autoCalculateMode === 'income'
                          });

                          if (rateResult === null) {
                            return (
                              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-xs">
                                <p className="font-semibold text-red-900">Cannot calculate required savings rate</p>
                              </div>
                            );
                          }

                          const { required, current, onTrack } = rateResult;
                          const isAchievable = required <= 100;

                          return (
                            <div className={`px-3 py-2 rounded text-xs border ${
                              onTrack
                                ? 'bg-green-50 border-green-200'
                                : isAchievable
                                ? 'bg-yellow-50 border-yellow-200'
                                : 'bg-red-50 border-red-200'
                            }`}>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className={`text-[10px] font-medium ${onTrack ? 'text-green-700' : isAchievable ? 'text-yellow-700' : 'text-red-700'}`}>
                                    Current Rate:
                                  </span>
                                  <span className={`text-sm font-bold ${onTrack ? 'text-green-900' : isAchievable ? 'text-yellow-900' : 'text-red-900'}`}>
                                    {current.toFixed(1)}%
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className={`text-[10px] font-medium ${onTrack ? 'text-green-700' : isAchievable ? 'text-yellow-700' : 'text-red-700'}`}>
                                    Required Rate:
                                  </span>
                                  <span className={`text-lg font-bold ${onTrack ? 'text-green-900' : isAchievable ? 'text-yellow-900' : 'text-red-900'}`}>
                                    {required.toFixed(1)}%
                                  </span>
                                </div>
                                <p className={`text-[10px] pt-1 border-t ${onTrack ? 'border-green-200 text-green-700' : isAchievable ? 'border-yellow-200 text-yellow-700' : 'border-red-200 text-red-700'}`}>
                                  {currentVision.autoCalculateMode === 'retireAge'
                                    ? `✓ This is your current savings rate at calculated retirement age`
                                    : onTrack
                                    ? `✓ On track! You have a ${(current - required).toFixed(1)}% safety buffer`
                                    : isAchievable
                                    ? `⚠️ Need to save ${(required - current).toFixed(1)}% more - reduce expenses or increase income`
                                    : `✗ Not achievable - need to reduce expenses, increase income, or retire later`
                                  }
                                </p>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    <div>
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Retirement Age</label>

                      {(currentVision.autoCalculateMode === 'retireAge' || currentVision.autoCalculateMode === 'savingsRate' || (currentVision.autoCalculateRetireAge && !currentVision.autoCalculateMode)) ? (
                        (() => {
                          const calculatedAge = calculateOptimalRetirementAge({ ...currentVision, _calculatingRetireAge: true });

                          if (calculatedAge === null) {
                            return (
                              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-xs">
                                <div className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold text-lg">⚠️</span>
                                  <div>
                                    <p className="font-semibold text-red-900 mb-1">Retirement Not Possible</p>
                                    <p className="text-red-700">
                                      Your expenses exceed your income. You need to either increase income or reduce expenses before retirement is viable.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          const yearsUntilRetirement = calculatedAge - currentVision.age;

                          // Extract years, months, and days from fractional age
                          const wholeYears = Math.floor(calculatedAge);
                          const remainingFraction = calculatedAge - wholeYears;
                          const totalDays = Math.round(remainingFraction * 365);
                          const months = Math.floor(totalDays / 30.44); // Average days per month
                          const days = Math.round(totalDays - (months * 30.44));

                          // Calculate exact retirement date
                          const today = new Date();
                          const retirementDate = new Date(today);
                          retirementDate.setFullYear(today.getFullYear() + Math.floor(yearsUntilRetirement));

                          // Add the fractional year as days
                          const fractionalDays = Math.round((yearsUntilRetirement - Math.floor(yearsUntilRetirement)) * 365);
                          retirementDate.setDate(today.getDate() + fractionalDays);

                          const retirementDateString = retirementDate.toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          });

                          return (
                            <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs">
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-medium text-blue-900">Calculated Retirement Age:</span>
                                <span className="text-lg font-bold text-blue-900">
                                  {wholeYears}y {months}m {days}d
                                </span>
                              </div>
                              <p className="text-blue-700 mb-1">
                                Retirement date: <span className="font-semibold">{retirementDateString}</span>
                              </p>
                              <p className="text-blue-700 text-[10px]">
                                Using {(() => {
                                  // Use custom withdrawal rate if set, otherwise calculate dynamically
                                  const actualRate = currentVision.financialSettings?.withdrawalRate
                                    ? currentVision.financialSettings.withdrawalRate
                                    : calculateWithdrawalRate(currentVision.age, Math.floor(calculatedAge));
                                  return (actualRate * 100).toFixed(1);
                                })()}% safe withdrawal rate
                                {currentVision.financialSettings?.withdrawalRate && (
                                  <span className="ml-1 text-blue-600 font-semibold">(custom)</span>
                                )}
                              </p>
                            </div>
                          );
                        })()
                      ) : (
                        <>
                          <input
                            type="number"
                            step="0.1"
                            min={currentVision.age + 0.1}
                            value={currentVision.retireAge || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                // Allow empty during editing
                                updateVision(currentVision.id, { retireAge: '' });
                                return;
                              }
                              const newRetireAge = parseFloat(value);
                              if (!isNaN(newRetireAge)) {
                                const updates = { retireAge: newRetireAge };
                                // If syncing partner retirement age, update it too
                                if (currentVision.syncPartnerRetireAge) {
                                  const ageDiff = (currentVision.partnerAge || currentVision.age) - currentVision.age;
                                  updates.partnerRetireAge = Math.round((newRetireAge + ageDiff) * 10) / 10;
                                }
                                updateVision(currentVision.id, updates);
                              }
                            }}
                            onBlur={(e) => {
                              const value = parseFloat(e.target.value);
                              if (isNaN(value) || value <= currentVision.age) {
                                updateVision(currentVision.id, { retireAge: currentVision.age + 1 });
                              }
                            }}
                            className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                          <p className={`text-[10px] mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Can use decimals (e.g., 45.5 for 45 years 6 months)
                          </p>
                        </>
                      )}
                    </div>
                    </div>)}
                  </div>

                  {/* Housing */}
                  <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${
                    darkMode ? 'bg-slate-800 border-slate-600 hover:border-emerald-500' : 'bg-emerald-50 border-emerald-300 hover:border-emerald-400'
                  }`}>
                    <h3
                      className={`text-sm font-semibold flex items-center justify-between cursor-pointer select-none ${darkMode ? 'text-white' : 'text-gray-900'}`}
                      onClick={() => toggleSection('housing')}
                    >
                      <div className="flex items-center gap-1">
                        <Home className={`w-4 h-4 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                        Housing
                      </div>
                      <div className="flex items-center gap-2">
                        {collapsedSections['housing'] && (
                          <span className={`text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {currentVision.housingStatus === 'will-buy'
                              ? `Buy at ${currentVision.homePurchaseAge} | $${(currentVision.homePrice/1000).toFixed(0)}k`
                              : currentVision.housingStatus === 'already-own'
                              ? `Own | $${currentVision.currentMortgagePayment || 0}/mo`
                              : 'Renting'}
                          </span>
                        )}
                        <ChevronDown className={`w-4 h-4 transition-transform ${darkMode ? 'text-emerald-400' : 'text-emerald-600'} ${collapsedSections['housing'] ? '-rotate-90' : ''}`} />
                      </div>
                    </h3>

                    {!collapsedSections['housing'] && (<div className="space-y-2">
                    <div>
                      <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Housing Status</label>
                      <select
                        value={currentVision.housingStatus || 'will-buy'}
                        onBlur={(e) => updateVision(currentVision.id, { housingStatus: e.target.value })}
                        className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-orange-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      >
                        <option value="already-own">Already Own</option>
                        <option value="will-buy">Will Buy</option>
                        <option value="renting-forever">Renting Forever</option>
                      </select>
                    </div>

                    {/* Renting: Show rent field */}
                    {currentVision.housingStatus === 'renting-forever' && (
                      <div>
                        <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Monthly Rent</label>
                        <input
                          type="number"
                          defaultValue={currentVision.precisionExpenses?.monthlyRent || ''}
                          key={`rent-${currentVision.id}-${currentVision.precisionExpenses?.monthlyRent}`}
                          onBlur={(e) => updateVision(currentVision.id, {
                            precisionExpenses: { ...currentVision.precisionExpenses, monthlyRent: e.target.value === '' ? 0 : parseInt(e.target.value) }
                          })}
                          className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-orange-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        />
                      </div>
                    )}

                    {/* Will Buy: Show purchase age and home price */}
                    {currentVision.housingStatus === 'will-buy' && (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Purchase Age</label>
                            <input
                              type="number"
                              value={currentVision.homePurchaseAge || 35}
                              onBlur={(e) => updateVision(currentVision.id, { homePurchaseAge: parseInt(e.target.value) || 35 })}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-orange-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                          </div>
                          <div>
                            <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Home Price</label>
                            <input
                              type="number"
                              value={currentVision.homePrice || 400000}
                              onBlur={(e) => updateVision(currentVision.id, { homePrice: parseInt(e.target.value) || 400000 })}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-orange-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Down Payment</label>
                            <input
                              type="number"
                              value={currentVision.downPayment || ''}
                              onBlur={(e) => updateVision(currentVision.id, { downPayment: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-orange-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              placeholder="0 = 20%"
                            />
                          </div>
                          <div>
                            <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Rate %</label>
                            <input
                              type="number"
                              step="0.1"
                              value={currentVision.mortgageRate || 7}
                              onBlur={(e) => updateVision(currentVision.id, { mortgageRate: parseFloat(e.target.value) || 7 })}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-orange-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                          </div>
                          <div>
                            <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Est. Payment</label>
                            <div className={`px-2 py-1 text-sm border rounded ${
                              darkMode ? 'bg-slate-700 border-slate-500 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-700'
                            }`}>
                              ${(() => {
                                const price = currentVision.homePrice || 400000;
                                const down = currentVision.downPayment || (price * 0.20);
                                const loan = price - down;
                                const rate = (currentVision.mortgageRate || 7) / 100;
                                const r = rate / 12;
                                const n = 360;
                                const payment = loan * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
                                return Math.round(payment).toLocaleString();
                              })()}/mo
                            </div>
                          </div>
                        </div>
                        {currentVision.homePurchaseAge && (
                          <div className="px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-xs">
                            <p className="text-emerald-900">
                              Buying at age <span className="font-bold">{currentVision.homePurchaseAge}</span>,
                              mortgage paid off at <span className="font-bold">{currentVision.homePurchaseAge + 30}</span>
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Already Own: Show current mortgage details */}
                    {currentVision.housingStatus === 'already-own' && (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Monthly Mortgage</label>
                            <input
                              type="number"
                              value={currentVision.currentMortgagePayment || ''}
                              onBlur={(e) => updateVision(currentVision.id, { currentMortgagePayment: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-orange-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              placeholder="0 if paid off"
                            />
                          </div>
                          <div>
                            <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Mortgage Rate %</label>
                            <input
                              type="number"
                              step="0.1"
                              value={currentVision.mortgageRate || 7}
                              onBlur={(e) => updateVision(currentVision.id, { mortgageRate: parseFloat(e.target.value) || 7 })}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-orange-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                          </div>
                        </div>

                        <div>
                          <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Years Left on Mortgage</label>
                          <input
                            type="number"
                            value={currentVision.yearsLeftOnMortgage || ''}
                            onBlur={(e) => updateVision(currentVision.id, { yearsLeftOnMortgage: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                            className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-orange-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            placeholder="0 if paid off"
                          />
                        </div>

                        {currentVision.yearsLeftOnMortgage > 0 && (
                          <div className="px-2 py-1 bg-teal-50 border border-teal-200 rounded text-xs">
                            <p className="text-teal-900">
                              Paid off at age <span className="font-bold">{currentVision.age + currentVision.yearsLeftOnMortgage}</span>
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Home-related costs for owners */}
                    {(currentVision.housingStatus === 'already-own' || currentVision.housingStatus === 'will-buy') && (
                        <div className="pt-2 border-t border-gray-200 space-y-2">
                          <p className="text-xs font-medium text-gray-700">Other Costs (Monthly)</p>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { key: 'propertyTax', label: 'Property Tax' },
                              { key: 'homeInsurance', label: 'Insurance' },
                              { key: 'utilities', label: 'Utilities' },
                              { key: 'homeMaintenance', label: 'Maintenance' }
                            ].map(({ key, label }) => (
                              <div key={key}>
                                <label className={`block text-xs mb-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{label}</label>
                                <input
                                  type="number"
                                  value={currentVision.precisionExpenses?.[key] || ''}
                                  onBlur={(e) => updateVision(currentVision.id, {
                                    precisionExpenses: { ...currentVision.precisionExpenses, [key]: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }
                                  })}
                                  className={`w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                                    darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                                  }`}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                    )}
                    </div>)}
                  </div>

                  {/* Current Assets */}
                  <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${
                    darkMode ? 'bg-slate-800 border-slate-600 hover:border-green-500' : 'bg-green-50 border-green-300 hover:border-green-400'
                  }`}>
                    <h3
                      className={`text-sm font-semibold flex items-center justify-between cursor-pointer select-none ${darkMode ? 'text-white' : 'text-gray-900'}`}
                      onClick={() => toggleSection('assets')}
                    >
                      <div className="flex items-center gap-1">
                        <DollarSign className={`w-4 h-4 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                        Current Assets
                      </div>
                      <div className="flex items-center gap-2">
                        {collapsedSections['assets'] && (
                          <span className={`text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            ${(((currentVision.currentCash || 0) + (currentVision.current401k || 0) + (currentVision.currentInvestments || 0) + (currentVision.currentHomeEquity || 0) + (currentVision.currentOtherAssets || 0)) / 1000).toFixed(0)}k total
                          </span>
                        )}
                        <ChevronDown className={`w-4 h-4 transition-transform ${darkMode ? 'text-green-400' : 'text-green-600'} ${collapsedSections['assets'] ? '-rotate-90' : ''}`} />
                      </div>
                    </h3>

                    {!collapsedSections['assets'] && (<div className="space-y-2">
                    <div>
                      <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Cash/Savings</label>
                      <input
                        type="number"
                        value={currentVision.currentCash || ''}
                        onBlur={(e) => updateVision(currentVision.id, { currentCash: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                        className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>

                    <div>
                      <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>401k/IRA</label>
                      <input
                        type="number"
                        value={currentVision.current401k || ''}
                        onBlur={(e) => updateVision(currentVision.id, { current401k: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                        className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>

                    <div>
                      <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Investments</label>
                      <input
                        type="number"
                        value={currentVision.currentInvestments || ''}
                        onBlur={(e) => updateVision(currentVision.id, { currentInvestments: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                        className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>

                    <div>
                      <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Home Equity</label>
                      <input
                        type="number"
                        value={currentVision.currentHomeEquity || ''}
                        onBlur={(e) => updateVision(currentVision.id, { currentHomeEquity: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                        className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>

                    <div>
                      <label className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Other Assets</label>
                      <input
                        type="number"
                        value={currentVision.currentOtherAssets || ''}
                        onBlur={(e) => updateVision(currentVision.id, { currentOtherAssets: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                        className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>

                    <div className="pt-2 border-t border-gray-200">
                      <div className="flex justify-between text-xs">
                        <span className="font-semibold text-gray-700">Total Assets:</span>
                        <span className="font-bold text-green-600">
                          ${((currentVision.currentCash || 0) + (currentVision.current401k || 0) +
                             (currentVision.currentInvestments || 0) + (currentVision.currentHomeEquity || 0) +
                             (currentVision.currentOtherAssets || 0)).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    </div>)}
                  </div>

                  {/* Family */}
                  <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${
                    darkMode ? 'bg-slate-800 border-slate-600 hover:border-cyan-500' : 'bg-cyan-50 border-cyan-300 hover:border-cyan-400'
                  }`}>
                    <h3
                      className={`text-sm font-semibold flex items-center justify-between cursor-pointer select-none ${darkMode ? 'text-white' : 'text-gray-900'}`}
                      onClick={() => toggleSection('family')}
                    >
                      <div className="flex items-center gap-1">
                        <Users className={`w-4 h-4 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                        Family
                      </div>
                      <div className="flex items-center gap-2">
                        {collapsedSections['family'] && (
                          <span className={`text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {currentVision.hasPartner ? `Partner $${(currentVision.partnerIncomeStart/1000).toFixed(0)}k` : 'Single'} | {currentVision.kids} kids
                          </span>
                        )}
                        <ChevronDown className={`w-4 h-4 text-cyan-600 transition-transform ${collapsedSections['family'] ? '-rotate-90' : ''}`} />
                      </div>
                    </h3>

                    {!collapsedSections['family'] && (<div className="space-y-4">
                    <div>
                      <label className="flex items-center gap-2 mb-3">
                        <input
                          type="checkbox"
                          checked={currentVision.hasPartner}
                          onBlur={(e) => updateVision(currentVision.id, { hasPartner: e.target.checked })}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium text-gray-700">Partner / Dual Income</span>
                      </label>

                      {currentVision.hasPartner && (
                        <div className="space-y-3 pl-6">
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Partner Income</label>
                            <input
                              type="number"
                              value={currentVision.partnerIncomeStart}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  updateVision(currentVision.id, { partnerIncomeStart: '' });
                                } else {
                                  updateVision(currentVision.id, { partnerIncomeStart: parseInt(value) || 0 });
                                }
                              }}
                              onBlur={(e) => {
                                const value = parseInt(e.target.value);
                                if (isNaN(value) || value < 0) {
                                  updateVision(currentVision.id, { partnerIncomeStart: 0 });
                                }
                              }}
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                          </div>
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Partner Current Age</label>
                            <input
                              type="number"
                              value={currentVision.partnerAge || currentVision.age}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  updateVision(currentVision.id, { partnerAge: '' });
                                } else {
                                  const newValue = parseInt(value);
                                  if (!isNaN(newValue)) {
                                    updateVision(currentVision.id, { partnerAge: newValue });
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                const value = parseInt(e.target.value);
                                if (isNaN(value) || value <= 0) {
                                  updateVision(currentVision.id, { partnerAge: currentVision.age || 30 });
                                }
                              }}
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Partner's age today</p>
                          </div>
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Partner Retirement Age</label>

                            <div className="flex items-center gap-2 mb-2">
                              <input
                                type="checkbox"
                                id="syncRetirementAges"
                                checked={currentVision.syncPartnerRetireAge || false}
                                onChange={(e) => {
                                  const sync = e.target.checked;
                                  if (sync) {
                                    // Sync partner to retire on same calendar date as you
                                    const ageDiff = (currentVision.partnerAge || currentVision.age) - currentVision.age;
                                    const partnerRetireAge = Math.round((currentVision.retireAge + ageDiff) * 10) / 10;
                                    updateVision(currentVision.id, {
                                      syncPartnerRetireAge: true,
                                      partnerRetireAge: partnerRetireAge
                                    });
                                  } else {
                                    updateVision(currentVision.id, { syncPartnerRetireAge: false });
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                              />
                              <label htmlFor="syncRetirementAges" className={`text-xs cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Retire together (same calendar date)
                              </label>
                            </div>

                            <input
                              type="number"
                              disabled={currentVision.syncPartnerRetireAge}
                              value={currentVision.partnerRetireAge || currentVision.retireAge}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  updateVision(currentVision.id, { partnerRetireAge: '' });
                                } else {
                                  const newValue = parseInt(value);
                                  if (!isNaN(newValue)) {
                                    updateVision(currentVision.id, { partnerRetireAge: newValue });
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                const value = parseInt(e.target.value);
                                if (isNaN(value) || value <= 0) {
                                  updateVision(currentVision.id, { partnerRetireAge: currentVision.retireAge || 65 });
                                }
                              }}
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                currentVision.syncPartnerRetireAge
                                  ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                  : darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            />
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {currentVision.syncPartnerRetireAge
                                ? 'Synced to retire on the same date as you'
                                : 'Partner\'s age when they retire (not your age)'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Number of Kids</label>
                      <input
                        type="number"
                        value={currentVision.kids}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            updateVision(currentVision.id, { kids: '' });
                          } else {
                            updateVision(currentVision.id, { kids: parseInt(value) || 0 });
                          }
                        }}
                        onBlur={(e) => {
                          const value = parseInt(e.target.value);
                          if (isNaN(value) || value < 0) {
                            updateVision(currentVision.id, { kids: 0 });
                          }
                        }}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>

                    {currentVision.kids > 0 && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>First Child at Age</label>
                        <input
                          type="number"
                          value={currentVision.firstChildAge}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              updateVision(currentVision.id, { firstChildAge: '' });
                            } else {
                              updateVision(currentVision.id, { firstChildAge: parseInt(value) || 0 });
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseInt(e.target.value);
                            if (isNaN(value) || value <= 0) {
                              updateVision(currentVision.id, { firstChildAge: 30 });
                            }
                          }}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        />
                      </div>
                    )}

                    {currentVision.kids > 1 && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Years Between Children</label>
                        <input
                          type="number"
                          value={currentVision.childSpacing || 3}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              updateVision(currentVision.id, { childSpacing: 3 });
                            } else {
                              updateVision(currentVision.id, { childSpacing: parseInt(value) || 3 });
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseInt(e.target.value);
                            if (isNaN(value) || value <= 0) {
                              updateVision(currentVision.id, { childSpacing: 3 });
                            }
                          }}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          min="1"
                          max="10"
                        />
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {currentVision.kids > 1 && `Children born at ages: ${Array.from({ length: currentVision.kids }, (_, i) =>
                            (currentVision.firstChildAge || 30) + (i * (currentVision.childSpacing || 3))
                          ).join(', ')}`}
                        </p>
                      </div>
                    )}
                    </div>)}
                  </div>

                  {/* Monthly Expenses */}
                  {/* Total Monthly Expenses Summary */}
                  <div className={`rounded-xl border-2 shadow-lg p-4 ${
                    darkMode ? 'bg-gradient-to-r from-slate-700 to-slate-800 border-blue-500' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        <h3 className={`text-base font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Total Monthly Expenses
                        </h3>
                      </div>
                      <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        ${(() => {
                          const pe = currentVision.precisionExpenses || {};
                          const total =
                            // Housing (rent/mortgage handled separately, just recurring costs)
                            (pe.propertyTax || 0) + (pe.homeInsurance || 0) + (pe.utilities || 0) + (pe.homeMaintenance || 0) +
                            // Transportation
                            (pe.carPayment || 0) + (pe.carInsurance || 0) + (pe.gas || 0) + (pe.publicTransit || 0) + (pe.carMaintenance || 0) +
                            // Daily Living
                            (pe.groceries || 0) + (pe.diningOut || 0) + (pe.shopping || 0) + (pe.personalCare || 0) +
                            // Healthcare & Entertainment
                            (pe.healthInsurance || 0) + (pe.medicalExpenses || 0) + (pe.entertainment || 0) + (pe.travel || 0) +
                            (pe.hobbies || 0) + (pe.subscriptions || 0) + (pe.gym || 0) +
                            // Kids (per child)
                            ((pe.childcare || 0) + (pe.childEducation || 0) + (pe.childActivities || 0) + (pe.childOther || 0)) * (currentVision.kids || 0) +
                            // Other
                            (pe.lifeInsurance || 0) + (pe.gifts || 0) + (pe.charity || 0) + (pe.other || 0) +
                            // Retirement contributions
                            (pe.retirement401k || 0);
                          return total.toLocaleString();
                        })()}/mo
                      </div>
                    </div>
                    <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      After-tax spending in today's dollars • Excludes rent/mortgage • Savings rate auto-calculated
                    </p>
                  </div>

                      {/* Transportation */}
                      <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${darkMode ? 'bg-slate-800 border-slate-600 hover:border-blue-500' : 'bg-blue-50 border-blue-300 hover:border-blue-400'}`}>
                        <h3
                          className={`text-sm font-semibold flex items-center justify-between cursor-pointer select-none ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          onClick={() => toggleSection('transportation')}
                        >
                          <div className="flex items-center gap-1">
                            <Calendar className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                            Transportation (Monthly)
                          </div>
                          <div className="flex items-center gap-2">
                            {collapsedSections['transportation'] && (
                              <span className={`text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ${((currentVision.precisionExpenses?.carPayment || 0) + (currentVision.precisionExpenses?.carInsurance || 0) + (currentVision.precisionExpenses?.gas || 0) + (currentVision.precisionExpenses?.carMaintenance || 0)).toLocaleString()}/mo
                              </span>
                            )}
                            <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${collapsedSections['transportation'] ? '-rotate-90' : ''}`} />
                          </div>
                        </h3>
                        {!collapsedSections['transportation'] && (
                        <div className="grid grid-cols-2 gap-4">
                          {[
                            { key: 'carPayment', label: 'Car Payment' },
                            { key: 'carInsurance', label: 'Car Insurance' },
                            { key: 'gas', label: 'Gas/Fuel' },
                            { key: 'publicTransit', label: 'Public Transit' },
                            { key: 'carMaintenance', label: 'Car Maintenance' }
                          ].map(({ key, label }) => (
                            <div key={key}>
                              <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
                              <input
                                type="number"
                                defaultValue={currentVision.precisionExpenses[key] || ''}
                                key={`${key}-${currentVision.id}-${currentVision.precisionExpenses[key]}`}
                                onBlur={(e) => updateVision(currentVision.id, {
                                  precisionExpenses: { ...currentVision.precisionExpenses, [key]: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }
                                })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              />
                            </div>
                          ))}
                        </div>
                        )}
                      </div>

                      {/* Daily Living */}
                      <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${darkMode ? 'bg-slate-800 border-slate-600 hover:border-emerald-500' : 'bg-emerald-50 border-emerald-300 hover:border-emerald-400'}`}>
                        <h3
                          className={`text-sm font-semibold flex items-center justify-between cursor-pointer select-none ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          onClick={() => toggleSection('daily-living')}
                        >
                          <div className="flex items-center gap-1">
                            <DollarSign className={`w-4 h-4 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                            Daily Living (Monthly)
                          </div>
                          <div className="flex items-center gap-2">
                            {collapsedSections['daily-living'] && (
                              <span className={`text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ${((currentVision.precisionExpenses?.groceries || 0) + (currentVision.precisionExpenses?.diningOut || 0) + (currentVision.precisionExpenses?.shopping || 0) + (currentVision.precisionExpenses?.personalCare || 0)).toLocaleString()}/mo
                              </span>
                            )}
                            <ChevronDown className={`w-4 h-4 text-emerald-600 transition-transform ${collapsedSections['daily-living'] ? '-rotate-90' : ''}`} />
                          </div>
                        </h3>
                        {!collapsedSections['daily-living'] && (
                        <div className="grid grid-cols-2 gap-4">
                          {[
                            { key: 'groceries', label: 'Groceries' },
                            { key: 'diningOut', label: 'Dining Out' },
                            { key: 'shopping', label: 'Shopping/Clothes' },
                            { key: 'personalCare', label: 'Personal Care' }
                          ].map(({ key, label }) => (
                            <div key={key}>
                              <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
                              <input
                                type="number"
                                defaultValue={currentVision.precisionExpenses[key] || ''}
                                key={`${key}-${currentVision.id}-${currentVision.precisionExpenses[key]}`}
                                onBlur={(e) => updateVision(currentVision.id, {
                                  precisionExpenses: { ...currentVision.precisionExpenses, [key]: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }
                                })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              />
                            </div>
                          ))}
                        </div>
                        )}
                      </div>

                      {/* Healthcare & Entertainment combined in one row */}
                      <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${darkMode ? 'bg-slate-800 border-slate-600 hover:border-cyan-500' : 'bg-cyan-50 border-cyan-300 hover:border-cyan-400'}`}>
                        <h3
                          className={`text-sm font-semibold flex items-center justify-between cursor-pointer select-none ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          onClick={() => toggleSection('health-entertainment')}
                        >
                          <div className="flex items-center gap-1">
                            <Plus className={`w-4 h-4 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                            Healthcare & Entertainment (Monthly)
                          </div>
                          <div className="flex items-center gap-2">
                            {collapsedSections['health-entertainment'] && (
                              <span className={`text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ${((currentVision.precisionExpenses?.healthInsurance || 0) + (currentVision.precisionExpenses?.medicalExpenses || 0) + (currentVision.precisionExpenses?.entertainment || 0) + (currentVision.precisionExpenses?.travel || 0) + (currentVision.precisionExpenses?.hobbies || 0) + (currentVision.precisionExpenses?.subscriptions || 0)).toLocaleString()}/mo
                              </span>
                            )}
                            <ChevronDown className={`w-4 h-4 text-cyan-600 transition-transform ${collapsedSections['health-entertainment'] ? '-rotate-90' : ''}`} />
                          </div>
                        </h3>
                        {!collapsedSections['health-entertainment'] && (
                        <div className="space-y-6">
                          {/* Healthcare */}
                          <div>
                            <h3 className="font-semibold text-gray-700 flex items-center gap-2 mb-3 text-sm">
                              <div className="w-6 h-6 bg-teal-100 rounded flex items-center justify-center">
                                <Plus className="w-3 h-3 text-teal-600" />
                              </div>
                              Healthcare
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                              {[
                                { key: 'healthInsurance', label: 'Health Insurance' },
                                { key: 'medicalExpenses', label: 'Medical Expenses' }
                              ].map(({ key, label }) => (
                                <div key={key}>
                                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
                                  <input
                                    type="number"
                                    defaultValue={currentVision.precisionExpenses[key] || ''}
                                key={`${key}-${currentVision.id}-${currentVision.precisionExpenses[key]}`}
                                    onBlur={(e) => updateVision(currentVision.id, {
                                      precisionExpenses: { ...currentVision.precisionExpenses, [key]: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }
                                    })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Entertainment */}
                          <div>
                            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                              <div className="w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-sky-600" />
                              </div>
                              Entertainment (Monthly)
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                              {[
                                { key: 'entertainment', label: 'Entertainment' },
                                { key: 'hobbies', label: 'Hobbies' },
                                { key: 'travel', label: 'Travel' },
                                { key: 'subscriptions', label: 'Subscriptions' },
                                { key: 'gym', label: 'Gym/Fitness' }
                              ].map(({ key, label }) => (
                                <div key={key}>
                                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
                                  <input
                                    type="number"
                                    defaultValue={currentVision.precisionExpenses[key] || ''}
                                key={`${key}-${currentVision.id}-${currentVision.precisionExpenses[key]}`}
                                    onBlur={(e) => updateVision(currentVision.id, {
                                      precisionExpenses: { ...currentVision.precisionExpenses, [key]: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }
                                    })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        )}
                      </div>

                      {/* Kids & Other */}
                      <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${darkMode ? 'bg-slate-800 border-slate-600 hover:border-green-500' : 'bg-green-50 border-green-300 hover:border-green-400'}`}>
                        <h3
                          className={`text-sm font-semibold flex items-center justify-between cursor-pointer select-none ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          onClick={() => toggleSection('kids')}
                        >
                          <div className="flex items-center gap-1">
                            <Users className={`w-4 h-4 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                            Kids (Monthly per child)
                            {collapsedSections['kids'] && (
                              <span className={`text-xs font-normal ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ${((currentVision.precisionExpenses?.childcare || 0) +
                                   (currentVision.precisionExpenses?.childEducation || 0) +
                                   (currentVision.precisionExpenses?.childActivities || 0) +
                                   (currentVision.precisionExpenses?.childOther || 0)).toLocaleString()}/mo × {currentVision.kids || 0} kids
                              </span>
                            )}
                          </div>
                          <ChevronDown className={`w-4 h-4 text-green-600 transition-transform ${collapsedSections['kids'] ? '-rotate-90' : ''}`} />
                        </h3>
                        {!collapsedSections['kids'] && (
                        <div className="grid grid-cols-2 gap-4">
                          {[
                            { key: 'childcare', label: 'Childcare/Daycare' },
                            { key: 'childEducation', label: 'Education' },
                            { key: 'childActivities', label: 'Activities/Sports' },
                            { key: 'childOther', label: 'Other Kid Costs' }
                          ].map(({ key, label }) => (
                            <div key={key}>
                              <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
                              <input
                                type="number"
                                defaultValue={currentVision.precisionExpenses[key] || ''}
                                key={`${key}-${currentVision.id}-${currentVision.precisionExpenses[key]}`}
                                onBlur={(e) => updateVision(currentVision.id, {
                                  precisionExpenses: { ...currentVision.precisionExpenses, [key]: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }
                                })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              />
                            </div>
                          ))}
                        </div>
                        )}
                      </div>

                      {/* Financial & Other */}
                      <div className={`rounded-xl border-2 shadow-md hover:shadow-lg transition-all p-3 space-y-2 ${darkMode ? 'bg-slate-800 border-slate-600 hover:border-gray-500' : 'bg-gray-50 border-gray-300 hover:border-gray-400'}`}>
                        <h3
                          className={`text-sm font-semibold flex items-center justify-between cursor-pointer select-none ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          onClick={() => toggleSection('other')}
                        >
                          <div className="flex items-center gap-1">
                            <DollarSign className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                            Other (Monthly)
                            {collapsedSections['other'] && (
                              <span className={`text-xs font-normal ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ${((currentVision.precisionExpenses?.debtPayments || 0) +
                                   (currentVision.precisionExpenses?.gifts || 0) +
                                   (currentVision.precisionExpenses?.charity || 0) +
                                   (currentVision.precisionExpenses?.petExpenses || 0) +
                                   (currentVision.precisionExpenses?.other || 0)).toLocaleString()}/mo
                              </span>
                            )}
                          </div>
                          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${collapsedSections['other'] ? '-rotate-90' : ''}`} />
                        </h3>
                        {!collapsedSections['other'] && (
                        <div className="grid grid-cols-2 gap-4">
                          {[
                            { key: 'debtPayments', label: 'Debt Payments' },
                            { key: 'gifts', label: 'Gifts' },
                            { key: 'charity', label: 'Charity' },
                            { key: 'petExpenses', label: 'Pet Expenses' },
                            { key: 'other', label: 'Miscellaneous' }
                          ].map(({ key, label }) => (
                            <div key={key}>
                              <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
                              <input
                                type="number"
                                defaultValue={currentVision.precisionExpenses[key] || ''}
                                key={`${key}-${currentVision.id}-${currentVision.precisionExpenses[key]}`}
                                onBlur={(e) => updateVision(currentVision.id, {
                                  precisionExpenses: { ...currentVision.precisionExpenses, [key]: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }
                                })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${darkMode ? 'bg-slate-700 border-slate-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              />
                            </div>
                          ))}
                        </div>
                        )}
                      </div>

                  {/* NEW FEATURES */}
                  <LifestyleExpensesPanel vision={currentVision} onUpdate={(updates) => updateVision(currentVision.id, updates)} darkMode={darkMode} />

                  <DebtPanel vision={currentVision} onUpdate={(updates) => updateVision(currentVision.id, updates)} darkMode={darkMode} />

                  <EventsPanel vision={currentVision} onUpdate={(updates) => updateVision(currentVision.id, updates)} darkMode={darkMode} />

                  <CareerPanel vision={currentVision} onUpdate={(updates) => updateVision(currentVision.id, updates)} darkMode={darkMode} />

                  {currentVision.kids > 0 && (
                    <CollegeSavingsPanel vision={currentVision} onUpdate={(updates) => updateVision(currentVision.id, updates)} darkMode={darkMode} />
                  )}
                </div>
              </div>

              {/* Middle: Lifestyle Trade-offs Column */}
              {showScenarios && (
                <div className={`w-full lg:w-1/3 overflow-y-auto p-6 lg:border-r-2 ${darkMode ? 'border-slate-700 bg-slate-800' : 'border-blue-200 bg-blue-50'}`}>
                  <DecisionScenariosPanel
                    vision={currentVision}
                    onUpdate={(updates) => updateVision(currentVision.id, updates)}
                    calculateRetirementAge={calculateOptimalRetirementAge}
                    darkMode={darkMode}
                  />
                </div>
              )}

              {/* Right: Charts */}
              <div className={`overflow-y-auto p-3 sm:p-4 lg:p-6 transition-all duration-300 ${
                darkMode ? 'bg-slate-900' : 'bg-sky-50'
              } ${
                collapsedSections['life-vision']
                  ? 'w-full'
                  : showScenarios
                    ? 'w-full lg:w-1/3'
                    : 'w-full lg:w-1/2'
              }`}>
                <div className="mb-4">
                  <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : ''}`} style={darkMode ? {} : { color: brandColors.navyDark }}>Life Preview</h2>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Estimates for educational purposes only - not financial advice</p>
                </div>
                <div className="space-y-6">
                  {(() => {
                    // DEBUG: Log which vision is being calculated
                    console.log('📊 CALCULATING CHARTS FOR VISION:');
                    console.log('  Vision Name:', currentVision?.name);
                    console.log('  Vision ID:', currentVision?.id);
                    console.log('  Age:', currentVision?.age);
                    console.log('  Current Income:', currentVision?.currentIncome);
                    console.log('  Partner Income:', currentVision?.partnerIncomeStart);
                    console.log('  Active Vision ID:', activeVision);

                    const financialData = calculateFinancials(currentVision);
                    return (
                      <>
                        <PortfolioChart data={financialData} vision={currentVision} darkMode={darkMode} />
                        <MonteCarloChart data={financialData} vision={currentVision} darkMode={darkMode} />
                        <IncomeExpensesChart data={financialData} vision={currentVision} darkMode={darkMode} />
                        <SavingsRateChart data={financialData} vision={currentVision} darkMode={darkMode} />
                        <StressChart data={financialData} vision={currentVision} darkMode={darkMode} />
                      </>
                    );
                  })()}

                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    {(() => {
                      const summary = getVisionSummary(currentVision);
                      return (
                        <>
                          <div className={`rounded-lg p-3 ${
                            summary.avgSavingsRate >= 20 ? 'bg-green-50 border border-green-200' :
                            summary.avgSavingsRate >= 10 ? 'bg-blue-50 border border-blue-200' :
                            summary.avgSavingsRate >= 0 ? 'bg-yellow-50 border border-yellow-200' :
                            'bg-red-50 border border-red-200'
                          }`}>
                            <div className={`text-xs mb-1 ${
                              summary.avgSavingsRate >= 20 ? 'text-green-900' :
                              summary.avgSavingsRate >= 10 ? 'text-blue-900' :
                              summary.avgSavingsRate >= 0 ? 'text-yellow-900' : 'text-red-900'
                            }`}>Avg Savings Rate</div>
                            <div className={`text-lg font-bold ${
                              summary.avgSavingsRate >= 20 ? 'text-green-900' :
                              summary.avgSavingsRate >= 10 ? 'text-blue-900' :
                              summary.avgSavingsRate >= 0 ? 'text-yellow-900' : 'text-red-900'
                            }`}>
                              {summary.avgSavingsRate.toFixed(1)}%
                            </div>
                          </div>

                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="text-xs text-green-900 mb-1">At Retirement</div>
                            <div className="text-lg font-bold text-green-900">
                              ${(summary.retirementPortfolio / 1000000).toFixed(1)}M
                            </div>
                          </div>

                          <div className={`rounded-lg p-3 ${
                            summary.peakStress > 75 ? 'bg-red-50 border border-red-200' :
                            summary.peakStress > 50 ? 'bg-yellow-50 border border-yellow-200' :
                            'bg-green-50 border border-green-200'
                          }`}>
                            <div className={`text-xs mb-1 ${
                              summary.peakStress > 75 ? 'text-red-900' :
                              summary.peakStress > 50 ? 'text-yellow-900' : 'text-green-900'
                            }`}>Peak Stress</div>
                            <div className={`text-lg font-bold ${
                              summary.peakStress > 75 ? 'text-red-900' :
                              summary.peakStress > 50 ? 'text-yellow-900' : 'text-green-900'
                            }`}>
                              {summary.peakStress > 75 ? 'High' : summary.peakStress > 50 ? 'Moderate' : 'Low'}
                            </div>
                          </div>

                          <div className={`rounded-lg p-3 ${
                            summary.feasibility === 'high' ? 'bg-green-50 border border-green-200' :
                            summary.feasibility === 'moderate' ? 'bg-yellow-50 border border-yellow-200' :
                            'bg-red-50 border border-red-200'
                          }`}>
                            <div className={`text-xs mb-1 ${
                              summary.feasibility === 'high' ? 'text-green-900' :
                              summary.feasibility === 'moderate' ? 'text-yellow-900' : 'text-red-900'
                            }`}>Feasibility</div>
                            <div className={`text-lg font-bold capitalize ${
                              summary.feasibility === 'high' ? 'text-green-900' :
                              summary.feasibility === 'moderate' ? 'text-yellow-900' : 'text-red-900'
                            }`}>
                              {summary.feasibility}
                            </div>
                          </div>

                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                            <div className="text-xs text-slate-900 mb-1">Lifetime Taxes</div>
                            <div className="text-lg font-bold text-slate-900">
                              ${(summary.lifetimeTaxes / 1000000).toFixed(1)}M
                            </div>
                            <div className="text-xs text-slate-700 mt-1">
                              Avg: {(summary.avgTaxRate * 100).toFixed(1)}%
                            </div>
                          </div>

                          {summary.totalDebtPaid > 0 && (
                            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                              <div className="text-xs text-cyan-900 mb-1">Total Debt Paid</div>
                              <div className="text-lg font-bold text-cyan-900">
                                ${(summary.totalDebtPaid / 1000).toFixed(0)}K
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-sky-50">
              <div className="text-center text-sky-700">
                <TrendingUp className="w-16 h-16 mx-auto mb-4 text-sky-400" />
                <p className="text-xl font-semibold">No Vision Selected</p>
                <p className="text-sm mt-2 text-sky-600">Create or select a vision from the sidebar to begin</p>
              </div>
            </div>
          )}
        </div>
      </main>
      <Analytics />
    </div>
  );
};

export default LifeMathVision;
